/**
 * Parser source transforms
 *
 * All source-to-source text transforms used by the preprocess pipeline.
 * These operate on raw source strings before Acorn parsing.
 */

import { SyntaxError } from './types'
import {
  findUnsafeSpans,
  maskLiterals,
  maskLiteralsKeepComments,
  isEscapedAt,
  scanLiterals,
  matchingBrace,
  splitTopLevelTrimmed,
  isRegexStart,
  findRegexEnd,
} from '../strip-comments'
import {
  constructsDeclaredClass,
  declaredClassNames,
  newExpressionPattern,
} from './declared-classes'
import { stripParamMarkers } from './parser-params'
import { rt, RT_NS, WASM_JS_MARKER } from './rt-namespace'
import {
  literalUnionValues,
  typeArgumentSource,
  typeNameExample,
} from './inference'

/**
 * Extract a brace-balanced value from source after a regex match.
 * Returns a match-like object with [1] being the balanced { ... } content,
 * or null if no match. Handles nested braces.
 */
function extractBalancedValue(
  source: string,
  startRe: RegExp
): RegExpMatchArray | null {
  const m = source.match(startRe)
  if (!m) return null

  const braceStart = m.index! + m[0].length - 1 // position of opening {
  let depth = 1
  let j = braceStart + 1
  while (j < source.length && depth > 0) {
    if (source[j] === '{') depth++
    else if (source[j] === '}') depth--
    j++
  }
  if (depth !== 0) return null

  const balanced = source.slice(braceStart, j) // includes { and }
  const result = [m[0].slice(0, -1) + balanced, balanced] as RegExpMatchArray
  result.index = m.index
  return result
}
import type {
  WasmBlock,
  TestBlock,
  PolyVariant,
  TokenizerState,
} from './parser-types'
// Aliased: this module is concatenated with `parser.ts` into one scope by the self-hosting
// canary (`bootstrap.test.ts`), where a bare `parse` is the TJS parser's own — so the
// type-expression readers got the wrong parser and rejected every example.
import { parse as parseJS } from 'acorn'
import { emitVerifiedPredicate, formatPredicateDiagnostics } from './predicate'
import type { PredicateVerification } from './types'
import { typeSignatureFor } from './type-signature'

/**
 * If a `Type`/`FunctionPredicate` predicate body verifies as predicate-safe
 * (pure, synchronous, no loops/IO), return a self-contained fuel-bounded guard
 * **expression** to inline in place of the raw arrow; otherwise return null so
 * the caller falls back to the unverified body (never rejects — see
 * PRINCIPLES.md, TJS ⊇ JS). This is the transpile-time consumer of the predicate
 * engine: a safe predicate "earns" the native fast path and DoS-safe validation.
 */
function verifiedGuardExpr(
  name: string,
  kind: 'Type' | 'Generic',
  params: string,
  body: string,
  knownPredicates?: string[],
  report?: PredicateVerification[]
): string | null {
  // Synthesize a named declaration the verifier can analyze. The body has
  // already been through the TJS equality/typeof rewrites (Eq/NotEq/Is/IsNot/
  // TypeOf), all whitelisted as pure in the verifier. `knownPredicates` lets a
  // Generic predicate compose with its type-param checks (`checkT(x[0])`) — the
  // verifier treats those calls as composition with another safe predicate.
  //
  // The entry function is named per-declaration (`__pred_<Type>`), not a fixed
  // `__pred`: the guard IIFEs are inlined into the module, and two identically-
  // named nested `function __pred`s would be picked up by the polymorphic-merge
  // transform (which runs after this one) as ambiguous overloads. Type/Generic
  // names are unique within a module, so this is collision-free and deterministic.
  const entry = `__pred_${name}`
  const fnSource = `function ${entry}(${params}) { ${body} }`
  const r = emitVerifiedPredicate(
    fnSource,
    entry,
    knownPredicates ? { knownPredicates: new Set(knownPredicates) } : {}
  )
  if (r.safe) {
    report?.push({ name, kind, verified: true })
    return r.code!
  }
  report?.push({
    name,
    kind,
    verified: false,
    // The verifier names the synthesized entry `__pred_<Name>`; surface the
    // declaration name in the user-facing reason instead.
    reason: formatPredicateDiagnostics(r.diagnostics).replace(/__pred_/g, ''),
  })
  return null
}

export function transformTryWithoutCatch(source: string): string {
  let result = ''
  let i = 0

  // Scanned over a MASKED view: a `{`/`}` inside a string, template, regex or comment is
  // not structure. Counting them raw put the "closing brace" of a try block INSIDE a
  // string literal — for `'  const re = /[}]/'` (a brace in a character class in a string)
  // the depth hit zero early, no `catch` followed, and the generated monadic handler was
  // spliced into the middle of the literal:
  //
  //   '  const re = /[}]/'  ->  '  const re = /[}…__tjs?.getStack?.()) }]/'
  //
  // Third instance of this defect class found in one day. The masked view is computed
  // once; `result` is still built from the ORIGINAL source, so literals are preserved.
  //
  // `source.slice(i).match(…)` also allocated a copy of the remaining source on EVERY
  // character — quadratic in file size for a scan that only ever needed to look at four
  // characters. It reads the masked view in place now.
  const masked = maskLiterals(source)
  const tryAt = (at: number): number => {
    // Length of `try\s*{` at `at`, or 0.
    if (!masked.startsWith('try', at)) return 0
    if (at > 0 && /[A-Za-z0-9_$]/.test(masked[at - 1])) return 0
    let k = at + 3
    while (k < masked.length && /\s/.test(masked[k])) k++
    return masked[k] === '{' ? k + 1 - at : 0
  }

  while (i < source.length) {
    // Look for 'try' keyword followed by '{'
    const tryLen = tryAt(i)
    if (tryLen) {
      // Found 'try {', now find the matching closing brace
      const startBrace = i + tryLen - 1
      const bodyStart = startBrace + 1
      let depth = 1
      let j = bodyStart

      while (j < masked.length && depth > 0) {
        const char = masked[j]
        if (char === '{') depth++
        else if (char === '}') depth--
        j++
      }

      if (depth !== 0) {
        // Unbalanced braces, let the parser handle the error
        result += source[i]
        i++
        continue
      }

      // Check what comes after the closing brace
      const afterTry = masked.slice(j).match(/^\s*(catch|finally)\b/)

      if (afterTry) {
        // Has catch or finally - leave it alone, copy the try block as-is
        result += source.slice(i, j)
        i = j
      } else {
        // No catch or finally - add monadic error handler
        // Returns MonadicError to maintain monadic flow (propagates through function chains)
        const body = source.slice(bodyStart, j - 1)
        result += `try {${body}} catch (__try_err) { return new (__tjs?.MonadicError ?? Error)(__try_err?.message || String(__try_err), 'try', undefined, undefined, __tjs?.getStack?.()) }`
        i = j
      }
    } else {
      result += source[i]
      i++
    }
  }

  return result
}

/**
 * Extract WASM blocks from source and replace with runtime dispatch code
 *
 * Simple form (body used as both WASM source and JS fallback):
 *   wasm {
 *     for (let i = 0; i < arr.length; i++) { arr[i] *= 2 }
 *   }
 *
 * With explicit fallback (when you need different JS code):
 *   wasm {
 *     // WASM-optimized version
 *   } fallback {
 *     // Different JS implementation
 *   }
 *
 * Output:
 *   (globalThis.__tjs_wasm_a1b2c3_0
 *     ? globalThis.__tjs_wasm_a1b2c3_0(captures...)
 *     : (() => { body })())
 *
 * Variables are auto-captured from the body.
 *
 * The `a1b2c3` is a content hash of the module (see `moduleTag`). Inline blocks
 * used to be numbered by a per-FILE counter — `__tjs_wasm_0`, `__tjs_wasm_1` —
 * so EVERY module's first block claimed `globalThis.__tjs_wasm_0`. Two modules
 * with an inline wasm block silently overwrote each other's binding, and since
 * the emitted call site guards the wasm path on that global merely *existing*,
 * module B could look up the name, find module A's compiled function, and call
 * it with B's captures. Salting with the module's content hash makes the id
 * unique per module while staying fully deterministic (same source ⇒ same id),
 * which the metadata cache and the tests both rely on.
 *
 * NOTE: named `wasm function` declarations keep their `__tjs_wasm_<name>` id
 * (see `transformWasmFunctions`) — that name IS the cross-file composition
 * contract, and hashing it would break importing a wasm function by name.
 */

/**
 * FNV-1a over the source, base36 — a short, stable, dependency-free tag that
 * distinguishes one module's inline blocks from another's. Not cryptographic:
 * it only needs to make accidental collisions between two hand-written source
 * files vanishingly unlikely, not resist an attacker.
 */
function moduleTag(source: string): string {
  let h = 0x811c9dc5
  for (let i = 0; i < source.length; i++) {
    h ^= source.charCodeAt(i)
    h = Math.imul(h, 0x01000193) >>> 0
  }
  return h.toString(36)
}

/**
 * Mask the BODY of every inline `wasm { ... }` block behind an operator-free
 * comment placeholder, so the source-level operator transforms (`==`→`Eq()`,
 * `Is`/`IsNot`→calls) don't rewrite wasm code — the wasm compiler can't compile
 * `Eq(a,b)` and would silently fall back to JS (L807). Only the wasm body is
 * masked; a following `fallback { ... }` is JS and keeps its normal rewrite.
 * Pair with `unmaskWasmBodies` after the operator transforms; the real
 * `extractWasmBlocks` (run later, post paren/poly transforms for correct
 * variable capture) then sees the restored bodies untouched.
 */
export function maskWasmBodies(source: string): {
  source: string
  masks: string[]
} {
  const masks: string[] = []
  let result = ''
  let i = 0
  // Detect openers and count braces over a LITERAL-MASKED view; slice bodies out of the
  // real source. Scanning raw source made `const open = 'wasm {'` a wasm block: brace
  // counting then ran past the string, and the two lines of real code that followed were
  // swallowed into the "body" and replaced by the placeholder. No error — the result still
  // parsed — so the code simply vanished, and the round-trip did not restore it.
  //
  // This function is new in 471ee6e and is a REGRESSION: before it existed the `==` → `Eq`
  // rewrite ran first, so the affected source was transpiled correctly.
  const masked = maskLiterals(source)
  while (i < source.length) {
    const m = masked.slice(i).match(/^\bwasm\s*\{/)
    if (m) {
      const bodyStart = i + m[0].length
      let depth = 1
      let j = bodyStart
      while (j < masked.length && depth > 0) {
        const c = masked[j]
        if (c === '{') depth++
        else if (c === '}') depth--
        j++
      }
      if (depth === 0) {
        const id = masks.length
        masks.push(source.slice(bodyStart, j - 1))
        // `wasm {` + operator-free comment placeholder + `}` — balanced braces,
        // nothing for the operator transforms to touch. No padding spaces: they made the
        // round-trip lossy (two spaces appeared in every restored body), which is a
        // needless way for a masking pass to change the code it is only meant to hide.
        result += `${m[0]}/*__WASM_MASK_${id}__*/}`
        i = j
        continue
      }
    }
    result += source[i]
    i++
  }
  return { source: result, masks }
}

/** Restore the wasm bodies masked by `maskWasmBodies`. */
export function unmaskWasmBodies(source: string, masks: string[]): string {
  let result = source
  for (let id = 0; id < masks.length; id++) {
    // Function replacer so `$` in a wasm body isn't treated as a replacement token.
    result = result.replace(`/*__WASM_MASK_${id}__*/`, () => masks[id])
  }
  return result
}

/**
 * The first statement in a `wasm` body that computes a value and throws it away.
 *
 * A wasm block is a JavaScript block: it returns only with `return`. The WASM QuickStart's
 * first example ended in a bare `a + b`, which compiled to a function with NO result, so it
 * returned `undefined` while its fallback returned 7. A statement whose whole subtree has no
 * effect (no call, assignment, update or `delete`) can only be that mistake, so it is refused
 * here, with the fix, rather than compiled. (ASSUMPTIONS L6 decided this in 2026-08; it was
 * never built.)
 */
export function findDiscardedValue(
  body: string
): { text: string; offset: number } | null {
  const prefix = 'function __wasm_body__() {'
  let program: any
  try {
    program = parseJS(`${prefix}${body}\n}`, { ecmaVersion: 2022 })
  } catch {
    return null // not plain JS (the compiler reports its own errors)
  }
  const hasEffect = (node: any): boolean => {
    if (!node || typeof node !== 'object') return false
    if (Array.isArray(node)) return node.some(hasEffect)
    // Calls to the PURE intrinsics only compute: `f32x4_*` (except `f32x4_store`, which
    // writes memory) and `Math.*`. Counting them as effects let the dot-product shape —
    // `f32x4_extract_lane(acc, 0) + …` as the last statement — through.
    if (node.type === 'CallExpression') {
      const c = node.callee
      const pure =
        (c?.type === 'Identifier' &&
          c.name.startsWith('f32x4_') &&
          c.name !== 'f32x4_store') ||
        (c?.type === 'MemberExpression' &&
          c.object?.type === 'Identifier' &&
          c.object.name === 'Math')
      if (!pure) return true
      return hasEffect(node.arguments)
    }
    if (
      node.type === 'NewExpression' ||
      node.type === 'AssignmentExpression' ||
      node.type === 'UpdateExpression' ||
      node.type === 'AwaitExpression' ||
      (node.type === 'UnaryExpression' && node.operator === 'delete')
    )
      return true
    return Object.keys(node).some(
      (k) => k !== 'loc' && typeof node[k] === 'object' && hasEffect(node[k])
    )
  }
  const visit = (stmt: any): any => {
    if (!stmt) return null
    if (stmt.type === 'ExpressionStatement')
      return hasEffect(stmt.expression) ? null : stmt.expression
    for (const child of [
      ...(stmt.type === 'BlockStatement' ? stmt.body : []),
      stmt.body?.type ? stmt.body : null,
      stmt.consequent,
      stmt.alternate,
    ]) {
      const hit = child && visit(child)
      if (hit) return hit
    }
    return null
  }
  const hit = visit(program.body[0].body)
  return hit
    ? {
        text: body.slice(hit.start - prefix.length, hit.end - prefix.length),
        offset: hit.start - prefix.length,
      }
    : null
}

/** Refuse a wasm body that discards a value (see `findDiscardedValue`). */
function assertNoDiscardedValue(
  source: string,
  body: string,
  bodyStart: number
): void {
  const hit = findDiscardedValue(body)
  if (!hit) return
  throw new SyntaxError(
    `\`${hit.text}\` in a \`wasm\` block computes a value and throws it away. A wasm ` +
      `block returns only with \`return\`, as in any JavaScript block: write ` +
      `\`return ${hit.text}\`.`,
    locAt(source, bodyStart + hit.offset)
  )
}

export function extractWasmBlocks(source: string): {
  source: string
  blocks: WasmBlock[]
} {
  const blocks: WasmBlock[] = []
  const tag = moduleTag(source)
  let result = ''
  let i = 0
  let blockId = 0

  // Detect openers and count braces over a LITERAL-MASKED view; slice bodies from the real
  // source. Scanning raw source meant a `wasm { ... }` written inside a DOC COMMENT was
  // extracted and compiled — and the flagship `wasm-functions` playground example, whose
  // intro prose necessarily says "Inline `wasm { ... }` blocks live inside a specific JS
  // function", printed "did not compile — running the fallback{} (JS)" on every single
  // run. The one place the syntax is guaranteed to appear in prose is the documentation
  // for the syntax.
  //
  // Sibling of the same fix in `maskWasmBodies`; both scanners had the same blind spot,
  // and fixing one of them would have left this one broken in exactly this way.
  const masked = maskLiterals(source)

  while (i < source.length) {
    // Look for 'wasm {' or 'wasm{' - simple block without params
    const wasmMatch = masked.slice(i).match(/^\bwasm\s*\{/)
    if (wasmMatch) {
      const matchStart = i

      // Find the body
      const bodyStart = i + wasmMatch[0].length
      let braceDepth = 1
      let j = bodyStart

      while (j < masked.length && braceDepth > 0) {
        const char = masked[j]
        if (char === '{') braceDepth++
        else if (char === '}') braceDepth--
        j++
      }

      if (braceDepth !== 0) {
        result += source[i]
        i++
        continue
      }

      const body = source.slice(bodyStart, j - 1)
      assertNoDiscardedValue(source, body, bodyStart)
      let fallbackBody: string | undefined
      let matchEnd = j

      // Check for optional 'fallback {' block
      const fallbackMatch = source.slice(j).match(/^\s*fallback\s*\{/)
      if (fallbackMatch) {
        const fallbackStart = j + fallbackMatch[0].length
        braceDepth = 1
        let k = fallbackStart

        while (k < source.length && braceDepth > 0) {
          const char = source[k]
          if (char === '{') braceDepth++
          else if (char === '}') braceDepth--
          k++
        }

        if (braceDepth === 0) {
          fallbackBody = source.slice(fallbackStart, k - 1)
          matchEnd = k
        }
      }

      // Auto-detect captured variables from the body
      const captureNames = detectCaptures(body)

      // Try to find type annotations from enclosing function parameters
      // Look backwards from matchStart to find the function signature
      const captures = captureNames.map((name) => {
        const typeAnnotation = findParameterType(source, matchStart, name)
        return typeAnnotation ? `${name}: ${typeAnnotation}` : name
      })

      // Create the block record
      const block: WasmBlock = {
        id: `__tjs_wasm_${tag}_${blockId}`,
        body,
        fallback: fallbackBody,
        captures,
        start: matchStart,
        end: matchEnd,
      }
      blocks.push(block)

      // Generate runtime dispatch code:
      // The fallback is the body itself (or explicit fallback if provided)
      const fallbackCode = fallbackBody ?? body
      // Strip type annotations from captures for runtime args (e.g., "xs: Float32Array" -> "xs")
      const captureArgNames = captures.map((c) => c.split(':')[0].trim())
      const captureArgs =
        captureArgNames.length > 0 ? captureArgNames.join(', ') : ''

      // For WASM: pass captures as arguments
      // For fallback: just run inline (captures are in scope)
      const wasmCall =
        captureArgNames.length > 0
          ? `globalThis.${block.id}(${captureArgs})`
          : `globalThis.${block.id}()`

      // `globalThis.__tjs_wasm_enabled === false` forces the fallback path even
      // when the WASM is instantiated — a public toggle for A/B benchmarking
      // (WASM vs JS) without poking the internal `__tjs_wasm_<id>` globals (UI-#3).
      // An IMPLICIT fallback (the wasm body run as JS) is marked with an unused parameter so
      // the emitter's `==` rewrite leaves it alone: it must compare as the WASM does, and
      // `f64.ne` says NaN != NaN where TJS `!=` does not. An explicit `fallback {}` is the
      // author's own JavaScript and keeps TJS semantics.
      const fallbackFn =
        fallbackBody === undefined ? `(${WASM_JS_MARKER}) =>` : '() =>'
      const dispatch = `((globalThis.__tjs_wasm_enabled !== false && globalThis.${block.id}) ? ${wasmCall} : (${fallbackFn} {${fallbackCode}})())`

      result += dispatch
      i = matchEnd
      blockId++
    } else {
      result += source[i]
      i++
    }
  }

  return { source: result, blocks }
}

/**
 * Extract top-level `wasm function NAME(params): RetType { body }` declarations.
 *
 * Unlike `extractWasmBlocks` (which finds `wasm { ... }` blocks nested inside
 * regular tjs functions with auto-captured variables), this extractor finds
 * top-level wasm function declarations with explicit parameters and an
 * optional return type. The body is the wasm-subset source.
 *
 * Each declaration becomes a `WasmBlock` whose `captures` array holds the
 * function's parameters with their type annotations (e.g. `['a: Float32Array',
 * 'b: Float32Array', 'n: i32']`). The block ID is derived from the function
 * name (`__tjs_wasm_<name>`), so the JS-side wrapper can reference it.
 *
 * The declaration is replaced in source with a regular JS function that
 * forwards its args to the wasm export, preserving the `export` modifier
 * if present:
 *
 *   (export)? wasm function dot(a: Float32Array, b: Float32Array, n: f64): f64 {
 *     <wasm-source>
 *   }
 *
 *   becomes:
 *
 *   (export)? function dot(a, b, n) { return globalThis.__tjs_wasm_dot(a, b, n) }
 *
 * This runs BEFORE `extractWasmBlocks` so its output (a regular JS function
 * wrapper) isn't disturbed by the inline-block scanner.
 *
 * DETECTED over `maskLiterals`, bodies sliced from the real source. Scanning raw text meant
 * a `wasm function` written inside a STRING was compiled as if it were real code — and a
 * language's own test suite is mostly source held in strings, so this hit four of its own
 * suites at once. `flight-recorder-wasm.test.ts` had its entire fixture replaced by the
 * wrapper the fixture was supposed to produce; `vector-search.bench.test.ts` lost all three
 * of its fixtures; `wasm-ready.test.ts` got a real compiled WebAssembly module emitted into
 * the test file itself. The damage is invisible at the site: the file still parses, still
 * runs, and asserts against a fixture that is no longer the fixture.
 *
 * Same class, and the same pair of defects, as `extractAndRunTests`: an unmasked scan, plus
 * a `^\b` word-boundary check that is not one. `\b` at the START of a sliced string is
 * satisfied whenever the next character is a word character, so it never looked at what
 * PRECEDED the match and `mywasm function f()` matched — which is precisely what the comment
 * claiming otherwise said it prevented.
 *
 * Return-type note: the underlying wasm bytecode builder currently emits f64
 * or void return types only. The declared `: RetType` annotation is parsed
 * and preserved on the block, but not yet validated against the backend's
 * capabilities — `: f64` and omitted-return work today; other types (`: i32`,
 * `: f32`, `: v128`) will be supported when the bytecode builder grows
 * per-function return-type encoding.
 */
export function extractWasmFunctions(source: string): {
  source: string
  blocks: WasmBlock[]
} {
  const blocks: WasmBlock[] = []
  let result = ''
  let i = 0

  // Literals AND comments blanked, offsets preserved. Detection and every brace/paren scan
  // below run over this; text is always sliced from `source`.
  const masked = maskLiterals(source)

  while (i < source.length) {
    // Match: `(export )?wasm function NAME(` (with leading whitespace allowed)
    const declRe = /^(export\s+)?wasm\s+function\s+(\w+)\s*\(/
    const m = masked.slice(i).match(declRe)
    // The word-boundary check the `^\b` in this regex never actually performed: look at the
    // character BEFORE the match, in the real source.
    if (!m || (i > 0 && /[\w$]/.test(source[i - 1]!))) {
      result += source[i]
      i++
      continue
    }

    const hasExport = !!m[1]
    const name = m[2]
    const matchStart = i

    // After the opening `(`: scan balanced parens for the params block
    const parensStart = i + m[0].length
    let parenDepth = 1
    let j = parensStart
    while (j < masked.length && parenDepth > 0) {
      if (masked[j] === '(') parenDepth++
      else if (masked[j] === ')') parenDepth--
      j++
    }
    if (parenDepth !== 0) {
      // Unmatched parens — leave source alone and move on
      result += source[i]
      i++
      continue
    }
    const paramsSource = source.slice(parensStart, j - 1)
    // j now points just past the closing `)`

    // Phase 2: detect `(!` — reserved syntax for unsafe wasm functions
    // (will allow host-import calls, side-effecting globals, etc.).
    // Implementation deferred; for now we reject with a clear message so
    // users either remove the bang or know to wait.
    const unsafeMatch = paramsSource.match(/^\s*!/)
    if (unsafeMatch) {
      throw new SyntaxError(
        `Unsafe wasm functions (with \`!\` marker) are reserved for a ` +
          `future phase. Remove the bang from \`wasm function ${name}\` ` +
          `to declare it as a regular (pure) wasm function, or wait until ` +
          `the unsafe variant is implemented.`,
        locAt(source, matchStart)
      )
    }

    // Optional return-type annotation: `: TYPE`, a single identifier (`f64`) or a numeric
    // example (`0`, `0.0`, `-1.5`). `\w+` alone matched the `0` of `: 0.0`, missed the brace
    // after `.0`, and passed the declaration through as JavaScript.
    // Pointer-style annotations like `Ptr<f32>` are reserved for a follow-up.
    let returnType: string | undefined
    let afterReturnType = j
    const retMatch = masked.slice(j).match(/^\s*:\s*([+-]?\d+(?:\.\d+)?|\w+)/)
    if (retMatch) {
      returnType = retMatch[1]
      afterReturnType = j + retMatch[0].length
    }

    // Expect `{` next (with leading whitespace)
    const braceMatch = masked.slice(afterReturnType).match(/^\s*\{/)
    if (!braceMatch) {
      // Not a wasm function decl after all — pass through
      result += source[i]
      i++
      continue
    }

    // Find body via balanced braces
    const bodyStart = afterReturnType + braceMatch[0].length
    let braceDepth = 1
    let k = bodyStart
    while (k < masked.length && braceDepth > 0) {
      if (masked[k] === '{') braceDepth++
      else if (masked[k] === '}') braceDepth--
      k++
    }
    if (braceDepth !== 0) {
      result += source[i]
      i++
      continue
    }
    const body = source.slice(bodyStart, k - 1)
    assertNoDiscardedValue(source, body, bodyStart)
    // k now points just past the closing `}`

    // Parse params into the existing `captures` shape used by the wasm
    // compiler: each entry is either `name` or `name: type`.
    const captures = parseWasmFunctionParams(paramsSource)

    const id = `__tjs_wasm_${name}`
    const block: WasmBlock = {
      id,
      name, // enables Phase 3 cross-file matching against imported symbols
      returnType, // undefined when the user wrote no `: T` annotation
      body,
      captures,
      start: matchStart,
      end: k,
    }
    blocks.push(block)

    // Generate the JS wrapper function. The wasm runtime sets globalThis[id]
    // (with type-aware marshalling already baked into the wrapper); we just
    // forward args. Preserve `export` if present.
    const argNames = captures.map((c) => c.split(':')[0].trim())
    const exportKeyword = hasExport ? 'export ' : ''
    const wrapper = `${exportKeyword}function ${name}(${argNames.join(
      ', '
    )}) { return globalThis.${id}(${argNames.join(', ')}) }`

    result += wrapper
    i = k
  }

  return { source: result, blocks }
}

/**
 * Phase 3: cross-file wasm-function composition.
 *
 * Scans the consumer's source for `import { name1, name2, ... } from 'spec'`
 * statements. For each, resolves the spec via the supplied ModuleLoader; if
 * the imported file is tjs/ts and one of the imported names corresponds to a
 * `wasm function` declared there, the function's WasmBlock is pulled into the
 * consumer's compilation and replaced in source by a local JS wrapper. The
 * import statement is rewritten to remove the satisfied names (or removed
 * entirely if every imported name was wasm-composed).
 *
 * This is the heart of the cross-file composition story: imported wasm
 * functions become local functions in the consumer's single WebAssembly.Module
 * (via the Phase 0.5 consolidated-module path). The library's transpiled .js
 * is NOT involved — the source is consumed at transpile time.
 *
 * Caller is responsible for providing a configured ModuleLoader. When no
 * loader is supplied (the common case before Phase 3 is fully wired up), this
 * function returns the source unchanged with an empty blocks array.
 *
 * @param source the consumer's source (after extractWasmFunctions on its own
 *               wasm functions, before transformParenExpressions)
 * @param options.loader the ModuleLoader to resolve imports through
 * @param options.importerPath the path of the file being transpiled (used as
 *               the resolver's importer context); optional
 * @returns updated source + the list of imported wasm function blocks
 */
export function composeImportedWasmFunctions(
  source: string,
  options: {
    loader?: {
      load(
        specifier: string,
        importerPath?: string
      ): {
        parseResult: { wasmBlocks: WasmBlock[] }
      } | null
    }
    importerPath?: string
  }
): { source: string; blocks: WasmBlock[] } {
  const { loader, importerPath } = options
  if (!loader) return { source, blocks: [] }

  const composedBlocks: WasmBlock[] = []
  // Track imported names that have been composed, so we don't pull the same
  // wasm function in twice if it's imported from multiple specifiers.
  const composedNames = new Set<string>()

  /**
   * Pull a wasm function into the consumer's composition and recursively
   * pull in any other wasm functions it calls from the same source module.
   *
   * Transitive walking is required for correctness: if `outer` calls
   * `inner` and the consumer only imports `outer`, we still need `inner`
   * in the consumer's wasm module — otherwise outer's body's
   * `call <inner-index>` instruction would reference a function index
   * that doesn't exist. The wasm compiler would (correctly) reject the
   * call at body-compile time.
   *
   * The body scan uses word-boundary regex against the known wasm-function
   * names in the source module. False positives (matching a method call
   * `obj.inner(x)` or a shadowed local) only cause bloat — not correctness
   * problems — and TJS wasm bodies don't use either of those forms anyway.
   */
  function pullInTransitively(
    block: WasmBlock,
    sourceModuleFns: Map<string, WasmBlock>
  ): void {
    if (composedNames.has(block.id)) return
    composedBlocks.push(block)
    composedNames.add(block.id)
    // Scan the body for calls to other wasm functions in this source module
    for (const [name, target] of sourceModuleFns) {
      if (name === block.name) continue // self isn't transitive
      if (composedNames.has(target.id)) continue
      const callRe = new RegExp(`\\b${name}\\s*\\(`)
      if (callRe.test(block.body)) {
        pullInTransitively(target, sourceModuleFns)
      }
    }
  }

  // Match `import { ... } from 'spec'` or `import { ... } from "spec"`.
  // Default imports, namespace imports, and side-effect imports are left
  // alone — only named-bindings are candidates for wasm composition.
  // Multiline imports are supported via the [^}]*? non-greedy match.
  const importRe =
    /^(\s*)import\s*\{([^}]*?)\}\s*from\s*(['"])([^'"]+)\3\s*;?\s*$/gm

  const replaced = source.replace(
    importRe,
    (match, indent: string, bindings: string, _quote, spec: string) => {
      const mod = loader.load(spec, importerPath)
      if (!mod) return match // not a loadable tjs/ts module — leave as-is

      const importedWasmFunctions = new Map<string, WasmBlock>()
      for (const b of mod.parseResult.wasmBlocks) {
        if (b.name) importedWasmFunctions.set(b.name, b)
      }
      if (importedWasmFunctions.size === 0) return match // no wasm here

      // Parse the bindings list: `{ a, b as c, d }`
      // Each binding is `name` or `name as local` (we keep `local` as the
      // local name; for wasm-composed names, `local` must equal the wasm
      // function name since the local wrapper uses the original name).
      const parts = bindings
        .split(',')
        .map((p) => p.trim())
        .filter((p) => p.length > 0)

      const wrappers: string[] = []
      const remainingBindings: string[] = []

      for (const part of parts) {
        const m = part.match(/^(\w+)(?:\s+as\s+(\w+))?$/)
        if (!m) {
          // Couldn't parse — be conservative and keep it as-is
          remainingBindings.push(part)
          continue
        }
        const imported = m[1]
        const local = m[2] ?? m[1]
        const wasmBlock = importedWasmFunctions.get(imported)
        if (!wasmBlock) {
          // Not a wasm function in the source module — keep the import
          remainingBindings.push(part)
          continue
        }
        // Composed: pull the block in (with any transitive callees from
        // the same source module) and emit a local wrapper that uses the
        // LOCAL name (in case of `as` renames) but forwards to the wasm
        // export's original id. Transitive walking is what makes
        // `import { outer } from 'lib'` work when outer internally calls
        // inner — both end up in the consumer's composed module.
        pullInTransitively(wasmBlock, importedWasmFunctions)
        const argNames = wasmBlock.captures.map((c) => c.split(':')[0].trim())
        wrappers.push(
          `function ${local}(${argNames.join(', ')}) { return globalThis.${
            wasmBlock.id
          }(${argNames.join(', ')}) }`
        )
      }

      const wrapperBlock = wrappers.join('\n')

      if (remainingBindings.length === 0) {
        // All names were composed — drop the import statement entirely
        return wrapperBlock ? `${indent}${wrapperBlock}` : `${indent}`
      }
      // Some names remain — keep them as a smaller import
      const trimmedImport = `${indent}import { ${remainingBindings.join(
        ', '
      )} } from '${spec}'`
      return wrapperBlock
        ? `${trimmedImport}\n${indent}${wrapperBlock}`
        : trimmedImport
    }
  )

  return { source: replaced, blocks: composedBlocks }
}

/**
 * Split a parameter list source into individual param strings.
 * Returns entries like `name` (no type) or `name: type` (with type).
 * Type values are single identifiers (`i32`, `Float32Array`, etc.) — generic
 * forms like `Ptr<f32>` are reserved for a follow-up phase.
 */
function parseWasmFunctionParams(paramsSource: string): string[] {
  const trimmed = paramsSource.trim()
  if (!trimmed) return []
  // Simple comma split is fine for the current type syntax (no generics yet).
  return trimmed
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
}

/** Check if an identifier is a WASM SIMD intrinsic (not a captured variable) */
function isWasmIntrinsic(name: string): boolean {
  return name.startsWith('f32x4_') || name.startsWith('v128_')
}

/**
 * Detect variables captured from enclosing scope
 *
 * Finds identifiers that are:
 * - Used in the body
 * - Not declared within the body (let, const, var, function params)
 *
 * This is a simple heuristic - a full implementation would use proper AST analysis
 */
function detectCaptures(body: string): string[] {
  // Comments AND literal bodies removed, via the shared scanner. A raw
  // `.replace(/\/\/[^\n]*/g, '')` reads a `//` inside a template literal as a comment and
  // truncates the line — the ASI bug (c64bcd3). It would also extract capture names from
  // inside strings, which is the thing this strip exists to prevent.
  const bodyWithoutComments = maskLiterals(body)

  // Collect identifiers that appear as property accesses (after a dot)
  const propertyOnly = new Set<string>()
  const propPattern = /\.([a-zA-Z_$][a-zA-Z0-9_$]*)\b/g
  let match
  while ((match = propPattern.exec(bodyWithoutComments)) !== null) {
    propertyOnly.add(match[1])
  }

  // Find all identifiers used in the body (not after a dot)
  const identifierPattern = /(?<!\.)(\b[a-zA-Z_$][a-zA-Z0-9_$]*)\b/g
  const allIdentifiers = new Set<string>()
  while ((match = identifierPattern.exec(bodyWithoutComments)) !== null) {
    allIdentifiers.add(match[1])
  }

  // Remove identifiers that ONLY appear as property accesses
  for (const prop of propertyOnly) {
    if (!allIdentifiers.has(prop)) continue
    // Check if this identifier is also used standalone (not just as .prop)
    const standalonePattern = new RegExp(`(?<!\\.)\\b${prop}\\b`, 'g')
    const dotPattern = new RegExp(`\\.${prop}\\b`, 'g')
    const standaloneMatches =
      bodyWithoutComments.match(standalonePattern)?.length || 0
    const dotMatches = bodyWithoutComments.match(dotPattern)?.length || 0
    // If every occurrence is a property access, remove it
    if (standaloneMatches <= dotMatches) {
      allIdentifiers.delete(prop)
    }
  }

  // Find identifiers declared in the body
  const declared = new Set<string>()

  // let/const/var declarations
  const declPattern = /\b(?:let|const|var)\s+([a-zA-Z_$][a-zA-Z0-9_$]*)/g
  while ((match = declPattern.exec(bodyWithoutComments)) !== null) {
    declared.add(match[1])
  }

  // for loop variables: for (let i = ...)
  const forPattern =
    /\bfor\s*\(\s*(?:let|const|var)\s+([a-zA-Z_$][a-zA-Z0-9_$]*)/g
  while ((match = forPattern.exec(bodyWithoutComments)) !== null) {
    declared.add(match[1])
  }

  // function declarations and parameters would go here for full impl

  // JS keywords and builtins to exclude
  const reserved = new Set([
    'if',
    'else',
    'for',
    'while',
    'do',
    'switch',
    'case',
    'break',
    'continue',
    'return',
    'function',
    'let',
    'const',
    'var',
    'new',
    'this',
    'true',
    'false',
    'null',
    'undefined',
    'typeof',
    'instanceof',
    'in',
    'of',
    'try',
    'catch',
    'finally',
    'throw',
    'async',
    'await',
    'class',
    'extends',
    'super',
    'import',
    'export',
    'default',
    'from',
    'as',
    'static',
    'get',
    'set',
    'yield',
    // Common globals
    'console',
    'Math',
    'Array',
    'Object',
    'String',
    'Number',
    'Boolean',
    'Date',
    'JSON',
    'Promise',
    'Map',
    'Set',
    'WeakMap',
    'WeakSet',
    'Float32Array',
    'Float64Array',
    'Int8Array',
    'Int16Array',
    'Int32Array',
    'Uint8Array',
    'Uint16Array',
    'Uint32Array',
    'BigInt64Array',
    'BigUint64Array',
    'ArrayBuffer',
    'DataView',
    'Error',
    'TypeError',
    'RangeError',
    'length',
    'push',
    'pop',
    'shift',
    'unshift',
    'slice',
    'splice',
    'map',
    'filter',
    'reduce',
    'forEach',
    'find',
    'findIndex',
    'indexOf',
    'includes',
    'globalThis',
    'window',
    'document',
    'Infinity',
    'NaN',
    'isNaN',
    'isFinite',
    'parseInt',
    'parseFloat',
    'encodeURI',
    'decodeURI',
    'eval',
    'wasmBuffer',
  ])

  // Return identifiers that are used but not declared or reserved
  const captures: string[] = []
  for (const id of allIdentifiers) {
    if (!declared.has(id) && !reserved.has(id) && !isWasmIntrinsic(id)) {
      captures.push(id)
    }
  }

  return captures.sort()
}

/**
 * Find the type annotation for a parameter in the enclosing function
 *
 * Looks backwards from wasmBlockStart to find the function signature,
 * then extracts the type annotation for the given parameter name.
 *
 * Supports:
 * - TJS colon syntax: `param: Float32Array`
 * - TypeScript syntax: `param: Float32Array`
 */
function findParameterType(
  source: string,
  wasmBlockStart: number,
  paramName: string
): string | undefined {
  // Look backwards to find the function signature
  // Find the nearest 'function' keyword before the wasm block
  const beforeBlock = source.slice(0, wasmBlockStart)

  // Match function declaration with parameters
  // This regex finds function signatures and captures the parameter list
  const funcPattern = /function\s+\w+\s*\(([^)]*)\)\s*(?:->.*?)?\s*\{[^}]*$/
  const match = beforeBlock.match(funcPattern)

  if (!match) {
    // Try arrow function or method syntax
    const arrowPattern =
      /(?:const|let|var)?\s*\w+\s*=\s*(?:async\s*)?\(([^)]*)\)\s*(?:=>|->)?\s*\{[^}]*$/
    const arrowMatch = beforeBlock.match(arrowPattern)
    if (!arrowMatch) return undefined
    return extractTypeFromParams(arrowMatch[1], paramName)
  }

  return extractTypeFromParams(match[1], paramName)
}

/**
 * Extract the type annotation for a specific parameter from a parameter list string
 */
function extractTypeFromParams(
  paramsStr: string,
  paramName: string
): string | undefined {
  // Split by comma (handling nested structures)
  const params = paramsStr.split(',').map((p) => p.trim())

  for (const param of params) {
    // Match patterns like:
    // - `name: Float32Array`
    // - `name: number`
    // - `name = Float32Array` (TJS example syntax)
    const colonMatch = param.match(
      new RegExp(`^${paramName}\\s*:\\s*([A-Za-z][A-Za-z0-9]*)`)
    )
    if (colonMatch) {
      return colonMatch[1]
    }

    // Match TypeScript-style with equals (default value that's a type constructor)
    const equalsMatch = param.match(
      new RegExp(
        `^${paramName}\\s*=\\s*(Float32Array|Float64Array|Int32Array|Uint8Array|Int8Array|Int16Array|Uint16Array|Uint32Array)`
      )
    )
    if (equalsMatch) {
      return equalsMatch[1]
    }
  }

  return undefined
}

/**
 * Transform Is/IsNot infix operators to function calls
 *
 * Syntax:
 *   a Is b      -> Is(a, b)
 *   a IsNot b   -> IsNot(a, b)
 *
 * This enables structural equality with a clean syntax.
 *
 * MATCHED over `maskLiterals`, spliced into the real source. These were plain
 * `source.replace(...)` calls, so `a Is b` written INSIDE a string was rewritten as if it
 * were code — the last failure standing on the dogfood behaviour gate, in
 * `eval-no-transpile-execution.test.ts`, whose whole job is to hold TJS constructs as data
 * and check which ones reach the AJS path. A scanner that edits its fixtures makes that
 * suite measure itself.
 *
 * Masking is safe for the operands even though the pattern accepts string literals:
 * `maskLiterals` blanks a literal's INTERIOR and keeps its quotes, so `c Is 'lit'` still
 * matches (as `c Is '   '`) while `'x Is y'` no longer does. The matched TEXT always comes
 * from `source` via the match indices, never from the mask.
 */
export function transformIsOperators(source: string): string {
  // Match: (simpleExpr) IsNot (simpleExpr) - must check IsNot first (longer match)
  // simpleExpr = identifier chain with optional [], () OR literals
  const exprPat =
    '([\\w][\\w.\\[\\]()]*|null|undefined|true|false|\\d+(?:\\.\\d+)?|\'[^\']*\'|"[^"]*")'

  /** Rewrite every match of `pat` found in the MASKED view, using text from `source`. */
  const spliceOver = (src: string, pat: RegExp, fn: string): string => {
    const masked = maskLiterals(src)
    const edits: Array<[number, number, string]> = []
    let m: RegExpExecArray | null
    pat.lastIndex = 0
    while ((m = pat.exec(masked)) !== null) {
      const d = (m as any).indices as Array<[number, number]>
      const [ls, le] = d[1]!
      const [rs, re] = d[2]!
      edits.push([
        m.index,
        m.index + m[0].length,
        `${fn}(${src.slice(ls, le)}, ${src.slice(rs, re)})`,
      ])
    }
    let out = src
    for (const [start, end, text] of edits.reverse())
      out = out.slice(0, start) + text + out.slice(end)
    return out
  }

  // Transform IsNot first (longer keyword)
  const isNotRegex = new RegExp(exprPat + '\\s+IsNot\\s+' + exprPat, 'gd')
  source = spliceOver(source, isNotRegex, rt('IsNot'))

  // Transform Is
  const isRegex = new RegExp(exprPat + '\\s+Is\\s+' + exprPat, 'gd')
  source = spliceOver(source, isRegex, rt('Is'))

  return source
}

const isIdentChar = (c: string | undefined) =>
  c !== undefined && /[\w$\u0080-\uffff]/.test(c)

/**
 * A predicate over the comment- and literal-MASKED source: does the text ending at offset `end`
 * (inclusive) close a control statement's HEADER — `if (…)`, `while (…)`, `for (…)`,
 * `for await (…)`, `with (…)`, or the keywords `else` / `do` — so that the next line is its BODY?
 *
 * Decided from the parenthesis STRUCTURE, not from one line: the `)` is matched to its `(` (in a
 * table built once, so a header spanning lines resolves to its keyword), and the word before that
 * `(` is the keyword. That is what makes `if (a &&\n b)`, `outer: for (…)` and `for await (…)`
 * headers too (rc.2 eighth re-review M2) — the single-line version saw none of them, and a `;`
 * inserted after a header BECOMES its body: `if (a &&\n b)\n [r] = [1]` ran the assignment
 * unconditionally. `if (a) foo(b)` also ends in `)`, but that `)` belongs to a call, so it is not
 * a header and the line after it keeps its guard.
 */
function controlHeaderEndingAt(
  masked: string,
  end: number,
  openOf: Map<number, number>
): boolean {
  let e = end
  while (e >= 0 && /\s/.test(masked[e])) e--
  if (e < 0) return false
  const wordBefore = (at: number): [string, number] => {
    let j = at
    while (j >= 0 && /\s/.test(masked[j])) j--
    const stop = j
    while (j >= 0 && isIdentChar(masked[j])) j--
    // a member (`x.if`, `obj.do`) is not a keyword
    if (masked[j] === '.' || masked[j] === '#') return ['', j]
    return [masked.slice(j + 1, stop + 1), j]
  }
  if (masked[e] === ')') {
    const open = openOf.get(e)
    if (open === undefined) return false
    const [word, before] = wordBefore(open - 1)
    if (word === 'if' || word === 'while' || word === 'for' || word === 'with')
      return true
    return word === 'await' && wordBefore(before)[0] === 'for'
  }
  const [word] = wordBefore(e)
  return word === 'else' || word === 'do'
}

/** Every `)` in the masked source, mapped to its `(`. One pass, so lookups are O(1). */
function parenTable(masked: string): Map<number, number> {
  const openOf = new Map<number, number>()
  const stack: number[] = []
  for (let i = 0; i < masked.length; i++) {
    if (masked[i] === '(') stack.push(i)
    else if (masked[i] === ')' && stack.length) openOf.set(i, stack.pop()!)
  }
  return openOf
}

/**
 * Insert semicolons to prevent ASI footguns (TjsStandard mode)
 *
 * JavaScript's ASI (Automatic Semicolon Insertion) has notorious footguns:
 *
 *   foo          // Intended: call foo, then IIFE
 *   (() => {})() // Actual: foo(...)(...) - calls foo with IIFE as argument!
 *
 * TjsStandard prevents this by treating newlines as statement terminators
 * (like Go, Swift, Kotlin). When a line starts with a problematic character
 * that could continue the previous line, we insert a semicolon.
 *
 * Problematic line starts: ( [ / + - `
 *
 * We only insert when the previous line doesn't already end with:
 * - A semicolon
 * - An opening brace/bracket/paren (multi-line expression)
 * - A comma (array/object literal or params)
 * - An operator that clearly continues (+, -, *, /, =, etc.)
 * - A keyword that expects continuation (return, throw, etc. followed by value)
 */
export function insertAsiProtection(
  source: string,
  warnings?: string[]
): string {
  // Characters that can continue a previous expression (ASI footguns)
  // Only (, [, and ` — these are the real hazards where ASI causes bugs.
  // +, -, / at line start are continuations or literals, not footguns.
  const continuationStarts = /^[\s]*[([`]/

  // Characters/patterns that indicate the previous line expects continuation
  // (don't insert semicolon after these)
  const expectsContinuation = /[{([,;:+\-*/%=&|?<>!~^]\s*$|^\s*$/

  // Keywords that expect an expression to follow on same or next line
  const continueKeywords =
    /\b(return|throw|yield|await|case|default|extends|new|typeof|void|delete|in|of|instanceof)\s*$/

  const lines = source.split('\n')
  /**
   * The same lines with STRING AND TEMPLATE CONTENT blanked out.
   *
   * The previous line has to be inspected for a trailing operator, and doing that on raw
   * text with `replace(/\/\/.*$/, '')` reads a `//` inside a template literal as a
   * comment. For
   *
   *     const m = `a // b` +
   *       `c`
   *
   * that truncated the line to ``const m = `a``, which no longer ends in `+`, so this
   * transform inserted a defensive semicolon and split one expression in two —
   * ``+\n  ;`c` `` — which then failed to parse.
   *
   * Masked once for the whole source, not per line, because a template can span lines and
   * a single line cannot tell whether it opened inside one.
   */
  const masked = maskLiterals(source)
  const maskedLines = masked.split('\n')
  const lineEnds: number[] = []
  for (let i = 0; i < masked.length; i++)
    if (masked[i] === '\n') lineEnds.push(i)
  let openOf: Map<number, number> | undefined

  /**
   * Lines whose first non-whitespace character is INSIDE a literal — content or closing
   * delimiter — and which therefore must never be touched.
   *
   * Masking alone is not enough here, and that is the subtlety the first fix missed:
   * `maskLiterals` blanks a literal's CONTENT but keeps its DELIMITERS, so the closing
   * backtick of a multi-line template still looks like a line-initial backtick. Both of
   * these matched the raw test and had a `;` spliced into the string's value:
   *
   *     const s = `line one          const b = `a
   *     (two) three`                 `
   *
   * The range is `[innerStart, end)` deliberately — content and closing delimiter, but NOT
   * the opening one. A line that legitimately BEGINS a statement with a backtick
   * (`` `template`.length ``) starts at `region.start`, is therefore not protected, and
   * still gets its defensive semicolon. Protecting `[start, end)` would have silently
   * dropped that case.
   */
  const literalLines = new Set<number>()
  {
    const lineOfOffset: number[] = new Array(source.length + 1)
    let ln = 0
    for (let i = 0; i <= source.length; i++) {
      lineOfOffset[i] = ln
      if (source[i] === '\n') ln++
    }
    const lineStart: number[] = [0]
    for (let i = 0; i < source.length; i++) {
      if (source[i] === '\n') lineStart.push(i + 1)
    }
    const firstNonWs = (i: number): number => {
      let p = lineStart[i]
      const limit = i + 1 < lineStart.length ? lineStart[i + 1] : source.length
      while (p < limit && (source[p] === ' ' || source[p] === '\t')) p++
      return p
    }
    for (const region of scanLiterals(source)) {
      const from = lineOfOffset[region.innerStart] ?? 0
      const to =
        lineOfOffset[Math.max(region.innerStart, region.end - 1)] ?? from
      for (let i = from; i <= to && i < lines.length; i++) {
        const p = firstNonWs(i)
        if (p >= region.innerStart && p < region.end) literalLines.add(i)
      }
    }
  }

  const result: string[] = []
  let inBlockComment = false

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    const prevLine = i > 0 ? lines[i - 1] : ''

    // Track block comment state
    if (inBlockComment) {
      result.push(line)
      if (line.includes('*/')) inBlockComment = false
      continue
    }

    // Check if this line opens a block comment
    const commentOpen = line.indexOf('/*')
    const commentClose = line.indexOf('*/')
    if (
      commentOpen !== -1 &&
      (commentClose === -1 || commentClose < commentOpen)
    ) {
      inBlockComment = true
      result.push(line)
      continue
    }

    // Check if this line starts with a problematic character.
    //
    // Tested against the MASKED line, not the raw one. A template literal spans lines and
    // its interior is DATA: `c64bcd3` made the PREVIOUS-line test literal-aware and left
    // this one raw, so the second physical line of a multi-line template starting with
    // `(`, `[` or a backtick had a `;` spliced into the string's VALUE — and the warning
    // told the author the opposite of what happened. `const b = \`a\n\`` evaluated to
    // "a\n;". Masked, a line inside a literal is blank and cannot match.
    if (i > 0 && !literalLines.has(i) && continuationStarts.test(line)) {
      // The previous line, comment- and literal-free. `maskLiterals` already removed
      // comments AND blanked literal bodies, so a `//` inside a template cannot be
      // mistaken for one — which is exactly what used to happen.
      const prevNoComment = maskedLines[i - 1] ?? prevLine

      // Don't insert if prev line clearly expects continuation — or is a bare control HEADER,
      // whose next line is its BODY: a `;` there BECAME the body (an empty statement), and
      // `if (c)\n  [a, b] = …\nelse …` orphaned the `else`.
      if (
        !expectsContinuation.test(prevNoComment) &&
        !continueKeywords.test(prevNoComment) &&
        !controlHeaderEndingAt(
          masked,
          lineEnds[i - 1] - 1,
          (openOf ??= parenTable(masked))
        )
      ) {
        // Insert semicolon at start of this line (preserving whitespace).
        //
        // This is the ONE place TJS and JavaScript disagree about statement boundaries,
        // so it is also the one place worth telling the author about. JS would have joined
        // these two lines — `const x = g` / `(a)` calls `g(a)` — and TJS does not. Nobody
        // writes that deliberately (the idiom is a leading `;(…)`, precisely because the
        // bare form is a trap), but "nobody" is not "no one", so say it at the site rather
        // than changing meaning in silence.
        warnings?.push(
          `Line ${i + 1} starts with \`${
            line.trim()[0]
          }\`, which JavaScript would join to ` +
            `the previous line. TJS treats them as separate statements. If you meant the ` +
            `continuation, put it on one line or start this line with \`;\`.`
        )
        const match = line.match(/^(\s*)/)
        const indent = match ? match[1] : ''
        const rest = line.slice(indent.length)
        result.push(indent + ';' + rest)
        continue
      }
    }

    result.push(line)
  }

  return result.join('\n')
}

/**
 * Replace `typeof X` with `TypeOf(X)` outside string/template/comment/regex bodies.
 * X may be `ident`, `ident.foo`, `ident?.foo`, etc. Anything that doesn't look
 * like a simple identifier expression (e.g. `typeof (x + 1)`) is left alone.
 */
export function transformTypeofKeyword(source: string): string {
  type Match = { keywordStart: number; operandEnd: number; operand: string }
  // For `matchingBrace` when consuming a computed access or a call — a bracket inside a
  // string is not structure. `scanLiterals` is memoized, so this is a lookup.
  const masked = maskLiterals(source)
  const matches: Match[] = []
  let i = 0
  let state: TokenizerState = 'normal'
  const templateStack: number[] = []

  while (i < source.length) {
    const char = source[i]
    const nextChar = source[i + 1]

    switch (state) {
      case 'single-string':
        if (char === '\\' && i + 1 < source.length) {
          i += 2
          continue
        }
        if (char === "'") state = 'normal'
        i++
        continue
      case 'double-string':
        if (char === '\\' && i + 1 < source.length) {
          i += 2
          continue
        }
        if (char === '"') state = 'normal'
        i++
        continue
      case 'template-string':
        if (char === '\\' && i + 1 < source.length) {
          i += 2
          continue
        }
        if (char === '$' && nextChar === '{') {
          i += 2
          templateStack.push(1)
          state = 'normal'
          continue
        }
        if (char === '`') state = 'normal'
        i++
        continue
      case 'line-comment':
        if (char === '\n') state = 'normal'
        i++
        continue
      case 'block-comment':
        if (char === '*' && nextChar === '/') {
          i += 2
          state = 'normal'
          continue
        }
        i++
        continue
      case 'regex':
        if (char === '\\' && i + 1 < source.length) {
          i += 2
          continue
        }
        if (char === '[') {
          i++
          while (i < source.length && source[i] !== ']') {
            if (source[i] === '\\' && i + 1 < source.length) i += 2
            else i++
          }
          if (i < source.length) i++
          continue
        }
        if (char === '/') {
          i++
          while (i < source.length && /[gimsuy]/.test(source[i])) i++
          state = 'normal'
          continue
        }
        i++
        continue
      case 'normal':
        if (templateStack.length > 0) {
          if (char === '{') {
            templateStack[templateStack.length - 1]++
          } else if (char === '}') {
            templateStack[templateStack.length - 1]--
            if (templateStack[templateStack.length - 1] === 0) {
              templateStack.pop()
              i++
              state = 'template-string'
              continue
            }
          }
        }
        if (char === "'") {
          i++
          state = 'single-string'
          continue
        }
        if (char === '"') {
          i++
          state = 'double-string'
          continue
        }
        if (char === '`') {
          i++
          state = 'template-string'
          continue
        }
        if (char === '/' && nextChar === '/') {
          i += 2
          state = 'line-comment'
          continue
        }
        if (char === '/' && nextChar === '*') {
          i += 2
          state = 'block-comment'
          continue
        }
        if (char === '/') {
          let j = i - 1
          while (j >= 0 && /\s/.test(source[j])) j--
          const beforeChar = j >= 0 ? source[j] : ''
          const isRegexContext =
            !beforeChar ||
            /[=(!,;:{[&|?+\-*%<>~^]/.test(beforeChar) ||
            (j >= 5 &&
              /\b(return|case|throw|in|of|typeof|instanceof|new|delete|void)$/.test(
                source.slice(Math.max(0, j - 10), j + 1)
              ))
          if (isRegexContext) {
            i++
            state = 'regex'
            continue
          }
        }

        // Detect `typeof <ident-chain>` — only when preceded by a non-word
        // boundary and followed by whitespace then an identifier.
        if (
          char === 't' &&
          source.slice(i, i + 6) === 'typeof' &&
          (i === 0 || !/[\w$]/.test(source[i - 1])) &&
          /\s/.test(source[i + 6] ?? '')
        ) {
          let j = i + 6
          while (j < source.length && /\s/.test(source[j])) j++
          if (j < source.length && /[a-zA-Z_$]/.test(source[j])) {
            const operandStart = j
            while (j < source.length && /[\w$]/.test(source[j])) j++
            // The WHOLE member expression, including computed access and calls.
            //
            // This consumed only `.name` / `?.name`, so a COMPUTED access fell outside the
            // call and `typeof` bound to the object instead of the member:
            //
            //   typeof obj[k] !== 'function'   ->   TypeOf(obj)[k] !== 'function'
            //
            // `TypeOf(obj)` is the string `'object'`, `'object'[k]` is `undefined`, and
            // `undefined !== 'function'` is ALWAYS TRUE. Every guard of that shape silently
            // inverted to "always pass" — no parse error, no type error, no warning, and the
            // source reads correctly. Reported from an ecosystem sweep (#29); reproduces
            // back to 0.8.1.
            //
            // Calls were wrong too, though loudly: `typeof f()` became `TypeOf(f)()`, which
            // tries to CALL a string.
            //
            // Groups are matched by depth so nesting inside the subscript is safe
            // (`x[a[b]]`, `f(g(1))`), and over the MASKED view so a bracket inside a string
            // is not structure.
            while (j < source.length) {
              if (source[j] === '.' && /[a-zA-Z_$]/.test(source[j + 1] ?? '')) {
                j++
                while (j < source.length && /[\w$]/.test(source[j])) j++
              } else if (
                source[j] === '?' &&
                source[j + 1] === '.' &&
                /[a-zA-Z_$]/.test(source[j + 2] ?? '')
              ) {
                j += 2
                while (j < source.length && /[\w$]/.test(source[j])) j++
              } else if (source[j] === '[' || source[j] === '(') {
                const close = matchingBrace(masked, j)
                if (close === -1) break
                j = close + 1
              } else {
                break
              }
            }
            matches.push({
              keywordStart: i,
              operandEnd: j,
              operand: source.slice(operandStart, j),
            })
            i = j
            continue
          }
        }
        break
    }
    i++
  }

  if (matches.length === 0) return source

  // Apply replacements from end to start so earlier positions remain valid.
  let result = source
  for (let k = matches.length - 1; k >= 0; k--) {
    const m = matches[k]
    result =
      result.slice(0, m.keywordStart) +
      `${rt('TypeOf')}(${m.operand})` +
      result.slice(m.operandEnd)
  }
  return result
}

/**
 * A `predicate` written in a form we do not recognise must be an ERROR, never ignored.
 *
 * `predicate(x) { … }` is the only implemented spelling. The two shorter forms that were
 * DECIDED but not built — `predicate => expr` and `predicate { return expr }` — parsed
 * cleanly and fell through to a Type with no predicate at all, i.e. one that **accepts
 * every value**. Measured: `predicate => Even % 2 === 0` returned `true` for 3.
 *
 * That is the drift hazard pointed forwards instead of backwards. A form that silently
 * accepts everything looks like it works, lands in an example, ships in the playground,
 * and then becomes something we must either implement or break. An abandoned form that
 * still parses is bad; an UNBUILT form that still parses is the same trap with a shorter
 * fuse, because the person writing it has been told it is the recommended spelling.
 *
 * So fail loudly and name the form that exists. When the short forms are implemented this
 * guard narrows rather than disappears — it should still catch the next unrecognised one.
 */
/**
 * Derive a parameterized type's predicate from its example.
 *
 *   Type Box<T> { example: { value: T } }
 *     -> (v, T) => v !== null && typeof v === 'object' && … && T(v?.["value"])
 *
 * The example already says WHERE the parameter goes, so requiring
 * `predicate(x, T) { return T(x.value) }` alongside it is asking the author to state the
 * same thing twice — and until this existed, omitting it produced `Generic([…], () => true)`,
 * a parameterized type that accepted every value while looking like it checked one.
 *
 * A type parameter arrives as a CHECK FUNCTION (`Generic` maps its arguments through
 * `typeParamToCheck` first), so `T` at a slot compiles to a call at that slot.
 *
 * Returns `null` when the example contains no type parameter at all — there is nothing a
 * derived predicate would add over the ordinary example schema, and replacing a
 * well-tested mechanism with an equivalent one is how they drift apart.
 */
export function genericPredicateFromExample(
  example: string,
  typeParams: string[]
): string | null {
  if (!typeParams.length) return null
  const params = new Set(typeParams)
  let usesParam = false

  const walk = (node: any, path: string): string => {
    if (node.type === 'Identifier' && params.has(node.name)) {
      usesParam = true
      return `${node.name}(${path})`
    }
    if (node.type === 'ObjectExpression') {
      const parts = [
        `${path} !== null && typeof ${path} === 'object' && !Array.isArray(${path})`,
      ]
      const keys: string[] = []
      for (const p of node.properties) {
        if (p.type !== 'Property' || p.computed) continue
        const key =
          p.key.type === 'Identifier' ? p.key.name : String(p.key.value)
        keys.push(key)
        const sub = `${path}?.[${JSON.stringify(key)}]`
        parts.push(`${JSON.stringify(key)} in ${path}`)
        parts.push(walk(p.value, sub))
      }
      // A described shape is closed, matching `__match` and the real runtime.
      if (keys.length) {
        parts.push(
          `Object.keys(${path}).every((k) => ${JSON.stringify(
            keys
          )}.includes(k))`
        )
      }
      return `(${parts.filter((p) => p !== 'true').join(' && ')})`
    }
    if (node.type === 'ArrayExpression') {
      const first = node.elements[0]
      if (!first) return `Array.isArray(${path})`
      return `(Array.isArray(${path}) && ${path}.every((__e) => ${walk(
        first,
        '__e'
      )}))`
    }
    const isUnary = node.type === 'UnaryExpression'
    const lit = isUnary ? node.argument : node
    if (lit?.type === 'Literal') {
      const val = lit.value
      if (val === null) return `${path} === null`
      if (typeof val === 'number') {
        const base = `typeof ${path} === 'number'`
        if (!Number.isInteger(val)) return base
        const int = `${base} && Number.isInteger(${path})`
        return isUnary && node.operator === '+'
          ? `(${int} && ${path} >= 0)`
          : `(${int})`
      }
      return `typeof ${path} === ${JSON.stringify(typeof val)}`
    }
    return 'true' // an element we cannot read must not constrain
  }

  try {
    const parsed = parseJS(`(${example})`, { ecmaVersion: 2022 }) as any
    const expr = parsed.body[0]?.expression
    if (!expr) return null
    const body = walk(expr, 'v')
    if (!usesParam) return null
    return `(v, ${typeParams.join(', ')}) => ${body}`
  } catch (e) {
    // An example we cannot parse is one we must not narrow — but a bare `catch` here
    // once swallowed a ReferenceError from a missing import and returned `null`, which
    // is indistinguishable from "nothing to derive". Only syntax errors are expected.
    if (!(e instanceof globalThis.SyntaxError)) throw e
    return null
  }
}

/**
 * Rewrite the short predicate spelling into the function form the rest of the pipeline
 * already understands.
 *
 *   predicate { BODY }         ->  predicate(TypeName) { BODY }
 *
 * (`predicate => EXPR` was a third spelling. It was removed before 0.14.0 final: it saved
 * only `{ return }` over this form, and is refused by `assertPredicateFormRecognized` with a
 * message naming the replacement.)
 *
 * The type name binds to the value under test, which is the decided design: it reads as
 * "an `Even` is a value where `Even % 2 === 0`". Normalising here rather than teaching the
 * emitter a second shape means the short forms inherit predicate VERIFICATION, fuel
 * bounding and the `$predicate` schema path for free — three places a parallel
 * implementation would have had to be kept in step.
 *
 * On a generic, the type parameters follow the value, matching `predicate(x, T)`.
 *
 * Literal-aware, because this file's dominant defect class is scanning raw source for a
 * keyword and finding one inside a string.
 */
export function normalizePredicateForms(
  body: string,
  typeName: string,
  typeParams: string[] = []
): string {
  const params = [typeName, ...typeParams].join(', ')
  let out = body
  for (let guard = 0; guard < 8; guard++) {
    const masked = maskLiterals(out)
    // Only a BLOCK MEMBER, never a key inside an example value — same reason as
    // `topLevelPredicateOffsets`. `predicate(` is the function form and must not match
    // here either; the pattern requires the next non-space character to be `{`.
    const start = topLevelPredicateOffsets(out).find((i) =>
      /^predicate\s*\{/.test(masked.slice(i))
    )
    if (start === undefined) return out
    const m = masked.slice(start).match(/^predicate\s*\{/)!
    const after = start + m[0].length
    // Brace-match on the MASKED view so a `}` inside a string cannot end the body.
    let depth = 1
    let j = after
    while (j < masked.length && depth > 0) {
      if (masked[j] === '{') depth++
      else if (masked[j] === '}') depth--
      j++
    }
    if (depth !== 0) return out // unbalanced — leave it for the recognizer to reject
    const end = j
    const inner = out.slice(after, j - 1).trim()
    out = `${out.slice(0, start)}predicate(${params}) { ${inner} }${out.slice(
      end
    )}`
  }
  return out
}

/**
 * Mark what a `Type` example MEANS where its runtime value cannot say it.
 *
 * A `Type` example is evaluated at runtime and matched by value. Inline parameter types read
 * the example's AST instead (`inferTypeFromValue`), so everything the source says survives
 * there — and was lost here:
 *
 *   - `0.0 === 0`, so a float example narrowed to INTEGER: `Type Price = 0.0` rejected 9.99.
 *     `fromTS` maps every TypeScript `number` to `0.0`, so every converted interface with a
 *     number field rejected every non-integer.
 *   - `+0 === 0`, so `{ count: +0 }` accepted -1.
 *   - `'' | undefined` is a union to the parameter path and BITWISE OR to a value — it
 *     evaluated to `0`, so the member became a required integer.
 *   - `string`, `any` — honoured as types in a parameter, a `ReferenceError` in a value.
 *   - a reference to another type is TDZ when declared later, and a recursive type cannot
 *     name itself at all — which is why `fromTS` used to write `null` (= "must be null").
 *
 * All were latent because converted TypeScript did not validate until `TjsStrict` was made
 * to mean "full TJS". This reads the same AST with the same rules and wraps exactly those
 * nodes as `__tjs_rt.__k(kind, value, arg)` — a runtime type whose `.check` states the
 * meaning, which `Type` unwraps again for `.default`:
 *
 *   `float` `0.0`, `nonneg` `+N`, `undef` `undefined`, `pred` a TS type name
 *   (`typeArgumentSource`), `union` `a | b` (members checked by `__match`; an all-literal
 *   union stays a closed SET, as `literalUnionValues` rules), and `ref` — any other
 *   identifier, as a thunk: read when CHECKED, not when declared, so a forward or recursive
 *   reference works, and one that still cannot be read (TDZ, undeclared) degrades to
 *   unchecked instead of crashing the module (TJS ⊇ JS). An initialised binding means
 *   exactly what it meant before.
 *
 * Only TYPE positions are walked — the top level, object property values, array elements,
 * union members. A literal inside a call or a computed key is a value, not a type. An
 * example with nothing to mark comes back byte-identical, so no other emitted code changes.
 */
/**
 * The names a Type is an ALIAS for — when its example is nothing but names, `null` and
 * `undefined` joined by `|` (`Type A = B | null`) — else null. Used to reject a type defined
 * only in terms of itself: under coinduction `Type T = T` holds for EVERY object, which is
 * never what was meant. Sound type names (`string`) are not aliases.
 */
function aliasTargets(example: string): string[] | null {
  let ast: any
  try {
    ast = parseJS(`(${example})`, { ecmaVersion: 'latest' })
  } catch {
    return null
  }
  const names: string[] = []
  const walk = (n: any): boolean => {
    if (n.type === 'BinaryExpression' && n.operator === '|')
      return walk(n.left) && walk(n.right)
    if (n.type === 'Literal' && n.value === null) return true
    if (n.type === 'Identifier') {
      if (n.name === 'undefined') return true
      if (
        typeNameExample(n.name) !== null ||
        typeArgumentSource(n.name) !== null
      )
        return false
      names.push(n.name)
      return true
    }
    return false
  }
  return walk(ast.body[0]?.expression) && names.length ? names : null
}

export function markExampleKinds(example: string): string {
  let ast: any
  try {
    ast = parseJS(`(${example})`, { ecmaVersion: 'latest' })
  } catch {
    return example // not a plain expression — leave it exactly as it was
  }
  // Offsets are into `(${example})`, hence the -1. Every splice below is by OFFSET — never
  // `String.replace`, whose replacement string interprets `$&` and `$$`.
  const text = (n: any) => example.slice(n.start - 1, n.end - 1)
  const k = (kind: string, value = 'undefined', arg = '') =>
    `${rt('__k')}('${kind}', ${value}${arg ? `, ${arg}` : ''})`
  const isNum = (n: any) => n?.type === 'Literal' && typeof n.value === 'number'
  const lossyFloat = (n: any) =>
    isNum(n) && String(n.raw).includes('.') && Number.isInteger(n.value)
  const unionMembers = (n: any): any[] =>
    n.type === 'BinaryExpression' && n.operator === '|'
      ? [...unionMembers(n.left), ...unionMembers(n.right)]
      : [n]
  const isNullish = (n: any) =>
    (n.type === 'Literal' && n.value === null) ||
    (n.type === 'Identifier' && n.name === 'undefined')
  /** The `typeof` a literal member contributes to a closed set, or null if it is not one. */
  const literalKind = (n: any): string | null => {
    if (n.type === 'Literal' && !n.regex && n.value !== null)
      return typeof n.value
    if (n.type === 'UnaryExpression' && n.operator === '-' && isNum(n.argument))
      return 'number'
    return null
  }

  /** The node's source with its lossy parts marked; `null` when nothing needed marking. */
  const mark = (n: any): string | null => {
    if (!n) return null
    if (n.type === 'UnaryExpression' && isNum(n.argument)) {
      if (n.operator === '+') return k('nonneg', text(n))
      if (n.operator === '-' && lossyFloat(n.argument))
        return k('float', text(n))
      return null
    }
    if (lossyFloat(n)) return k('float', text(n))
    if (n.type === 'Identifier') {
      const name = n.name
      if (name === 'undefined') return k('undef')
      if (name === 'NaN' || name === 'Infinity') return null
      const example = typeNameExample(name)
      if (example !== null) return markExampleKinds(example)
      const pred = typeArgumentSource(name)
      if (pred !== null)
        return pred === '(() => true)' ? k('any') : k('pred', 'undefined', pred)
      // The name is passed as the value so a `ref` that cannot be read can SAY which one.
      return k('ref', JSON.stringify(name), `() => ${name}`)
    }
    if (n.type === 'BinaryExpression' && n.operator === '|') {
      const members = unionMembers(n)
      // An all-literal union is a closed SET — `literalUnionValues`' rule — and so is the
      // literal part of one that is also nullable: `'a' | 'b' | undefined` is "a, b, or
      // absent", not "any string or absent". Built from the members' SOURCE, so a bigint
      // member needs no serialisation and a RegExp can never be one.
      const nullish = members.filter(isNullish)
      const rest = members.filter((m) => !isNullish(m))
      const kinds = new Set(rest.map(literalKind))
      const isSet =
        rest.length > 0 &&
        !kinds.has(null) &&
        kinds.size === 1 &&
        (nullish.length === 0
          ? literalUnionValues(n) !== null
          : rest.length > 1)
      if (isSet) {
        const set = k('set', text(rest[0]), `[${rest.map(text).join(', ')}]`)
        if (!nullish.length) return set
        return k(
          'union',
          'undefined',
          `[${[set, ...nullish.map((m) => mark(m) ?? text(m))].join(', ')}]`
        )
      }
      return k(
        'union',
        'undefined',
        `[${members.map((m) => mark(m) ?? text(m)).join(', ')}]`
      )
    }
    if (n.type === 'ArrayExpression' || n.type === 'ObjectExpression') {
      const parts: { start: number; end: number; out: string }[] = []
      if (n.type === 'ArrayExpression') {
        for (const c of n.elements) {
          const out = c && mark(c)
          if (out != null) parts.push({ start: c.start, end: c.end, out })
        }
      } else {
        for (const p of n.properties) {
          if (p.type !== 'Property' || p.computed || p.kind !== 'init') continue
          const out = mark(p.value)
          if (out == null) continue
          // `{ x }` is a key AND a value in one token: rewriting the value alone would
          // produce `{ __tjs_rt.__k(…) }`, which is not JavaScript.
          if (p.shorthand)
            parts.push({
              start: p.start,
              end: p.end,
              out: `${text(p.key)}: ${out}`,
            })
          else parts.push({ start: p.value.start, end: p.value.end, out })
        }
      }
      if (!parts.length) return null
      let src = text(n)
      for (const p of parts.sort((x, y) => y.start - x.start))
        src =
          src.slice(0, p.start - n.start) + p.out + src.slice(p.end - n.start)
      return src
    }
    return null
  }
  return mark(ast.body[0]?.expression) ?? example
}

/**
 * Offsets of `predicate` appearing as a BLOCK MEMBER — brace-depth 0 within the body.
 *
 * A `Type` block's members sit at depth 0; anything deeper belongs to a VALUE. That
 * distinction is load-bearing, and missing it broke conversion of four of our own files:
 * a TypeScript interface with a field named `predicate` converts to
 *
 *     Type PredicateSchema { example: { predicate: '', description: '' } }
 *
 * and a depth-blind scan reads that nested key as a malformed predicate declaration.
 * `maskLiterals` does not help — it is an object key, not a string — so the fix is
 * structural rather than another masking pass.
 *
 * Caught by the full gate's converter stage, which is exactly the lane `test:fast` skips.
 */
function topLevelPredicateOffsets(body: string): number[] {
  return topLevelMemberOffsets(body, 'predicate')
}

/** Offsets of `word` as a depth-0 member of a block body (see `topLevelPredicateOffsets`). */
function topLevelMemberOffsets(body: string, word: string): number[] {
  const masked = maskLiterals(body)
  const out: number[] = []
  let depth = 0
  for (let i = 0; i < masked.length; i++) {
    const c = masked[i]
    if (c === '{' || c === '(' || c === '[') depth++
    else if (c === '}' || c === ')' || c === ']') depth--
    else if (depth === 0 && masked.startsWith(word, i)) {
      const before = masked[i - 1] ?? ' '
      const after = masked[i + word.length] ?? ' '
      if (!/[A-Za-z0-9_$]/.test(before) && !/[A-Za-z0-9_$]/.test(after))
        out.push(i)
    }
  }
  return out
}

function assertPredicateFormRecognized(
  typeName: string,
  body: string,
  matched: RegExpMatchArray | null,
  source: string,
  offset: number
): void {
  if (matched) return
  // Mask literals AND require brace-depth 0: a `predicate` inside an example string, a
  // description, or an example VALUE (`example: { predicate: '' }`) is not a declaration.
  const offsets = topLevelPredicateOffsets(body)
  if (offsets.length === 0) return
  const masked = maskLiterals(body)
  if (offsets.some((i) => /^predicate\s*=>/.test(masked.slice(i))))
    throw new SyntaxError(
      `\`${typeName}\`: \`predicate => …\` is not TJS. Write the block form, where the type ` +
        `name is the value under test and \`return\` is required, as in JavaScript:\n\n` +
        `    predicate { return /* … */ }\n\n` +
        `  or the function form: predicate(x) { return /* … */ }`,
      locAt(source, offset)
    )
  throw new SyntaxError(
    `\`${typeName}\` declares a \`predicate\` in a form TJS does not implement yet, so it ` +
      `would be IGNORED and the type would accept every value.\n\n` +
      `  The forms that exist:\n` +
      `    predicate(x) { return /* … */ }   // function form\n` +
      `    predicate { return /* … */ }      // block; the type name IS the value\n\n` +
      `  Anything else is rejected rather than ignored, precisely so a type that checks ` +
      `nothing cannot ship looking like one that does.`,
    locAt(source, offset)
  )
}

/**
 * Transform Type block declarations
 *
 * Syntax forms:
 *   Type Foo 'example'                    -> const Foo = Type('Foo', 'example')
 *   Type Foo { example: 'value' }         -> const Foo = Type('Foo', 'value')
 *   Type Foo 'description' { example: 'value' }
 *                                         -> const Foo = Type('description', 'value')
 *   Type Foo 'description' { example: 0, predicate(x) { return x > 0 } }
 *                                         -> const Foo = Type('description', (x) => { ... }, 0)
 *
 * When predicate + example: auto-generate type guard from example
 */
/**
 * Match a declaration header at `i` — DETECTED on the literal-masked view, CAPTURED from
 * the real source.
 *
 * The five declaration scanners (`Type`, `FunctionPredicate`, `Generic`, `Union`, `Enum`)
 * each hand-rolled `source.slice(i).match(…)` over raw text, so each read TJS syntax
 * written inside a string as a declaration. That is not theoretical for a language whose
 * own documentation, tests and playground examples are full of illustrative declarations:
 *
 *     const doc = `Type Age { example: +0 }`
 *     // emitted: const doc = `const Age = Type('Age', (v) => typeof v === 'number' && …)`
 *
 * — the user's string CONTENTS rewritten, silently. The single-quoted form was worse: it
 * injected unescaped quotes and failed to parse, so legal JavaScript was REJECTED, under
 * `dialect: 'js'` too. That is a JS ⊆ TJS breach (`PRINCIPLES.md`), not merely a bug.
 *
 * Masking is length-preserving and keeps delimiters (`'abc'` → `'   '`), so the masked
 * match's offsets and lengths are the real ones and can be used directly. Only groups that
 * capture a string literal's CONTENTS come back blank — those go through `text(n)`, which
 * slices the original using the match's `indices` (hence the `d` flag on the regexes that
 * need it). Modelled on `transformExtendDeclarations`, which already worked this way.
 */
function matchDeclHeader(
  masked: string,
  source: string,
  i: number,
  re: RegExp
): { m: RegExpMatchArray; text: (group: number) => string } | null {
  // The keyword must start at a real word boundary.
  //
  // Every pattern here is anchored `^\bType…` and applied to `masked.slice(i)` — where `\b`
  // is VACUOUS, because position 0 of a slice has no preceding character to compare against.
  // So the scan matched the `Type` inside `EntityType`, and
  //
  //     export * as EntityType from "./EntityType.js"
  //
  // became `export * as Entityconst from = Type('from', "./EntityType.js")`. Silent
  // corruption, not a parse error. It was unreachable while declaration names had to be
  // capitalised (`from` is lowercase); allowing any identifier — correct in itself — made it
  // reachable. The slice hides the context the pattern is asking about; supply it.
  if (i > 0 && /[A-Za-z0-9_$]/.test(masked[i - 1])) return null
  const m = masked.slice(i).match(re)
  if (!m) return null
  const indices = (m as { indices?: Array<[number, number] | undefined> })
    .indices
  return {
    m,
    text: (group: number) => {
      const span = indices?.[group]
      return span ? source.slice(i + span[0], i + span[1]) : m[group]
    },
  }
}

/**
 * Keeps a declaration rewrite's line count equal to its source's (#3404). A `Type`,
 * `Generic`, `FunctionPredicate`, `Union` or `Enum` block becomes ONE line of output, so every
 * line after it moved up, and a runtime error named the wrong line (`<source>:2:area…` for a
 * function on line 5).
 *
 * Every append goes through it: `keep.emit(text)` for a replacement (or an inner copy), and
 * `keep.copy(i)` for the main loop's copy of `source[i]`, which first pads with the newlines
 * the replaced text consumed, so the character lands on its own line number.
 *
 * It counts what it is HANDED and never reads the output. The output is a rope built by
 * appending, and reading it (`charCodeAt`, `slice`) flattens the whole string: a first version
 * that did so per character made the transform quadratic (the dogfood conversion overran its
 * 180s budget), and per declaration it was still superlinear (247ms vs 75ms at 718KB).
 */
export function lineKeeper(source: string) {
  let srcPos = 0
  let srcLines = 0
  let outLines = 0
  const countNewlines = (text: string) => {
    let n = 0
    for (let k = 0; k < text.length; k++) if (text.charCodeAt(k) === 10) n++
    return n
  }
  return {
    emit(text: string): string {
      outLines += countNewlines(text)
      return text
    },
    copy(i: number): string {
      for (; srcPos < i; srcPos++)
        if (source.charCodeAt(srcPos) === 10) srcLines++
      let pad = ''
      if (outLines < srcLines) {
        pad = '\n'.repeat(srcLines - outLines)
        outLines = srcLines
      }
      if (source.charCodeAt(i) === 10) outLines++
      return pad + source[i]
    },
  }
}
export function transformTypeDeclarations(
  source: string,
  report?: PredicateVerification[],
  /**
   * Out-param: every `Type`/`Generic` name declared here.
   *
   * The emitter needs it to decide whether `n: EvenNumber` names a real runtime type.
   * Without it a declared Type is built, verified and fuel-bounded — and then the
   * annotation that names it degrades to `any`, so the function checks nothing while
   * looking checked. Collected at the declaration sites rather than re-scanned from the
   * transformed source, because re-scanning would be one more literal-blind pass.
   */
  declaredTypes?: Set<string>
): string {
  // `Type X = Y` edges, checked for cycles once every declaration has been seen.
  const aliasOf = new Map<string, { targets: string[]; at: number }>()
  const noteAlias = (name: string, text: string, at: number) => {
    const targets = aliasTargets(text)
    if (targets) aliasOf.set(name, { targets, at })
  }
  let result = ''
  const keep = lineKeeper(source)
  let i = 0
  // Detection on the masked view; every slice from the real source. See matchDeclHeader.
  const masked = maskLiterals(source)

  while (i < source.length) {
    // Look for 'Type' keyword followed by identifier
    const typeHeader = matchDeclHeader(
      masked,
      source,
      i,
      // `$` is a valid JavaScript identifier character, and every declaration form here
      // rejected it — `\w` and `[a-zA-Z0-9_]` cover `_` but not `$`. zod names most of its
      // internals `$ZodType`, `$ZodConfig`, `$replace`, so the converter emitted
      // `export Type $ZodErrorClass { … }` and our own parser refused to read it. The same
      // class as the generator and destructuring gaps: valid input the converter produces and
      // the language cannot parse. Applied to Type, FunctionPredicate, Generic, Union, Enum.
      /^\bType\s+([A-Za-z_$][a-zA-Z0-9_$]*)\s*/
    )
    const typeMatch = typeHeader?.m
    if (typeMatch) {
      const typeName = typeMatch[1]
      let j = i + typeMatch[0].length

      // Check for optional description string
      // Only treat as description if followed by = or {
      let description = typeName
      let descriptionWasExplicit = false
      // The description is a string LITERAL, so it is blank in the masked view — matched
      // there for correct length (masking handles escapes; this regex does not), read
      // from the source through `text()`.
      const descHeader = matchDeclHeader(
        masked,
        source,
        j,
        /^(['"`])([^]*?)\1\s*/d
      )
      const descStringMatch = descHeader?.m ?? null
      /** The quoted contents, as they appear in the SOURCE. */
      const descInner = descHeader ? descHeader.text(2) : ''
      if (descStringMatch) {
        const afterString = j + descStringMatch[0].length
        const nextChar = source[afterString]
        // Check if this looks like end of statement (not followed by = or {)
        // Note: the \s* in the regex consumes trailing whitespace including newlines
        const isEndOfStatement =
          nextChar === undefined ||
          afterString >= source.length ||
          (nextChar !== '=' && nextChar !== '{')

        if (nextChar === '=' || nextChar === '{') {
          // It's a description followed by = or { block
          description = descInner
          descriptionWasExplicit = true
          j = afterString
        } else if (isEndOfStatement) {
          // Old simple form: Type Name 'value' - value is both example and default.
          // Read with the SAME reader as every other type expression: it took only the
          // string, so `Type Foo 'a' + 'b'` emitted `Type('Foo', 'a') + 'b'`.
          const ext = readTypeExpression(source, j)
          if (!ext)
            throw new SyntaxError(
              `\`${typeName}\` has a value that could not be read as one expression.`,
              locAt(source, i)
            )
          declaredTypes?.add(typeName)
          result += keep.emit(
            `const ${typeName} = ${rt(
              'Type'
            )}('${typeName}', ${markExampleKinds(ext.value)})`
          )
          i = ext.end
          continue
        }
      }

      // Check for = default value
      let defaultValue: string | undefined
      let posAfterDefault = j // Track position right after the default value
      const equalsMatch = source.slice(j).match(/^=\s*/)
      if (equalsMatch) {
        j += equalsMatch[0].length
        // The default is a VALUE EXPRESSION — possibly a union. This was a one-token regex:
        // `Type Opt = '' | undefined` emitted `Type('Opt', '') | undefined` (bitwise OR, so
        // `Opt` was the number 0), an object default stopped at its FIRST `}`, and a negative
        // number or a type name matched nothing at all.
        const extracted = readTypeExpression(source, j)
        // Unreadable is an ERROR, as for `example:` — falling through to the legacy one-token
        // regex is how `Type X = 'a' |\n 0` became `Type(…, 'a') | 0`.
        if (!extracted)
          throw new SyntaxError(
            `\`${typeName}\` has a \`= …\` default that could not be read as one ` +
              `expression, so the type would check the wrong thing. A default is a value, a ` +
              `type name, or a union of them: \`Type X = 0\`, \`Type X = A | null\`.`,
            locAt(source, i)
          )
        if (extracted) {
          defaultValue = extracted.value
          j = extracted.end
          posAfterDefault = j // Save position before consuming whitespace
          // Skip whitespace after default (only to check for block)
          const wsMatch = source.slice(j).match(/^\s*/)
          if (wsMatch) j += wsMatch[0].length
        }
      }

      // Check for block { ... }
      if (source[j] === '{') {
        // Block form: Type Foo 'desc'? = default? { ... }
        const bodyStart = j + 1
        let depth = 1
        let k = bodyStart

        // Find matching closing brace
        while (k < source.length && depth > 0) {
          const char = source[k]
          if (char === '{') depth++
          else if (char === '}') depth--
          k++
        }

        if (depth !== 0) {
          // Unbalanced - just copy and continue
          result += keep.emit(source[i])
          i++
          continue
        }

        let blockBody = source.slice(bodyStart, k - 1).trim()
        const blockEnd = k

        // Parse block body for description (old syntax fallback), example, predicate
        const descInsideMatch = blockBody.match(
          /description\s*:\s*(['"`])([^]*?)\1/
        )
        if (descInsideMatch && !descriptionWasExplicit) {
          description = descInsideMatch[2]
        }

        // Extract example value using state machine for nested structures
        let example: string | undefined
        // Found on the MASKED view, so `example:` inside a description string is not the
        // member; read with the same reader as the `=` form.
        const exampleKeyword = maskLiterals(blockBody).match(/\bexample\s*:\s*/)
        if (exampleKeyword) {
          const valueStart = exampleKeyword.index! + exampleKeyword[0].length
          const extracted = readTypeExpression(blockBody, valueStart)
          if (!extracted)
            throw new SyntaxError(
              `\`${typeName}\` has an \`example:\` that could not be read as one ` +
                `expression, so the type would check NOTHING. An example is a value, a ` +
                `type name, or a union of them: \`example: { a: 0 } \`, \`example: A | null\`.`,
              locAt(source, i)
            )
          example = extracted.value
        }

        // A `default:` MEMBER is not a form — a block's default is written before the
        // block, `Type T = 0 { … }`. It was never read, so it was silently dropped, and a
        // block holding nothing else compiled to `Type('T')`, which accepts every value.
        if (
          topLevelMemberOffsets(blockBody, 'default').some((o) =>
            /^default\s*:/.test(blockBody.slice(o))
          )
        )
          throw new SyntaxError(
            `\`${typeName}\` declares \`default:\` inside its block, which TJS does not ` +
              `read — the type would silently ignore it.\n\n` +
              `  A default is written before the block, or on its own:\n\n` +
              `    Type ${typeName} = <value> { example: … }\n` +
              `    Type ${typeName} = <value>\n`,
            locAt(source, i)
          )

        // `predicate { … }` becomes the function form before anything
        // else looks at the body, so every downstream stage sees one shape.
        blockBody = normalizePredicateForms(blockBody, typeName)
        const predicateMatch = blockBody.match(
          /predicate\s*\(([^)]*)\)\s*\{([^]*)\}/
        )
        assertPredicateFormRecognized(
          typeName,
          blockBody,
          predicateMatch,
          source,
          i
        )

        // Build Type() call with appropriate arguments
        // Type(description, predicateOrExample, example?, default?)
        if (predicateMatch && example) {
          // Predicate + example: the example is the base schema, the predicate
          // refines it. Verify the predicate → fuel-bounded native guard; the
          // example schema check stays as an outer gate (constructed once via an
          // IIFE, not per call). Falls back to the raw arrow when unverifiable.
          const params = predicateMatch[1].trim()
          const body = predicateMatch[2].trim()
          const defaultArg = defaultValue ? `, ${defaultValue}` : ''
          // Structure-then-predicate: the example gives the shape, the predicate refines
          // it — which is what lets a predicate be written tersely without re-checking
          // the shape it can already assume.
          //
          // It must fail OPEN when no full runtime is installed. Emitted `.js` is
          // documented to work standalone with only the inline stub, and the stub has no
          // `validate`/`infer` (they are tosijs-schema). Optional-chaining to `undefined`
          // made the gate falsy, so the whole check returned false and the type rejected
          // EVERY value — `double(4)` errored in a standalone file while working under the
          // full runtime. Unchecked-but-working is the correct degradation here (TJS ⊇ JS);
          // rejecting valid input is not, and it only became reachable once annotations
          // started routing through declared types.
          // An EMPTY example carries no structural information, so it must not constrain.
          //
          // `example: {}` says "an object"; it does not say "an object with no
          // properties". But `infer({})` produces a closed empty schema, and once
          // tosijs-schema 1.5.0 started enforcing `additionalProperties` correctly that
          // schema began rejecting every object with any key at all — so a type whose
          // structural half was meant to be "it's an object" silently became "it's the
          // empty object", and the predicate never got a chance to run.
          //
          // Exactly the defect fixed in `parametersToJsonSchema` the same day, in the
          // sibling function twenty lines away, which I did not check. Empty shape =
          // unconstrained, in both places.
          //
          // A NON-empty example still closes the object: excess keys are a real error
          // when the author described a shape.
          //
          // The schema is derived ONCE, lazily, and cached — it used to be re-derived on
          // every single `.check()`, because the IIFE below hoists the predicate and the
          // schema expression sat inside the per-call arrow. Measured on a declared type
          // with an example and a predicate: **3232 ns/call**, against 19.8 ns for
          // example-only and 6.8 ns for a plain `n: 0` — roughly 97% of the cost was
          // re-computing a compile-time constant. This is the release's headline
          // declared-Type path, so it is the slowest thing on the feature being advertised.
          //
          // Lazy rather than hoisted to module scope because it must still fail OPEN when
          // no full runtime is installed (see above): `globalThis.__tjs` may not exist when
          // the module evaluates, and it is not our business to require that it does. The
          // first call decides, and every later call reads the cache.
          const emptyExample = /^\{\s*\}$/.test(example.trim())
          // The GATE keeps exactly its original shape — only the schema behind it is
          // memoized, via `__schema()`. A first attempt inlined the memo as a comma
          // expression and it was silently broken: the truthiness rewrite turns
          // `toBool(a, b)` into a two-ARGUMENT call, so the second half was discarded and
          // the gate always passed, letting the predicate run on `null`. Emitting a comma
          // expression into a position that will be wrapped is not safe here.
          const schemaGate = emptyExample
            ? 'true'
            : `(globalThis.__tjs?.validate ? globalThis.__tjs.validate(${params}, __schema()) : true)`
          // `infer` sees VALUES, so a marked example cannot go through it: it narrowed `0.0`
          // to integer exactly as the matcher used to, and — worse — an inferred schema cannot
          // express a `ref`, so a recursive member degraded to `{}` and a predicate that
          // narrowed NOTHING made the type accept `{ next: 5 }`. A marked example gates on the
          // same coinductive `__match` the example-only form uses; it needs no runtime, so it
          // also stops failing open where none is installed.
          // EVERY non-empty example gates on `__match` — it needs no runtime. It used to
          // gate only a MARKED example, so whether a predicate Type enforced its example
          // standalone depended on `0` vs `0.0`, and a predicate that narrowed nothing widened
          // the type. The inferred schema stays as an EXTRA check when a runtime is installed
          // (never for a marked example: `infer` sees values and cannot express a `ref`).
          const marked = markExampleKinds(example)
          const schemaMemo = emptyExample
            ? ''
            : `const __ex = ${marked};` +
              (marked === example
                ? ` let __sc, __scInit = false; const __schema = () => { if (!__scInit) { __scInit = true; __sc = (globalThis.__tjs.inferOpen ?? globalThis.__tjs.infer)(${example}) } return __sc };`
                : '')
          const gate = emptyExample
            ? 'true'
            : marked === example
            ? `(${rt('__match')}(${params}, __ex) && ${schemaGate})`
            : `${rt('__match')}(${params}, __ex)`
          const guard = verifiedGuardExpr(
            typeName,
            'Type',
            params,
            body,
            undefined,
            report
          )
          // The raw predicate (`__g`) and the structural example (`__ex`) ride on the check
          // function, so the Type can expose them SEPARATELY to the recursive solver: it
          // settles the structure first and runs the predicate only on values that are
          // structurally valid at the fixed point — never on one whose match is merely
          // assumed (which let `x.n` run on `null` and throw).
          const expose = emptyExample ? '' : ' __f.__g = __g; __f.__ex = __ex;'
          const fn = guard
            ? `(__g => { ${schemaMemo} const __f = (${params}) => (${gate} ? __g(${params}) : false);${expose} return __f })(${guard})`
            : `(() => { ${schemaMemo} const __g = (${params}) => { ${body} }; const __f = (${params}) => (${gate} ? __g(${params}) : false);${expose} return __f })()`
          declaredTypes?.add(typeName)
          result += keep.emit(
            `const ${typeName} = ${rt(
              'Type'
            )}('${description}', ${fn}, ${marked}${defaultArg})`
          )
        } else if (predicateMatch) {
          // Predicate only: verify → fuel-bounded native guard, else raw arrow.
          const params = predicateMatch[1].trim()
          const body = predicateMatch[2].trim()
          const defaultArg = defaultValue ? `, undefined, ${defaultValue}` : ''
          const guard = verifiedGuardExpr(
            typeName,
            'Type',
            params,
            body,
            undefined,
            report
          )
          const fn = guard ?? `(${params}) => { ${body} }`
          declaredTypes?.add(typeName)
          result += keep.emit(
            `const ${typeName} = ${rt(
              'Type'
            )}('${description}', ${fn}${defaultArg})`
          )
        } else if (example) {
          // Example only (becomes validation schema)
          const defaultArg = defaultValue ? `, ${defaultValue}` : ''
          declaredTypes?.add(typeName)
          // A numeric example's KIND is a fact about the source, not about the value
          // (`+0 === 0`, `0.0 === 0`), so it is marked here, the only place that can see
          // it. This used to be `numericNarrowingPredicate`, which handled a top-level `+0`
          // but returned nothing for `0.0` and never looked inside an object or array.
          result += keep.emit(
            `const ${typeName} = ${rt('Type')}('${description}', undefined, ${
              (noteAlias(typeName, example, i), markExampleKinds(example))
            }${defaultArg})`
          )
        } else if (defaultValue) {
          // Default only (infer schema from default)
          declaredTypes?.add(typeName)
          result += keep.emit(
            `const ${typeName} = ${rt('Type')}('${description}', ${
              (noteAlias(typeName, defaultValue, i),
              markExampleKinds(defaultValue))
            })`
          )
        } else {
          // A block that declares NOTHING checkable — no example, no predicate, no
          // default — cannot be a type. It was emitted as `Type('Name')`, where the
          // inline stub accepts EVERY value and the real runtime throws at construction
          // ("requires a predicate, schema, or example"). Both are wrong, and the stub's
          // wrongness is the shipped one, silently:
          //
          //     Type User { name: '' \n age: 0 }   // interface style — NOT a TJS form
          //     User.check(42)      -> true
          //     User.check(null)    -> true
          //     greet(42)           -> undefined, no error
          //
          // The interface spelling is the reason this matters: it is what a TypeScript
          // author writes first, it parses, and it produces a type that validates
          // nothing while looking like it describes a shape. Same rule as the predicate
          // forms above — rejected rather than ignored, and the message carries the fix
          // as code, since a worked example repairs measurably more than prose
          // (ASSUMPTIONS.md A1).
          // `description` etc are TJS's OWN keys, not member declarations — diagnosing
          // `Type T { description: 'x' }` as "you wrote an interface" would be a
          // confidently wrong hint, which is worse than a generic one.
          const TJS_KEYS = /^(description|example|predicate|default)\s*:/
          const members = blockBody
            .split('\n')
            .map((l) => l.trim())
            .filter((l) => l && !l.startsWith('//') && !TJS_KEYS.test(l))
          const looksLikeInterface = members.some((l) =>
            /^\w+\s*:\s*\S/.test(l)
          )

          // An EMPTY or comments-only block is the converter's deliberate degradation
          // marker, not a mistake: `fromTS` emits `Type X { // TS: <original> }` for a
          // TypeScript type it cannot express, and `generateDTS` reads that comment back
          // to emit a real type alias. "We could not express this, so it accepts
          // anything" is an honest statement; "you described a shape and it accepts
          // anything" is not. Only the second is rejected.
          if (members.length === 0) {
            declaredTypes?.add(typeName)
            result += keep.emit(
              `const ${typeName} = ${rt('Type')}('${description}')`
            )
            i = blockEnd
            continue
          }

          throw new SyntaxError(
            `\`${typeName}\` declares no example, predicate or default, so it would ` +
              `accept EVERY value.\n\n` +
              (looksLikeInterface
                ? `  Member declarations are TypeScript's spelling, not TJS's. Put the ` +
                  `shape in an \`example\` — the example IS the type:\n\n` +
                  `    Type ${typeName} {\n` +
                  `      example: { ${members
                    .slice(0, 3)
                    .map((l) => l.replace(/,$/, ''))
                    .join(', ')} }\n` +
                  `    }\n`
                : `  Give it an example, or a predicate:\n\n` +
                  `    Type ${typeName} { example: { /* … */ } }\n` +
                  `    Type ${typeName} { predicate(v) { return /* … */ } }\n`),
            locAt(source, i)
          )
        }

        i = blockEnd
        continue
      } else if (defaultValue) {
        // Simple form with default: Type Foo = 'value' or Type Foo 'desc' = 'value'
        declaredTypes?.add(typeName)
        result += keep.emit(
          `const ${typeName} = ${rt('Type')}('${description}', ${
            (noteAlias(typeName, defaultValue, i),
            markExampleKinds(defaultValue))
          })`
        )
        i = posAfterDefault // Use position before whitespace was consumed
        continue
      } else if (!descStringMatch) {
        // No description, no default, no block - look for old simple form: Type Foo 'value'
        const valueMatch = source
          .slice(j)
          .match(
            /^(['"`][^]*?['"`]|\+?\d+(?:\.\d+)?|true|false|null|\{[^]*?\}|\[[^]*?\])/
          )
        if (valueMatch) {
          const example = markExampleKinds(valueMatch[0])
          declaredTypes?.add(typeName)
          result += keep.emit(
            `const ${typeName} = ${rt('Type')}('${typeName}', ${example})`
          )
          i = j + valueMatch[0].length
          continue
        }
      }
    }

    result += keep.copy(i)
    i++
  }

  // A cycle of pure aliases (`Type A = B`, `Type B = A`, or `Type T = T | null`) constrains
  // nothing: the greatest fixed point accepts every object. Reject it by name.
  for (const [name, { at }] of aliasOf) {
    const seen = new Set<string>()
    const stack = [...aliasOf.get(name)!.targets]
    while (stack.length) {
      const t = stack.pop()!
      if (t === name)
        throw new SyntaxError(
          `\`${name}\` is defined only in terms of itself (${[
            ...seen,
            name,
          ].join(
            ' → '
          )}), so it would accept every object. Give it a shape: an example object, a ` +
            `primitive, or a predicate.`,
          locAt(source, at)
        )
      if (seen.has(t)) continue
      seen.add(t)
      stack.push(...(aliasOf.get(t)?.targets ?? []))
    }
  }
  return result
}

/**
 * Transform FunctionPredicate declarations
 *
 * Block form:
 *   FunctionPredicate Callback {
 *     params: { x: 0, y: '' }
 *     returns: false
 *   }
 *   → const Callback = FunctionPredicate('Callback', { params: { x: 0, y: '' }, returns: false })
 *
 * Generic block form:
 *   FunctionPredicate Creator<T = {}> {
 *     params: { contents: [null] }
 *     returns: T
 *   }
 *   → const Creator = FunctionPredicate('Creator', [['T', {}]], (T) => ({ params: { contents: [null] }, returns: T }))
 *
 * Function form:
 *   FunctionPredicate Handler(existingFn, 'description')
 *   → const Handler = FunctionPredicate('description', existingFn)
 */

/**
 * A block member's value, found only at the TOP level of the block body.
 *
 * `blockBody.match(/returns\s*:\s*(.+?)/)` takes the first occurrence anywhere, and a
 * `FunctionPredicate` value nests the same key names:
 *
 *     FunctionPredicate Cb {
 *       params: { cte: FunctionPredicate('f', { params: { n: null }, returns: null }) }
 *       returns: null
 *     }
 *
 * so `returns` matched the INNER one, captured `null }) }`, and the lowering came out with
 * stray braces. kysely's `cte-builder.ts` is exactly this shape.
 *
 * The value runs to the end of its line, but only counting a newline that is itself at depth
 * zero — a member written across lines stays whole. Literals are masked so a brace inside a
 * string cannot move the depth.
 */
function topLevelMemberValue(body: string, key: string): string | null {
  const masked = maskLiterals(body)
  let depth = 0
  for (let i = 0; i < masked.length; i++) {
    const c = masked[i]
    if (c === '{' || c === '(' || c === '[') depth++
    else if (c === '}' || c === ')' || c === ']') depth--
    else if (depth === 0 && masked.startsWith(key, i)) {
      if (i > 0 && /[A-Za-z0-9_$]/.test(masked[i - 1])) continue
      const after = /^\s*:/.exec(masked.slice(i + key.length))
      if (!after) continue
      const start = i + key.length + after[0].length
      let d = 0
      let j = start
      for (; j < masked.length; j++) {
        const ch = masked[j]
        if (ch === '{' || ch === '(' || ch === '[') d++
        else if (ch === '}' || ch === ')' || ch === ']') d--
        else if (ch === '\n' && d === 0) break
      }
      return body.slice(start, j).trim()
    }
  }
  return null
}

export function transformFunctionPredicateDeclarations(source: string): string {
  let result = ''
  const keep = lineKeeper(source)
  let i = 0

  // Detection on the masked view; every slice from the real source. See matchDeclHeader.
  const masked = maskLiterals(source)

  while (i < source.length) {
    const fpMatch = matchDeclHeader(
      masked,
      source,
      i,
      /^\bFunctionPredicate\s+([A-Za-z_$][a-zA-Z0-9_$]*)\s*(?:<([^>]+)>)?\s*/
    )?.m
    if (fpMatch) {
      const fpName = fpMatch[1]
      const typeParamsStr = fpMatch[2] // undefined if no <...>
      const j = i + fpMatch[0].length

      // Check for block form: FunctionPredicate Name { ... } or FunctionPredicate Name<T> { ... }
      if (source[j] === '{') {
        // Find matching closing brace
        let depth = 1
        let k = j + 1
        while (k < source.length && depth > 0) {
          if (source[k] === '{') depth++
          else if (source[k] === '}') depth--
          k++
        }

        if (depth === 0) {
          const blockBody = source.slice(j + 1, k - 1).trim()

          // Extract params: { ... } (brace-balanced for nested objects like { el: {} })
          const paramsMatch = extractBalancedValue(blockBody, /params\s*:\s*\{/)
          // Members are read at the TOP LEVEL only — a `FunctionPredicate` value nests the
          // same key names, and matching anywhere grabbed the inner one. See
          // `topLevelMemberValue`.
          const returnsValue = topLevelMemberValue(blockBody, 'returns')
          const contractValue = topLevelMemberValue(blockBody, 'returnContract')
          const contractMatch = contractValue
            ? /^['"](\w+)['"]/.exec(contractValue)
            : null
          const descValue = topLevelMemberValue(blockBody, 'description')
          const descMatch = descValue
            ? /^(['"])([^]*?)\1/.exec(descValue)
            : null

          const spec: string[] = []
          if (paramsMatch) spec.push(`params: ${paramsMatch[1]}`)
          if (returnsValue) spec.push(`returns: ${returnsValue}`)
          if (contractMatch) {
            spec.push(`returnContract: '${contractMatch[1]}'`)
          }

          const desc = descMatch ? descMatch[2] : fpName

          if (typeParamsStr) {
            // Generic form — emit factory with type params
            const typeParams = parseGenericTypeParams(typeParamsStr).entries
            const paramNames = typeParamsStr
              .split(',')
              .map((p) => p.trim().split('=')[0].trim())
            result += keep.emit(
              `const ${fpName} = ${rt(
                'FunctionPredicate'
              )}('${desc}', [${typeParams.join(', ')}], (${paramNames.join(
                ', '
              )}) => ({ ${spec.join(', ')} }))`
            )
          } else {
            // Non-generic form
            result += keep.emit(
              `const ${fpName} = ${rt(
                'FunctionPredicate'
              )}('${desc}', { ${spec.join(', ')} })`
            )
          }
          i = k
          continue
        }
      }

      // Check for function form: FunctionPredicate Name(fn, 'desc')
      if (source[j] === '(') {
        // Find matching closing paren
        let depth = 1
        let k = j + 1
        while (k < source.length && depth > 0) {
          if (source[k] === '(') depth++
          else if (source[k] === ')') depth--
          k++
        }

        if (depth === 0) {
          const args = source.slice(j + 1, k - 1).trim()
          // Split on comma: fn, 'description'
          const commaIdx = args.indexOf(',')
          if (commaIdx !== -1) {
            const fnRef = args.slice(0, commaIdx).trim()
            const desc = args.slice(commaIdx + 1).trim()
            result += keep.emit(
              `const ${fpName} = ${rt('FunctionPredicate')}(${desc}, ${fnRef})`
            )
          } else {
            // Just a function reference, name as description
            result += keep.emit(
              `const ${fpName} = ${rt(
                'FunctionPredicate'
              )}('${fpName}', ${args})`
            )
          }
          i = k
          continue
        }
      }
    }

    result += keep.copy(i)
    i++
  }

  return result
}

/**
 * Transform Generic block declarations
 *
 * Syntax:
 *   Generic Pair<T, U> { description: '...', predicate(obj, T, U) { ... } }
 *   Generic Container<T, U = ''> { ... }  // U has default
 *
 * Transforms to:
 *   const Pair = Generic(['T', 'U'], (obj, checkT, checkU) => { ... }, '...')
 *   const Container = Generic(['T', ['U', '']], (obj, checkT, checkU) => { ... }, '...')
 */
export function transformGenericDeclarations(
  source: string,
  report?: PredicateVerification[],
  declaredTypes?: Set<string>
): string {
  let result = ''
  const keep = lineKeeper(source)
  let i = 0

  // Detection on the masked view; every slice from the real source. See matchDeclHeader.
  const masked = maskLiterals(source)

  while (i < source.length) {
    // Look for 'Generic' keyword followed by identifier and type params
    // `Type X<T> { … }` and `Generic X<T> { … }` are the SAME declaration. TypeScript
    // has one keyword (`type Box<T> = …`) and so does TJS now; `Generic` survives only as
    // a deprecated alias. Two keywords made `type` → `Type` conversion non-mechanical —
    // the converter would have to switch keyword on arity, and the downgrade switch back —
    // which is disposal tax for no information, since `<T>` already says it is parameterized.
    const genericMatch = matchDeclHeader(
      masked,
      source,
      i,
      /^\b(Generic|Type)\s+([A-Za-z_$][a-zA-Z0-9_$]*)\s*<([^>]+)>\s*\{/
    )?.m
    if (genericMatch) {
      const genericName = genericMatch[2]
      const typeParamsStr = genericMatch[3]
      declaredTypes?.add(genericName)
      const blockStart = i + genericMatch[0].length - 1
      const bodyStart = blockStart + 1
      let depth = 1
      let k = bodyStart

      // Find matching closing brace
      while (k < source.length && depth > 0) {
        const char = source[k]
        if (char === '{') depth++
        else if (char === '}') depth--
        k++
      }

      if (depth !== 0) {
        // Unbalanced - just copy and continue
        result += keep.emit(source[i])
        i++
        continue
      }

      const blockBody = source.slice(bodyStart, k - 1).trim()
      const blockEnd = k

      // Parse type params: T, U = Default
      const typeParams = parseGenericTypeParams(typeParamsStr).entries

      // Parse the block body
      // Strip declaration { ... } block before parsing (it's .d.ts metadata, not runtime)
      let parsedBody = blockBody
      const declIdx = parsedBody.search(/\bdeclaration\s*\{/)
      if (declIdx !== -1) {
        // Find matching closing brace for the declaration block
        const declBraceStart = parsedBody.indexOf('{', declIdx)
        let dDepth = 1
        let dj = declBraceStart + 1
        while (dj < parsedBody.length && dDepth > 0) {
          if (parsedBody[dj] === '{') dDepth++
          else if (parsedBody[dj] === '}') dDepth--
          dj++
        }
        // Remove the declaration block
        parsedBody = parsedBody.slice(0, declIdx) + parsedBody.slice(dj)
      }

      // The example is what lets a parameterized type check its parameter without a
      // hand-written predicate restating where that parameter goes.
      let genericExample: string | undefined
      // The same reader as the `Type` sites (`readTypeExpression`) — this was the third
      // one-token copy, and `example: { value: T } | null` read only the object.
      const exKeyword = maskLiterals(parsedBody).match(/\bexample\s*:\s*/)
      if (exKeyword) {
        const extracted = readTypeExpression(
          parsedBody,
          exKeyword.index! + exKeyword[0].length
        )
        if (!extracted)
          throw new SyntaxError(
            `\`${genericName}\` has an \`example:\` that could not be read as one ` +
              `expression, so the generic would check NOTHING.`,
            locAt(source, i)
          )
        genericExample = extracted.value
      }
      const descMatch = parsedBody.match(/description\s*:\s*(['"`])([^]*?)\1/)
      // Same normalisation as the `Type` site. On a generic the type parameters follow
      // the value, so `predicate { return T(Box.value) }` becomes `predicate(Box, T) { … }` —
      // matching the function form's `predicate(x, T)`.
      parsedBody = normalizePredicateForms(
        parsedBody,
        genericName,
        typeParamsStr
          .split(',')
          .map((p) => p.trim().split('=')[0].trim())
          .filter(Boolean)
      )
      const predicateMatch = parsedBody.match(
        /predicate\s*\(([^)]*)\)\s*\{([^]*)\}/
      )
      assertPredicateFormRecognized(
        genericName,
        parsedBody,
        predicateMatch,
        source,
        i
      )

      const description = descMatch ? descMatch[2] : genericName

      if (predicateMatch) {
        const params = predicateMatch[1]
          .trim()
          .split(',')
          .map((s) => s.trim())
        let body = predicateMatch[2].trim()

        // First param is the value, rest are type params
        const valueParam = params[0] || 'x'
        const typeParamNames = params.slice(1)
        const typeCheckParams = typeParamNames.map((p) => `check${p}`)

        // Replace type param names with check functions in body
        // e.g., T(x[0]) becomes checkT(x[0])
        typeParamNames.forEach((name, idx) => {
          body = body.replace(
            new RegExp(`\\b${name}\\s*\\(`, 'g'),
            `${typeCheckParams[idx]}(`
          )
        })

        // Verify the (type-param-rewritten) body, composing with the type-param
        // checks; safe → fuel-bounded native guard, else the raw arrow.
        const paramList = [valueParam, ...typeCheckParams].join(', ')
        const guard = verifiedGuardExpr(
          genericName,
          'Generic',
          paramList,
          body,
          typeCheckParams,
          report
        )
        const fn = guard ?? `(${paramList}) => { ${body} }`
        result += keep.emit(
          `const ${genericName} = ${rt('Generic')}([${typeParams.join(
            ', '
          )}], ${fn}, '${description}')`
        )
      } else {
        // No predicate: derive one from the example, which already says where each type
        // parameter goes. Falls back to always-passing only when there is nothing to
        // derive from — a parameterized type with no example and no predicate really
        // does constrain nothing.
        const derived = genericExample
          ? genericPredicateFromExample(
              genericExample,
              typeParamsStr
                .split(',')
                .map((p) => p.trim().split('=')[0].trim())
                .filter(Boolean)
            )
          : null
        result += keep.emit(
          `const ${genericName} = ${rt('Generic')}([${typeParams.join(
            ', '
          )}], ${derived ?? '() => true'}, '${description}')`
        )
      }

      i = blockEnd
      continue
    }

    result += keep.copy(i)
    i++
  }

  return result
}

/**
 * Transform Union declarations
 *
 * Syntax:
 *   Union Direction 'cardinal direction' {
 *     'up' | 'down' | 'left' | 'right'
 *   }
 *
 * Transforms to:
 *   const Direction = Union('cardinal direction', ['up', 'down', 'left', 'right'])
 *
 * Also supports inline form:
 *   Union Direction 'cardinal direction' 'up' | 'down' | 'left' | 'right'
 */
export function transformUnionDeclarations(
  source: string,
  /** Same registration as `Enum` above — a declared union is a runtime type. */
  declaredTypes?: Set<string>
): string {
  let result = ''
  const keep = lineKeeper(source)
  let i = 0

  // Detection on the masked view; every slice from the real source. See matchDeclHeader.
  const masked = maskLiterals(source)

  while (i < source.length) {
    // Look for 'Union' keyword followed by identifier and description
    const unionHeader = matchDeclHeader(
      masked,
      source,
      i,
      /^\bUnion\s+([A-Za-z_$][a-zA-Z0-9_$]*)\s+(['"`])([^]*?)\2\s*/d
    )
    const unionMatch = unionHeader?.m
    if (unionMatch && unionHeader) {
      const unionName = unionMatch[1]
      // Blank in the masked view — read the description from the source.
      const description = unionHeader.text(3)
      const j = i + unionMatch[0].length

      // Check what follows: block or inline values
      if (source[j] === '{') {
        // Block form: Union Foo 'desc' { ... }
        const bodyStart = j + 1
        let depth = 1
        let k = bodyStart

        // Find matching closing brace
        while (k < source.length && depth > 0) {
          const char = source[k]
          if (char === '{') depth++
          else if (char === '}') depth--
          k++
        }

        if (depth !== 0) {
          result += keep.emit(source[i])
          i++
          continue
        }

        const blockBody = source.slice(bodyStart, k - 1).trim()
        const blockEnd = k

        // Parse values: 'a' | 'b' | 'c' or "a" | "b" or mixed
        const values = parseUnionValues(blockBody)
        declaredTypes?.add(unionName)
        result += keep.emit(
          `const ${unionName} = ${rt('Union')}('${description}', [${values.join(
            ', '
          )}])`
        )
        i = blockEnd
        continue
      } else {
        // Inline form: Union Foo 'desc' 'a' | 'b' | 'c'
        // Find the end of the line or statement
        // The same extent rule as every other type expression (`readTypeExpression`): it
        // ended at the first NEWLINE, so `Union U 'u' 'a'\n | 'b'` silently lost `'b'`.
        const ext = readTypeExpression(source, j)
        let lineEnd = ext ? ext.end : source.indexOf('\n', j)
        if (lineEnd === -1) lineEnd = source.length
        const inlineValues = source
          .slice(j, lineEnd)
          .trim()
          .replace(/^\|\s*/, '')

        if (inlineValues) {
          const values = parseUnionValues(inlineValues)
          declaredTypes?.add(unionName)
          result += keep.emit(
            `const ${unionName} = ${rt(
              'Union'
            )}('${description}', [${values.join(', ')}])`
          )
          i = lineEnd
          continue
        }
      }
    }

    result += keep.copy(i)
    i++
  }

  return result
}

/**
 * Parse union values from a string like: 'a' | 'b' | 123 | true
 * Returns array of value literals as strings
 */
function parseUnionValues(input: string): string[] {
  const values: string[] = []
  // Split on | and trim, preserving quoted strings and literals
  const parts = input.split('|').map((p) => p.trim())

  for (const part of parts) {
    if (!part) continue
    // Keep the value as-is (already a valid JS literal)
    values.push(part)
  }

  return values
}

/**
 * Transform Enum declarations
 *
 * Syntax:
 *   Enum Status 'task status' {
 *     Pending
 *     Active
 *     Done
 *   }
 *
 *   Enum Color 'CSS color' {
 *     Red = 'red'
 *     Green = 'green'
 *     Blue = 'blue'
 *   }
 *
 * Transforms to:
 *   const Status = Enum('task status', { Pending: 0, Active: 1, Done: 2 })
 *   const Color = Enum('CSS color', { Red: 'red', Green: 'green', Blue: 'blue' })
 */
export function transformEnumDeclarations(
  source: string,
  /**
   * Registers the enum name as a runtime type.
   *
   * Without it, `function f(c: Color)` degraded to `any` and emitted NO check, while
   * warning that `Color` "could not be resolved to a runtime type" — the type the file
   * declares three lines above. `Color.check()` worked the whole time; nothing asked it.
   * Same omission as `Type`/`Generic` had, and the same fix: the declaration transform is
   * the only place that knows the name exists.
   */
  declaredTypes?: Set<string>
): string {
  let result = ''
  const keep = lineKeeper(source)
  let i = 0

  // Detection on the masked view; every slice from the real source. See matchDeclHeader.
  const masked = maskLiterals(source)

  while (i < source.length) {
    // Look for 'Enum' keyword followed by identifier and description
    const enumHeader = matchDeclHeader(
      masked,
      source,
      i,
      /^\bEnum\s+([A-Za-z_$][a-zA-Z0-9_$]*)\s+(['"`])([^]*?)\2\s*\{/d
    )
    const enumMatch = enumHeader?.m
    if (enumMatch && enumHeader) {
      const enumName = enumMatch[1]
      // Blank in the masked view — read the description from the source.
      const description = enumHeader.text(3)
      const blockStart = i + enumMatch[0].length - 1
      const bodyStart = blockStart + 1
      let depth = 1
      let k = bodyStart

      // Find matching closing brace
      while (k < source.length && depth > 0) {
        const char = source[k]
        if (char === '{') depth++
        else if (char === '}') depth--
        k++
      }

      if (depth !== 0) {
        result += keep.emit(source[i])
        i++
        continue
      }

      const blockBody = source.slice(bodyStart, k - 1).trim()
      const blockEnd = k

      // Parse enum members
      const members = parseEnumMembers(blockBody)
      const membersStr = members
        .map(([key, value]) => `${key}: ${value}`)
        .join(', ')

      declaredTypes?.add(enumName)
      result += keep.emit(
        `const ${enumName} = ${rt('Enum')}('${description}', { ${membersStr} })`
      )
      i = blockEnd
      continue
    }

    result += keep.copy(i)
    i++
  }

  return result
}

/**
 * Parse enum members from block body
 * Handles: Pending, Active = 5, Done, Name = 'value'
 * Returns array of [key, value] pairs
 */
function parseEnumMembers(input: string): [string, string][] {
  const members: [string, string][] = []
  let currentNumericValue = 0

  // Split on newlines and commas, filter empty
  const lines = input
    .split(/[\n,]/)
    .map((l) => l.trim())
    .filter((l) => l && !l.startsWith('//'))

  for (const line of lines) {
    // Match: Name or Name = value
    const match = line.match(/^([A-Za-z_][A-Za-z0-9_]*)\s*(?:=\s*(.+))?$/)
    if (match) {
      const key = match[1]
      const explicitValue = match[2]?.trim()

      if (explicitValue !== undefined) {
        members.push([key, explicitValue])
        // If it's a number, update the counter
        const numVal = Number(explicitValue)
        if (!isNaN(numVal)) {
          currentNumericValue = numVal + 1
        }
      } else {
        // Auto-increment numeric value
        members.push([key, String(currentNumericValue)])
        currentNumericValue++
      }
    }
  }

  return members
}

/**
 * Extension info for a single extend block
 */

export function transformExtendDeclarations(source: string): {
  source: string
  extensions: Map<string, Set<string>>
} {
  const extensions = new Map<string, Set<string>>()
  let result = ''
  let i = 0

  // Detection runs over the literal-masked view (offsets preserved); every slice comes from
  // the real source. Otherwise an `extend Foo { … }` written inside a `/*# … *\/` doc
  // comment — i.e. the documentation for this feature — is transformed as if it were code.
  const maskedSource = maskLiterals(source)

  while (i < source.length) {
    // Look for 'extend' keyword at statement boundary
    const remaining = maskedSource.slice(i)
    const extendMatch = remaining.match(/^(\s*)extend\s+([A-Z]\w*)\s*\{/)

    if (!extendMatch) {
      // Check if we're at start of line or after semicolon/brace
      const lineStart =
        i === 0 ||
        source[i - 1] === '\n' ||
        source[i - 1] === ';' ||
        source[i - 1] === '}'

      if (lineStart) {
        const afterWS = remaining.match(/^(\s*)extend\s+([A-Z]\w*)\s*\{/)
        if (afterWS) {
          // Already handled above, fall through
        }
      }
      result += source[i]
      i++
      continue
    }

    const indent = extendMatch[1]
    const typeName = extendMatch[2]
    const blockStart = i + extendMatch[0].length - 1 // position of {

    // Find matching closing brace
    const blockEnd = findFunctionBodyEnd(source, blockStart)

    // Parse methods from the block body
    // Match: methodName(params) { body } or async methodName(params) { body }
    const methods: { name: string; isAsync: boolean; fullText: string }[] = []
    let j = 0
    const bodySource = source.slice(blockStart + 1, blockEnd - 1)

    while (j < bodySource.length) {
      const methodRemaining = bodySource.slice(j)
      const methodMatch = methodRemaining.match(/^(\s*)(async\s+)?(\w+)\s*\(/)

      if (!methodMatch) {
        j++
        continue
      }

      const isAsync = !!methodMatch[2]
      const methodName = methodMatch[3]

      // Reject arrow functions — they don't bind `this`
      // We'll check after finding the body

      // Find the opening paren
      const parenStart = j + methodMatch[0].length - 1
      let parenDepth = 1
      let k = parenStart + 1
      while (k < bodySource.length && parenDepth > 0) {
        if (bodySource[k] === '(') parenDepth++
        if (bodySource[k] === ')') parenDepth--
        k++
      }
      const paramsStr = bodySource.slice(parenStart + 1, k - 1)

      // Skip whitespace to find { or =>
      let afterParams = k
      while (
        afterParams < bodySource.length &&
        /\s/.test(bodySource[afterParams])
      ) {
        afterParams++
      }

      // Check for arrow function
      if (
        bodySource[afterParams] === '=' &&
        bodySource[afterParams + 1] === '>'
      ) {
        const loc = locAt(source, blockStart + 1 + j)
        throw new SyntaxError(
          `Arrow functions are not allowed in extend blocks (method '${methodName}' in extend ${typeName}). ` +
            `Use regular function syntax instead, as extension methods need 'this' binding.`,
          loc
        )
      }

      if (bodySource[afterParams] !== '{') {
        j++
        continue
      }

      // Find matching closing brace for the method body
      const methodBodyEnd = findFunctionBodyEnd(bodySource, afterParams)

      // Build: methodName: function(params) { body }
      // Transform TJS colon params (name: value) to JS defaults (name = value)
      const transformedParams = paramsStr
        .split(',')
        .map((p) => p.trim())
        .filter((p) => p.length > 0)
        .map((p) => {
          // name: value -> name = value (TJS colon shorthand)
          const colonMatch = p.match(/^(\w+)\s*:\s*(.+)$/)
          if (colonMatch) return `${colonMatch[1]} = ${colonMatch[2]}`
          return p
        })
        .join(', ')
      const asyncPrefix = isAsync ? 'async ' : ''
      const methodBody = bodySource.slice(afterParams + 1, methodBodyEnd - 1)
      methods.push({
        name: methodName,
        isAsync,
        fullText: `${methodName}: ${asyncPrefix}function(${transformedParams}) {${methodBody}}`,
      })

      j = methodBodyEnd
    }

    // Track extensions
    const isFirstForType = !extensions.has(typeName)
    if (isFirstForType) {
      extensions.set(typeName, new Set())
    }
    const extSet = extensions.get(typeName)!
    for (const m of methods) {
      extSet.add(m.name)
    }

    // Generate the __ext object (or merge into existing)
    const methodEntries = methods.map((m) => `  ${m.fullText}`).join(',\n')
    let replacement: string
    if (isFirstForType) {
      replacement = `${indent}const __ext_${typeName} = {\n${methodEntries}\n${indent}}\n`
    } else {
      // Merge into existing: Object.assign(__ext_TypeName, { ... })
      replacement = `${indent}Object.assign(__ext_${typeName}, {\n${methodEntries}\n${indent}})\n`
    }

    // Generate registration calls
    for (const m of methods) {
      replacement += `${indent}if (__tjs?.registerExtension) { __tjs.registerExtension('${typeName}', '${m.name}', __ext_${typeName}.${m.name}) }\n`
      // `asCompared` ALSO lands in the file-local projection table, unconditionally.
      //
      // The line above is gated on a shared runtime being installed; a standalone emitted
      // file has none, and `asCompared` is read by the inline comparators in that same
      // file. Emitting it locally is what makes the chain's leaf local by construction —
      // and what stops this being a feature that silently only works when someone happens
      // to have called `installRuntime()`.
      if (m.name === 'asCompared') {
        replacement += `${indent}if (typeof ${RT_NS} !== 'undefined' && ${RT_NS}.__ac) { ${RT_NS}.__ac['${typeName}'] = __ext_${typeName}.asCompared }\n`
      }
    }

    result += replacement
    i = blockEnd
  }

  // Append any remaining source
  if (i <= source.length && result.length < source.length) {
    // Already handled character by character
  }

  return { source: result, extensions }
}

/**
 * Transform method calls on known-type receivers to use extension objects.
 *
 * For literals and typed variables where the type is known:
 *   'hello'.capitalize()  ->  __ext_String.capitalize.call('hello')
 *   [1,2,3].last()        ->  __ext_Array.last.call([1,2,3])
 *
 * This is a best-effort source-level transform. For unknown types,
 * the runtime fallback (resolveExtension) handles it.
 */
export function transformExtensionCalls(
  source: string,
  extensions: Map<string, Set<string>>
): string {
  if (extensions.size === 0) return source

  // Build a map of method names to possible type names for quick lookup
  const methodToTypes = new Map<string, string[]>()
  for (const [typeName, methods] of extensions) {
    for (const method of methods) {
      if (!methodToTypes.has(method)) {
        methodToTypes.set(method, [])
      }
      methodToTypes.get(method)!.push(typeName)
    }
  }

  let result = source

  // Rewrite calls on string literals: 'str'.method(...) or "str".method(...)
  for (const [method, typeNames] of methodToTypes) {
    if (!typeNames.includes('String')) continue

    // Match string literal followed by .method(
    // Single-quoted strings
    const singleQuotePattern = new RegExp(
      `('(?:[^'\\\\]|\\\\.)*')\\.(${method})\\((\\))?`,
      'g'
    )
    result = result.replace(singleQuotePattern, (_, str, meth, closeParen) => {
      return closeParen
        ? `__ext_String.${meth}.call(${str})`
        : `__ext_String.${meth}.call(${str}, `
    })

    // Double-quoted strings
    const doubleQuotePattern = new RegExp(
      `("(?:[^"\\\\]|\\\\.)*")\\.(${method})\\((\\))?`,
      'g'
    )
    result = result.replace(doubleQuotePattern, (_, str, meth, closeParen) => {
      return closeParen
        ? `__ext_String.${meth}.call(${str})`
        : `__ext_String.${meth}.call(${str}, `
    })

    // Template literals (backtick) — simple case only (no nested templates)
    const templatePattern = new RegExp(
      '(`(?:[^`\\\\]|\\\\.)*`)\\.' + method + '\\((\\))?',
      'g'
    )
    result = result.replace(templatePattern, (_, str, closeParen) => {
      return closeParen
        ? `__ext_String.${method}.call(${str})`
        : `__ext_String.${method}.call(${str}, `
    })
  }

  // Rewrite calls on array literals: [1,2,3].method(...)
  for (const [method, typeNames] of methodToTypes) {
    if (!typeNames.includes('Array')) continue

    // Match array literal [...].method(
    // This is tricky — we need to find balanced brackets
    // Simple approach: find ].method( and walk backward to find matching [
    const methodDot = `].${method}(`
    let searchFrom = 0
    let idx: number
    while ((idx = result.indexOf(methodDot, searchFrom)) !== -1) {
      // Walk backward from idx to find matching [
      let bracketDepth = 1
      let k = idx - 1
      let inStr: string | false = false
      while (k >= 0 && bracketDepth > 0) {
        const ch = result[k]
        if (inStr) {
          if (ch === inStr && !isEscapedAt(result, k)) {
            inStr = false
          }
        } else {
          if (ch === ']') bracketDepth++
          if (ch === '[') bracketDepth--
          if (ch === "'" || ch === '"' || ch === '`') inStr = ch
        }
        k--
      }

      if (bracketDepth === 0) {
        const arrayLiteral = result.slice(k + 1, idx + 1)
        const before = result.slice(0, k + 1)
        const after = result.slice(idx + methodDot.length)
        // Check if no-args call: next char is )
        if (after[0] === ')') {
          result = `${before}__ext_Array.${method}.call(${arrayLiteral})${after.slice(
            1
          )}`
        } else {
          result = `${before}__ext_Array.${method}.call(${arrayLiteral}, ${after}`
        }
      }

      searchFrom = idx + 1
    }
  }

  // Rewrite calls on number literals: (42).method(...)
  for (const [method, typeNames] of methodToTypes) {
    if (!typeNames.includes('Number')) continue

    const numPattern = new RegExp(
      `(\\d+(?:\\.\\d+)?)\\.(${method})\\((\\))?`,
      'g'
    )
    result = result.replace(numPattern, (_, num, meth, closeParen) => {
      return closeParen
        ? `__ext_Number.${meth}.call(${num})`
        : `__ext_Number.${meth}.call(${num}, `
    })
  }

  return result
}

/**
 * Compute {line, column} from a character offset in source.
 */
export function locAt(
  source: string,
  pos: number
): { line: number; column: number } {
  let line = 1
  let column = 0
  for (let i = 0; i < pos && i < source.length; i++) {
    if (source[i] === '\n') {
      line++
      column = 0
    } else {
      column++
    }
  }
  return { line, column }
}

/**
 * Info about a single function variant for polymorphic dispatch
 */

function typeCheckForDefault(
  argExpr: string,
  defaultValue: string,
  declaredTypes?: ReadonlySet<string>
): string {
  const dv = defaultValue.trim()

  // String literal
  if (/^['"`]/.test(dv)) return `typeof ${argExpr} === 'string'`

  // Boolean
  if (dv === 'true' || dv === 'false') return `typeof ${argExpr} === 'boolean'`

  // null
  if (dv === 'null') return `${argExpr} === null`

  // undefined
  if (dv === 'undefined') return `${argExpr} === undefined`

  // Array literal
  if (dv.startsWith('[')) return `Array.isArray(${argExpr})`

  // Object literal
  if (dv.startsWith('{'))
    return `(typeof ${argExpr} === 'object' && ${argExpr} !== null && !Array.isArray(${argExpr}))`

  // Non-negative integer: +N
  if (/^\+\d+/.test(dv))
    return `(typeof ${argExpr} === 'number' && Number.isInteger(${argExpr}) && ${argExpr} >= 0)`

  // Number with decimal → float
  if (/^-?\d+\.\d+/.test(dv)) return `typeof ${argExpr} === 'number'`

  // Integer (whole number, possibly negative)
  if (/^-?\d+$/.test(dv))
    return `(typeof ${argExpr} === 'number' && Number.isInteger(${argExpr}))`

  // SOUND TS TYPE NAMES. `fromTS` now keeps TypeScript's own spelling (`number` stays
  // `number`), so these arrive here where `0.0` and `''` used to. Without them both
  // `f(x: string)` and `f(x: number)` fell through to `'any'`, scored identical signatures,
  // and a legal overload pair was rejected as ambiguous — a regression created by two
  // correct changes meeting.
  if (dv === 'string') return `typeof ${argExpr} === 'string'`
  if (dv === 'number') return `typeof ${argExpr} === 'number'`
  if (dv === 'boolean') return `typeof ${argExpr} === 'boolean'`

  // A DECLARED TYPE NAME dispatches on the type itself (#45).
  //
  // `Circle` and `Rect` both fell through to `'true'` here, so every object overload was
  // indistinguishable and the pair was rejected as ambiguous — the mechanism TJS offers
  // INSTEAD of TypeScript's hand-written `switch (s.kind)` could not express the thing
  // discriminated unions are for.
  //
  // Guarded on the binding being initialised, for the same reason the emitted `declared`
  // check is: a Type declared after the function that names it is in TDZ during module
  // evaluation, and a legal JS ordering must not become a crash (TJS ⊇ JS).
  // Registry first, capitalisation as the cross-file fallback — same rule as
  // `typeSignatureFor`, and for the same reason: nothing about a type's behaviour should
  // depend on the case of its first letter.
  if (
    /^[A-Za-z_$][A-Za-z0-9_$]*$/.test(dv) &&
    (declaredTypes?.has(dv) || /^[A-Z]/.test(dv))
  ) {
    return `(typeof ${dv} !== 'undefined' && ${dv} && typeof ${dv}.check === 'function' && ${dv}.check(${argExpr}) === true)`
  }

  // Fallback: any
  return 'true'
}

/**
 * Get a type "signature" string from a default value for ambiguity checking.
 * Two params with the same signature at the same position are ambiguous.
 */
/**
 * Exported so the CONVERTER can ask the same question the parser asks.
 *
 * `from-ts` needs to know whether an overload group is runtime-distinguishable BEFORE it
 * emits one, so it can fall back to the implementation instead of emitting a group the
 * parser will reject. Two copies of this rule would diverge; there is one.
 */
export function typeSignatureForDefault(
  defaultValue: string,
  declaredTypes?: ReadonlySet<string>
): string {
  return typeSignatureFor(stripParamMarkers(defaultValue).trim(), declaredTypes)
}

/**
 * Parse a parameter string like "a = 0, b = 'hello', c = { x: 0 }"
 * into an array of { name, defaultValue, required } objects.
 * Handles nested braces/brackets/parens and template literals.
 */
function parseParamList(
  paramStr: string,
  requiredParams: Set<string>
): { name: string; defaultValue: string; required: boolean }[] {
  // Drop the parser's own required/type-name MARKERS before anything reads this text.
  //
  // They live between the `=` and the value so the emitter can tell a required parameter
  // from a defaulted one positionally, but every consumer downstream wants the value it
  // annotates. Left in, `typeSignatureForDefault` saw `/*!tjs-req*/ 0` and returned
  // `'any'` for every parameter — so all polymorphic variants looked identical and legal
  // overloads were rejected as ambiguous.
  paramStr = stripParamMarkers(paramStr)
  const params: { name: string; defaultValue: string; required: boolean }[] = []
  let depth = 0
  let current = ''
  let inString: string | false = false

  for (let i = 0; i < paramStr.length; i++) {
    const ch = paramStr[i]

    // Track string state
    if (!inString && (ch === "'" || ch === '"' || ch === '`')) {
      inString = ch
      current += ch
      continue
    }
    if (inString) {
      current += ch
      if (ch === '\\') {
        i++
        if (i < paramStr.length) current += paramStr[i]
        continue
      }
      if (ch === inString) inString = false
      continue
    }

    // Track nesting
    if (ch === '(' || ch === '[' || ch === '{') {
      depth++
      current += ch
      continue
    }
    if (ch === ')' || ch === ']' || ch === '}') {
      depth--
      current += ch
      continue
    }

    // Split on comma at depth 0
    if (ch === ',' && depth === 0) {
      const param = parseOneParam(current.trim(), requiredParams)
      if (param) params.push(param)
      current = ''
      continue
    }

    current += ch
  }

  // Last param
  const trimmed = current.trim()
  if (trimmed) {
    const param = parseOneParam(trimmed, requiredParams)
    if (param) params.push(param)
  }

  return params
}

/**
 * Parse a single parameter like "name = 'Alice'" or "/* unsafe * / x = 0"
 */
function parseOneParam(
  paramStr: string,
  requiredParams: Set<string>
): { name: string; defaultValue: string; required: boolean } | null {
  // Strip leading /* unsafe */ comment
  const str = paramStr.replace(/^\/\*\s*unsafe\s*\*\/\s*/, '')

  // Rest params not supported in polymorphic functions
  if (str.startsWith('...')) return null

  // Find = sign (the param has been transformed from : to = by transformParenExpressions)
  const eqIdx = str.indexOf('=')
  if (eqIdx === -1) {
    // No default value — untyped param
    return { name: str.trim(), defaultValue: '', required: true }
  }

  const name = str.slice(0, eqIdx).trim()
  const defaultValue = str.slice(eqIdx + 1).trim()
  return { name, defaultValue, required: requiredParams.has(name) }
}

/**
 * Find the end of a function body (matching closing brace).
 * Handles nested braces, strings, template literals, comments, and regex.
 * Returns the index AFTER the closing brace.
 */
export function findFunctionBodyEnd(
  source: string,
  openBracePos: number,
  /**
   * A hoisted masked view of `source`, for callers that loop.
   *
   * Two of the six call sites are inside loops, and this used to re-mask on every one —
   * the quadratic that `tests.ts` and `docs.ts` had each already been fixed for, with the
   * measurements in their docstrings (31% of transpile time; 37.6ms → 538ms at 4× input).
   * `maskLiterals` is memoized, which hides the cost when the same string comes back, and
   * exposes it exactly when a loop masks a fresh substring each time.
   */
  masked?: string
): number {
  // Counted over a literal-masked view. The hand-rolled walk this replaces tracked strings
  // and comments but had NO REGEX BRANCH, so a body containing `/^\}/` — or any regex
  // holding an unbalanced brace or a lone quote — closed the function early and truncated
  // everything after it. Sixth instance of the same blind spot in this one file.
  //
  // Delegates to the ONE matcher (`matchingBrace`) and converts its result. The two
  // differed in return convention — that one gives the index OF the closing brace, this
  // gives the index PAST it — which is precisely the kind of drift that makes reading one
  // and calling the other an off-by-one landing inside the next construct. Kept as a named
  // wrapper rather than changing six call sites' arithmetic.
  const view = masked ?? maskLiterals(source)
  const close = matchingBrace(view, openBracePos)
  return close === -1 ? view.length : close + 1
}

/**
 * Transform polymorphic functions: multiple function declarations with the
 * same name are merged into a single dispatcher function.
 *
 * Must be called AFTER transformParenExpressions (so params have = defaults)
 * but BEFORE wrapClassDeclarations.
 *
 * function greet(name = '') { ... }
 * function greet(first = '', last = '') { ... }
 *
 * becomes:
 *
 * function greet$1(name = '') { ... }
 * function greet$2(first = '', last = '') { ... }
 * function greet(...__args) {
 *   if (__args.length === 1 && typeof __args[0] === 'string') return greet$1(__args[0])
 *   if (__args.length === 2 && ...) return greet$2(__args[0], __args[1])
 *   return __tjs.typeError('greet', 'no matching overload', __args)
 * }
 */
/**
 * Parse a generic type-parameter list into the `Generic([...])` argument form.
 *
 * `<Name, Config = { args: X, parameters: Y }>` — the list is comma-separated and the members
 * may CONTAIN commas, so splitting on `,` cut inside the braces:
 *
 *     Generic(['Name', ['Config', { args: null], 'parameters: null', …
 *                                            ^ a brace closed by a bracket
 *
 * which is not valid JavaScript at all. effect's `Tool.ts` failed on exactly that. Uses
 * `splitTopLevelTrimmed`, the repo's one depth-aware splitter, rather than a fourth private
 * copy of the scan.
 *
 * The `=` split had the same defect and a quieter symptom: `T = () => 0` splits into three
 * parts on a plain `.split('=')`, and the old code fell through to the no-default branch, so
 * the DEFAULT WAS SILENTLY DROPPED and the parameter degraded to untyped. Splitting at the
 * first top-level `=` that is not part of `=>`, `==`, `<=`, `>=` or `!=` keeps it.
 */
function parseGenericTypeParams(typeParamsStr: string): {
  entries: string[]
  names: string[]
} {
  const entries: string[] = []
  const names: string[] = []
  for (const part of splitTopLevelTrimmed(typeParamsStr, ',')) {
    const at = topLevelDefaultEq(part)
    const name = (at === -1 ? part : part.slice(0, at)).trim()
    const def = at === -1 ? '' : part.slice(at + 1).trim()
    names.push(name)
    if (def) {
      // `any`/`undefined` are not values a default can hold. Anything else is a TYPE in a
      // value position, so it is read by `markExampleKinds` first: zod's
      // `<T = number | bigint>` became `[['T', 0.0 | 0n]]`, which is bitwise OR, and
      // `Cannot mix BigInt and other types` took down every suite that imported it.
      entries.push(
        `['${name}', ${
          def === 'any' || def === 'undefined' ? 'null' : markExampleKinds(def)
        }]`
      )
    } else {
      entries.push(`'${name}'`)
    }
  }
  return { entries, names }
}

/**
 * A TYPE EXPRESSION in a `Type` declaration — the `= …` default and the block's
 * `example: …` — read as ONE JavaScript expression, ending at a statement boundary.
 *
 * Both sites used to read one TOKEN: `Type Opt = '' | undefined` emitted
 * `Type(…, '') | undefined` (bitwise OR — `Opt` was the number 0), an object default stopped
 * at its first `}`, and `example: A | B`, `example: Node`, `example: Exactly('a')` read
 * NOTHING — so the block became `Type('X')`, which accepts every value. `fromTS` emits the
 * block form for every TypeScript union and alias, so converted code failed open on
 * `T | null` and rejected valid input for `string | number`. One reader now serves both
 * sites, and a declaration whose expression cannot be read is an ERROR, never a type that
 * silently checks nothing.
 */
function readTypeExpression(
  source: string,
  start: number
): { value: string; end: number } | null {
  // The member's EXTENT first — to the first top-level `,` `;` newline `}` or comment —
  // on the masked view, so a delimiter inside a string or a nested object does not end it.
  // (Parsing straight from `start` let acorn read `0, default: 5` as a comma EXPRESSION.)
  const masked = maskLiterals(source)
  // A LEADING `|` — the multi-line union style Prettier writes — belongs to no member.
  const lead = masked.slice(start).match(/^\s*\|(?!\|)/)
  if (lead) start += lead[0].length
  let depth = 0
  let end = start
  for (; end < masked.length; end++) {
    const c = masked[end]
    // `Type T = 0.0 { … }`: at the top level, a `{` after a COMPLETE expression is the
    // declaration's block, not an object literal — `= { a: 0 }` has nothing before it.
    if (c === '{' && depth === 0 && isOneExpression(source.slice(start, end)))
      break
    if (c === '(' || c === '[' || c === '{') depth++
    else if (c === ')' || c === ']' || c === '}') {
      if (depth === 0) break
      depth--
    } else if (depth === 0 && (c === ',' || c === ';')) break
    else if (depth === 0 && c === '\n') {
      // A newline ends the member only when what came before is already COMPLETE and the
      // next line does not CONTINUE it. It used to end it unconditionally, so a multi-line
      // union — `'a'\n | 0`, or Prettier's `A |\n B` — was silently TRUNCATED to its first
      // line: `Type X = 'a' |\n 0` became the NUMBER 0.
      const next = masked.slice(end + 1).match(/^\s*(\S)/)?.[1]
      const continues = next !== undefined && '|&?:.'.includes(next)
      if (!continues && isOneExpression(source.slice(start, end))) break
    } else if (depth === 0 && c === '/' && /[/*]/.test(masked[end + 1] ?? ''))
      break
  }
  const value = source.slice(start, end).trim()
  // …then it must be exactly ONE expression.
  if (!isOneExpression(value)) return null
  return { value, end: start + source.slice(start, end).trimEnd().length }
}

function isOneExpression(text: string): boolean {
  if (!text.trim()) return false
  try {
    const program: any = parseJS(`(${text})`, { ecmaVersion: 'latest' })
    return program.body.length === 1
  } catch {
    return false
  }
}

/** Index of the first top-level `=` that introduces a DEFAULT, or -1. */
function topLevelDefaultEq(src: string): number {
  const masked = maskLiterals(src)
  let depth = 0
  for (let i = 0; i < masked.length; i++) {
    const c = masked[i]
    if (c === '(' || c === '[' || c === '{' || c === '<') depth++
    else if (c === ')' || c === ']' || c === '}' || c === '>') depth--
    else if (c === '=' && depth === 0) {
      // Not `=>`, `==`, and not the tail of `<=`, `>=`, `!=`.
      if (masked[i + 1] === '>' || masked[i + 1] === '=') continue
      if ('<>!='.includes(masked[i - 1] ?? '')) continue
      return i
    }
  }
  return -1
}

export function transformPolymorphicFunctions(
  source: string,
  requiredParams: Set<string>,
  /** Types declared in THIS file — see `typeSignatureFor` for why a lookup beats a heuristic. */
  declaredTypes?: ReadonlySet<string>
): { source: string; polymorphicNames: Set<string> } {
  const polymorphicNames = new Set<string>()

  // Phase 1: Find all function declarations and group by name
  // Match: optional "export" + optional "async" + "function" + name + "("
  const funcPattern =
    /(?:^|(?<=[\n;{}]))\s*(export\s+)?(async\s+)?function\s+(\w+)\s*\(/gm
  const declarations = new Map<string, PolyVariant[]>()
  let match: RegExpExecArray | null

  // First pass: collect all function positions and names
  const allMatches: {
    name: string
    fullMatchStart: number
    funcKeywordStart: number
    exported: boolean
    isAsync: boolean
  }[] = []

  // Scan a LITERAL-MASKED copy: a `function foo(...)` inside a string or template is not
  // a declaration. `src/cli/create-app.ts` ships scaffolding templates containing example
  // functions, and two of them were being grouped as ambiguous variants of one another.
  // Offsets are preserved by the mask, so match indices still index into `source`.
  const scanSource = maskLiterals(source)
  while ((match = funcPattern.exec(scanSource)) !== null) {
    const exported = !!match[1]
    const isAsync = !!match[2]
    const name = match[3]
    const fullMatchStart = match.index
    // Find where "function" keyword starts (skip whitespace and export/async)
    let funcKeywordStart = fullMatchStart
    const prefix = match[0]
    const funcIdx = prefix.indexOf('function')
    if (funcIdx >= 0) funcKeywordStart = fullMatchStart + funcIdx

    allMatches.push({
      name,
      fullMatchStart,
      funcKeywordStart,
      exported,
      isAsync,
    })
  }

  // Which SCOPE each declaration sits in, so two same-named functions in different scopes
  // are not merged into one dispatch group.
  //
  // Grouping by name alone treated these as variants of each other:
  //
  //     function a() { function f(x) { return x } return f(1) }
  //     function b() { function f(x) { return x } return f(2) }
  //
  // — two ordinary local helpers, rejected with "variants 1 and 2 have ambiguous
  // signatures". That is plain JavaScript TJS refused to accept, so it was a SUBSET
  // VIOLATION (PRINCIPLES.md), not a dispatch limitation. It accounted for five of the
  // thirteen known compat-corpus failures, all of them filed under the wrong cause because
  // the error message described the merge's internal state rather than the merge being
  // wrong to have formed a group at all.
  //
  // Depth alone is not enough: effect's `httpClient.ts` has two IDENTICAL `function
  // onError(cause)` declarations nested one level inside DIFFERENT parents, so they share a
  // depth and must still not merge. The key is the offset of the innermost enclosing brace.
  //
  // One pass, consuming matches in order as it goes, rather than a per-match rescan — this
  // transform already runs over every file in a quadratic-sensitive pass.
  const scopeOfMatch = new Map<number, number>()
  {
    const stack: number[] = []
    let mi = 0
    for (let i = 0; i < scanSource.length && mi < allMatches.length; i++) {
      while (mi < allMatches.length && allMatches[mi].fullMatchStart === i) {
        scopeOfMatch.set(
          allMatches[mi].fullMatchStart,
          stack.length ? stack[stack.length - 1] : -1
        )
        mi++
      }
      const c = scanSource[i]
      if (c === '{') stack.push(i)
      else if (c === '}') stack.pop()
    }
    // Any match at or past the final scan position is top level by construction.
    for (; mi < allMatches.length; mi++) {
      scopeOfMatch.set(allMatches[mi].fullMatchStart, -1)
    }
  }
  /** Group key: a name is only polymorphic WITHIN one scope. */
  const keyOf = (m: { name: string; fullMatchStart: number }) =>
    `${m.name}@${scopeOfMatch.get(m.fullMatchStart) ?? -1}`

  /**
   * The real function name for each scoped key.
   *
   * The key (`name@scopeOffset`) is INTERNAL. It is a Map key and nothing else — it must
   * never reach emitted code or a diagnostic. It did, briefly, both ways: the dispatcher was
   * named from it and a user saw `Polymorphic function 'f@-1'`.
   */
  const nameOfKey = new Map<string, string>()

  // Group by name-within-scope
  for (const m of allMatches) {
    if (!declarations.has(keyOf(m))) {
      declarations.set(keyOf(m), [])
      nameOfKey.set(keyOf(m), m.name)
    }
  }

  // Count occurrences — only process names that appear more than once
  const nameCounts = new Map<string, number>()
  for (const m of allMatches) {
    nameCounts.set(keyOf(m), (nameCounts.get(keyOf(m)) || 0) + 1)
  }

  // Keyed by name-WITHIN-SCOPE, so a name repeated across sibling scopes is not a group.
  const polyNames = new Set<string>()
  for (const [key, count] of nameCounts) {
    if (count > 1) polyNames.add(key)
  }

  if (polyNames.size === 0) {
    return { source, polymorphicNames }
  }

  // Phase 2: For each polymorphic function, extract full details
  for (const m of allMatches) {
    if (!polyNames.has(keyOf(m))) continue

    // Find the opening paren
    const afterFunc = source.indexOf('(', m.funcKeywordStart)
    if (afterFunc === -1) continue

    // Find matching closing paren
    let parenDepth = 1
    let j = afterFunc + 1
    while (j < source.length && parenDepth > 0) {
      if (source[j] === '(') parenDepth++
      if (source[j] === ')') parenDepth--
      j++
    }
    const closeParen = j - 1
    const paramStr = source.slice(afterFunc + 1, closeParen)

    // Find the opening brace of the function body
    let bodyStart = j
    while (bodyStart < source.length && source[bodyStart] !== '{') bodyStart++
    if (bodyStart >= source.length) continue

    // Find matching closing brace
    const bodyEnd = findFunctionBodyEnd(source, bodyStart)

    // Determine the real start (including leading whitespace, export, async)
    let realStart = m.fullMatchStart
    // Include leading whitespace on the same line
    while (realStart > 0 && source[realStart - 1] === ' ') realStart--

    const variants = declarations.get(keyOf(m))!
    const params = parseParamList(paramStr, requiredParams)

    // Check for rest params
    const hasRestParam = paramStr.includes('...')
    if (hasRestParam) {
      const loc = locAt(source, m.funcKeywordStart)
      throw new SyntaxError(
        `Rest parameters are not supported in polymorphic function '${m.name}'. ` +
          `Use separate function names instead.`,
        loc
      )
    }

    variants.push({
      index: variants.length + 1,
      start: realStart,
      end: bodyEnd,
      text: source.slice(realStart, bodyEnd),
      exported: m.exported,
      isAsync: m.isAsync,
      params,
    })
  }

  // Phase 3: Validate — check for ambiguous variants
  for (const [key, variants] of declarations) {
    const name = nameOfKey.get(key)!
    if (variants.length < 2) continue

    // Check async consistency
    const asyncCount = variants.filter((v) => v.isAsync).length
    if (asyncCount > 0 && asyncCount < variants.length) {
      const loc = locAt(source, variants[0].start)
      throw new SyntaxError(
        `Polymorphic function '${name}': all variants must be either sync or async, not mixed.`,
        loc
      )
    }

    // Check for ambiguous signatures (same types at same positions, differing only in required/optional)
    for (let i = 0; i < variants.length; i++) {
      for (let j = i + 1; j < variants.length; j++) {
        const a = variants[i]
        const b = variants[j]

        // Different max arity is fine
        if (a.params.length !== b.params.length) continue

        // Same arity — check if types are identical at every position
        let allSame = true
        for (let k = 0; k < a.params.length; k++) {
          const sigA = a.params[k].defaultValue
            ? typeSignatureForDefault(a.params[k].defaultValue, declaredTypes)
            : 'any'
          const sigB = b.params[k].defaultValue
            ? typeSignatureForDefault(b.params[k].defaultValue, declaredTypes)
            : 'any'
          if (sigA !== sigB) {
            allSame = false
            break
          }
        }

        if (allSame) {
          const loc = locAt(source, b.start)
          throw new SyntaxError(
            `Polymorphic function '${name}': variants ${i + 1} and ${
              j + 1
            } have ambiguous signatures ` +
              `(same parameter types at every position). Overloads must differ by arity or parameter types.`,
            loc
          )
        }
      }
    }
  }

  // Phase 4: Build the transformed source
  // Sort all variants by position (reverse order for safe replacement)
  const allVariants: { name: string; variant: PolyVariant }[] = []
  for (const [key, variants] of declarations) {
    const name = nameOfKey.get(key)!
    if (variants.length < 2) continue
    for (const v of variants) {
      allVariants.push({ name, variant: v })
    }
  }
  allVariants.sort((a, b) => b.variant.start - a.variant.start)

  // Replace each variant in reverse order (preserves positions)
  let result = source
  for (const { name, variant } of allVariants) {
    const asyncPrefix = variant.isAsync ? 'async ' : ''
    // Rename: function greet(...) -> function greet$1(...)
    // Strip "export" from variants — only the dispatcher is exported
    // Use $$ in replacement to produce literal $ (avoid backreference interpretation)
    const renamed = variant.text.replace(
      new RegExp(
        `(?:export\\s+)?${
          asyncPrefix ? asyncPrefix.replace(/\s+$/, '\\s+') : ''
        }function\\s+${name}\\s*\\(`
      ),
      `${asyncPrefix}function ${name}$$${variant.index}(`
    )
    result =
      result.slice(0, variant.start) + renamed + result.slice(variant.end)
  }

  // Phase 5: Append dispatcher functions
  for (const [key, variants] of declarations) {
    const name = nameOfKey.get(key)!
    if (variants.length < 2) continue
    polymorphicNames.add(name)

    const isAsync = variants[0].isAsync
    const isExported = variants.some((v) => v.exported)
    const asyncPrefix = isAsync ? 'async ' : ''
    const exportPrefix = isExported ? 'export ' : ''

    // Sort variants by specificity for dispatch order:
    // 1. More params first (higher arity)
    // 2. More specific types first (integer before number, object before any)
    const sorted = [...variants].sort((a, b) => {
      // Different arity: more params = more specific (checked first within same arity group)
      if (a.params.length !== b.params.length) return 0 // arity groups handled in dispatch

      // Same arity: count specificity
      let specA = 0
      let specB = 0
      for (const p of a.params) {
        const sig = p.defaultValue
          ? typeSignatureForDefault(p.defaultValue, declaredTypes)
          : 'any'
        if (sig === 'non-negative-integer') specA += 3
        else if (sig === 'integer') specA += 2
        else if (sig !== 'any') specA += 1
      }
      for (const p of b.params) {
        const sig = p.defaultValue
          ? typeSignatureForDefault(p.defaultValue, declaredTypes)
          : 'any'
        if (sig === 'non-negative-integer') specB += 3
        else if (sig === 'integer') specB += 2
        else if (sig !== 'any') specB += 1
      }
      return specB - specA // More specific first
    })

    // Generate dispatch branches
    const branches: string[] = []
    for (const v of sorted) {
      const checks: string[] = [`__args.length === ${v.params.length}`]
      const args: string[] = []

      for (let k = 0; k < v.params.length; k++) {
        const p = v.params[k]
        args.push(`__args[${k}]`)
        if (p.defaultValue) {
          // Tested as the value a tosijs proxy wraps (#3406); the overload still receives
          // the argument itself. A single-signature function already did this, so adding an
          // overload used to make `f(calculator.x)` "no matching overload".
          const check = typeCheckForDefault(
            `${rt('__tv')}(__args[${k}])`,
            p.defaultValue,
            declaredTypes
          )
          if (check !== 'true') checks.push(check)
        }
      }

      branches.push(
        `  if (${checks.join(' && ')}) return ${name}$${v.index}(${args.join(
          ', '
        )})`
      )
    }

    const dispatcher = `
${exportPrefix}${asyncPrefix}function ${name}(...__args) {
${branches.join('\n')}
  // No overload matched: if an argument is already a MonadicError, THAT propagates — the
  // same rule every other failed check follows (decided in typeError, via its root arg).
  return __tjs.typeError('${name}', 'no matching overload', __args, undefined, __args.find((__a) => __tjs.isMonadicError(__a)))
}
`
    result += dispatcher
  }

  return { source: result, polymorphicNames }
}

/**
 * Transform bare assignments to const declarations
 *
 * Foo = ... -> const Foo = ...
 *
 * Only transforms assignments at statement level (start of line or after semicolon/brace)
 * where the identifier starts with uppercase (to avoid breaking normal assignments)
 */
export function transformBareAssignments(source: string): string {
  // Detected on the MASKED view (literals and comments blanked, offsets preserved) and
  // spliced into the real source. It used to run on RAW source, so a line inside a template
  // literal that looked like `Red = 'red'` had `const ` written INTO THE STRING — found when
  // the dogfood lane converted `parser.test.ts`, whose Enum fixtures are exactly that.
  const masked = maskLiterals(source)
  // A bare `Foo = …` is auto-const'd only on FIRST assignment. If the name is
  // already declared (let/const/var/function/class) anywhere in the source, the
  // statement is a REASSIGNMENT — leave it alone, or we'd emit a duplicate/
  // shadowing `const` (e.g. `let B = null; … B = x` → wrongly `const B = x`).
  const declared = new Set<string>()
  const declRe = /\b(?:let|const|var|function|class)\s+([A-Z][a-zA-Z0-9_]*)/g
  for (let m; (m = declRe.exec(masked)); ) declared.add(m[1])

  const at: number[] = []
  // Match: start of line/statement, uppercase identifier, =, not ==
  masked.replace(
    /(?<=^|[;\n{])(\s*)([A-Z][a-zA-Z0-9_]*)\s*=(?!=)/gm,
    (match, ws: string, name: string, offset: number) => {
      if (declared.has(name)) return match // reassignment of a declared binding
      // A bare-identifier RHS (`B = BABYLON`) is an ALIAS — a reassignment of a
      // binding that may live in an enclosing/host scope this source-level
      // transform can't see (it bit tosijs-3d: `let B = null` in the host, a
      // `/*# */` example does `B = BABYLON`, auto-const shadowed the host `B`).
      // The feature targets `UPPER = <definition>` (Type(...)/object/call), so
      // skip when the whole RHS is a plain identifier. Issue #22.
      const rhs = masked.slice(offset + match.length)
      if (/^\s*[a-zA-Z_$][\w$]*\s*(?=[;\n,)}\]]|$)/.test(rhs)) return match
      at.push(offset + ws.length)
      return match
    }
  )
  let out = source
  for (const p of at.reverse()) out = `${out.slice(0, p)}const ${out.slice(p)}`
  return out
}

/**
 * Parse source code into an Acorn AST
 */

export function extractAndRunTests(
  source: string,
  skipTests = false
): {
  source: string
  tests: TestBlock[]
  errors: string[]
} {
  const tests: TestBlock[] = []
  const errors: string[] = []

  /**
   * Detection runs over a view with LITERALS blanked and COMMENTS INTACT.
   *
   * It used to scan RAW source, so a `test '…' { … }` written inside a template literal or
   * a double-quoted string was taken for a real test block: its body was EXECUTED at
   * transpile time via `new Function`, and the text was deleted from the emitted output.
   * Silent on both counts. A file that merely QUOTES a test block — every fixture in this
   * repo's own language tests, and any doc example — lost the quoted text:
   *
   *     tjs(`test 'uses toBe' {\n  expect(1).toBe(1)\n}`)   ->   tjs(``)
   *
   * That accounted for 52 of the 99 failures in the dogfood behaviour gate, in three
   * suites, all of which were being read as "conversion loses assertions" when the
   * assertions were being deleted by this function on the way through.
   *
   * Comments stay INTACT because `/*test 'x' { … }*\/` is a deliberate TS-compat spelling
   * — a test written inside a comment so TypeScript tooling ignores it. That is exactly the
   * case `maskLiteralsKeepComments` exists for.
   */
  const masked = maskLiteralsKeepComments(source)

  /** `test` as a whole word at `at`, followed by whitespace. */
  const testKeywordAt = (at: number): boolean => {
    if (!masked.startsWith('test', at)) return false
    // The previous spelling was `source.slice(at).match(/^\btest\s+/)`, where `\b` sits at
    // the START of the sliced string and is therefore ALWAYS satisfied — so the word
    // boundary it was written to enforce was never checked, and `mytest 'x' { }` matched.
    if (at > 0 && /[\w$]/.test(source[at - 1]!)) return false
    return /\s/.test(masked[at + 4] ?? '')
  }

  let result = ''
  let i = 0

  while (i < source.length) {
    if (testKeywordAt(i)) {
      const start = i
      let j = i + 4
      while (j < masked.length && /\s/.test(masked[j]!)) j++

      // Optional description. Read the EXTENT from the masked view (which knows where a
      // literal really ends, escapes included) and the TEXT from the original.
      let description: string | undefined
      const quote = masked[j]
      if (quote === "'" || quote === '"' || quote === '`') {
        const close = masked.indexOf(quote, j + 1)
        if (close !== -1) {
          description = source.slice(j + 1, close)
          j = close + 1
          while (j < masked.length && /\s/.test(masked[j]!)) j++
        }
      }

      if (masked[j] === '{') {
        // THE balanced-brace matcher, over the masked view. This function used to carry its
        // own — a fifth hand-rolled literal scanner, with the same missing `${ … }` handling
        // that broke the bang-access transform.
        const close = matchingBrace(masked, j)
        if (close !== -1) {
          const bodyStart = j + 1
          const body = source.slice(bodyStart, close).trim()
          const end = close + 1 // one PAST the brace, the convention callers expect

          const line = (source.slice(0, start).match(/\n/g) || []).length + 1
          tests.push({ description, body, start, end, line })

          // Run the test unless skipped
          if (!skipTests) {
            try {
              // Execute test in isolated context
              // The test has access to the Types defined before it
              const testFn = new Function(body)
              testFn()
            } catch (err: any) {
              const desc = description || `test at line ${line}`
              errors.push(
                `Test failed: ${desc} (line ${line})\n  ${err.message || err}`
              )
            }
          }

          // Strip the test block from output (replace with whitespace to preserve line numbers)
          const removed = source.slice(start, end)
          const newlines = (removed.match(/\n/g) || []).length
          result += '\n'.repeat(newlines)
          i = end
          continue
        }
      }
    }

    result += source[i]
    i++
  }

  return { source: result, tests, errors }
}

/**
 * Wrap class declarations to make them callable without `new`
 *
 * Transforms:
 *   class Foo { ... }
 * To:
 *   let Foo = class Foo { ... };
 *   Foo = new Proxy(Foo, { apply(t, _, a) { return Reflect.construct(t, a) } });
 *
 * This emits standalone JS with no runtime dependencies.
 */

/**
 * Transform polymorphic constructors into static factory functions.
 *
 * When a class has multiple constructor() declarations, the first becomes
 * the real constructor and the rest become factory functions. The wrapClass
 * Proxy routes through a polymorphic dispatcher.
 *
 * class Point {
 *   constructor(x: 0.0, y: 0.0) { this.x = x; this.y = y }
 *   constructor(coords: { x: 0.0, y: 0.0 }) { this.x = coords.x; this.y = coords.y }
 * }
 *
 * becomes:
 *
 * class Point {
 *   constructor(x = 0.0, y = 0.0) { this.x = x; this.y = y }
 * }
 * function Point$ctor$2(coords = { x: 0.0, y: 0.0 }) { return new Point(coords.x, coords.y) }
 * // wrapClass Proxy dispatches through polymorphic factory
 */
export function transformPolymorphicConstructors(
  source: string,
  requiredParams: Set<string>
): { source: string; polyCtorClasses: Set<string> } {
  const polyCtorClasses = new Set<string>()

  // Find classes with multiple constructors.
  //
  // The SEARCH runs over a literal-masked view; every slice below still comes from the real
  // source (masking preserves offsets). Without this, an illustrative `class Point { … }`
  // written inside a `/*# … *\/` doc comment — which is exactly what a language's own
  // documentation contains — was picked up as a real declaration to transform.
  const maskedSource = maskLiterals(source)
  const classRegex = /\bclass\s+(\w+)(\s+extends\s+\w+)?\s*\{/g
  let classMatch

  // Collect all class info first
  const classInfos: {
    className: string
    extendsClause: string
    bodyStart: number
    bodyEnd: number
    body: string
  }[] = []

  while ((classMatch = classRegex.exec(maskedSource)) !== null) {
    const className = classMatch[1]
    const extendsClause = classMatch[2]?.trim() || ''
    const bodyStart = classMatch.index + classMatch[0].length - 1

    const bodyEnd = findFunctionBodyEnd(source, bodyStart)
    const body = source.slice(bodyStart, bodyEnd)

    classInfos.push({ className, extendsClause, bodyStart, bodyEnd, body })
  }

  // Process in reverse order to preserve positions
  let result = source
  for (let ci = classInfos.length - 1; ci >= 0; ci--) {
    const { className, extendsClause, bodyStart, bodyEnd, body } =
      classInfos[ci]

    // Find all constructor declarations in the class body
    const ctorPattern = /\bconstructor\s*\(/g
    let ctorMatch
    const ctorPositions: number[] = []

    while ((ctorMatch = ctorPattern.exec(body)) !== null) {
      ctorPositions.push(ctorMatch.index)
    }

    if (ctorPositions.length < 2) continue // Not polymorphic

    polyCtorClasses.add(className)

    // Parse each constructor
    interface CtorInfo {
      index: number
      paramStr: string
      bodyText: string
      fullStart: number // relative to class body
      fullEnd: number // relative to class body
    }
    const ctors: CtorInfo[] = []

    for (let i = 0; i < ctorPositions.length; i++) {
      const pos = ctorPositions[i]

      // Find opening paren
      const parenStart = body.indexOf('(', pos)
      let parenDepth = 1
      let j = parenStart + 1
      while (j < body.length && parenDepth > 0) {
        if (body[j] === '(') parenDepth++
        if (body[j] === ')') parenDepth--
        j++
      }
      const paramStr = body.slice(parenStart + 1, j - 1)

      // Find opening brace
      let braceStart = j
      while (braceStart < body.length && body[braceStart] !== '{') braceStart++

      // Find matching closing brace
      const ctorBodyEnd = findFunctionBodyEnd(body, braceStart)
      const bodyText = body.slice(braceStart + 1, ctorBodyEnd - 1)

      ctors.push({
        index: i + 1,
        paramStr,
        bodyText,
        fullStart: pos,
        fullEnd: ctorBodyEnd,
      })
    }

    // Remove the extra constructors, keeping the first constructor and other methods
    let cleanBody = body
    for (let i = ctors.length - 1; i >= 1; i--) {
      const ctor = ctors[i]
      // Find start of this constructor (including leading whitespace)
      let start = ctor.fullStart
      while (start > 0 && cleanBody[start - 1] === ' ') start--
      if (start > 0 && cleanBody[start - 1] === '\n') start--

      cleanBody = cleanBody.slice(0, start) + cleanBody.slice(ctor.fullEnd)
    }

    // Generate factory functions for constructors 2..N
    let factories = ''
    for (let i = 1; i < ctors.length; i++) {
      const ctor = ctors[i]
      // Parse params for type checking in dispatcher
      const hasRest = ctor.paramStr.includes('...')
      if (hasRest) {
        const loc = locAt(source, bodyStart + ctor.fullStart)
        throw new SyntaxError(
          `Rest parameters are not supported in polymorphic constructors for '${className}'.`,
          loc
        )
      }

      // The factory function creates the object manually
      // For base classes: use Object.create + call constructor body
      // Simpler: just use new ClassName() with the first ctor's params mapped
      // Actually simplest: the factory body IS the constructor body but with
      // `this.x = ...` replaced by building an object... No, that doesn't work
      // for inheritance.
      //
      // Best approach: factory creates via new, then applies the extra ctor body
      factories += `\nfunction ${className}$ctor$${ctor.index}(${ctor.paramStr}) {`
      factories += `\n  const __obj = Object.create(${className}.prototype)`
      if (extendsClause) {
        // For derived classes, we can't easily call super() outside constructor
        // Just call the constructor body and hope it sets fields
        // Actually — the factory can just do: new ClassName(defaultArgs) then overwrite
        // Let's use a simpler approach: the factory just does new + field assignment
      }
      factories += `\n  ;(function() {${ctor.bodyText}}).call(__obj)`
      factories += `\n  return __obj`
      factories += `\n}\n`
    }

    // Generate the polymorphic dispatcher for the Proxy's apply trap
    // First constructor variant uses Reflect.construct, rest use factories
    const dispatchBranches: string[] = []

    for (let i = 0; i < ctors.length; i++) {
      const ctor = ctors[i]
      const params = parseParamList(ctor.paramStr, requiredParams)
      const checks: string[] = [`a.length === ${params.length}`]

      for (let k = 0; k < params.length; k++) {
        const p = params[k]
        if (p.defaultValue) {
          const check = typeCheckForDefault(`a[${k}]`, p.defaultValue)
          if (check !== 'true') checks.push(check)
        }
      }

      if (i === 0) {
        // First constructor — use Reflect.construct
        dispatchBranches.push(
          `    if (${checks.join(' && ')}) return Reflect.construct(t, a)`
        )
      } else {
        // Factory function
        const args = params.map((_, k) => `a[${k}]`).join(', ')
        dispatchBranches.push(
          `    if (${checks.join(' && ')}) return ${className}$ctor$${
            ctor.index
          }(${args})`
        )
      }
    }

    // Generate the dispatcher function
    factories += `\nfunction ${className}$dispatch(t, a) {\n`
    factories += dispatchBranches.join('\n') + '\n'
    factories += `    return __tjs.typeError('${className}', 'no matching constructor', a)\n`
    factories += `}\n`

    // Replace the class body and append factories
    result = result.slice(0, bodyStart) + cleanBody + result.slice(bodyEnd)

    // Insert factories after the class
    const insertPos = bodyStart + cleanBody.length
    result = result.slice(0, insertPos) + factories + result.slice(insertPos)
  }

  return { source: result, polyCtorClasses }
}

export function wrapClassDeclarations(
  source: string,
  polyCtorClasses: Set<string> = new Set()
): string {
  // Match class declarations: class Name { or class Name extends Base {
  // Capture the class name and find the full class body.
  // Searched over the literal-masked view so a `class … {` shown inside a doc comment or a
  // string is not wrapped as a real declaration; all slices use the real source.
  const maskedSource = maskLiterals(source)
  const classRegex = /\bclass\s+(\w+)(\s+extends\s+\w+)?\s*\{/g
  let result = ''
  let lastIndex = 0
  let match

  while ((match = classRegex.exec(maskedSource)) !== null) {
    const className = match[1]
    const extendsClause = match[2] || ''
    const classStart = match.index
    const bodyStart = classStart + match[0].length - 1 // position of {

    // Find the matching closing brace over the MASKED view (offsets are preserved), so a `}`
    // in a string, template or regex inside a method is not the end of the class. Counting
    // raw characters put the wrapper inside the class body after `brace(s) { return s + '}' }`,
    // where `Fmt = new Proxy(…)` parsed by accident as a class FIELD: the class was never
    // made callable. Found when the wrapper grew a statement a class body cannot hold (#3608).
    let depth = 1
    let i = bodyStart + 1
    while (i < maskedSource.length && depth > 0) {
      const char = maskedSource[i]
      if (char === '{') depth++
      else if (char === '}') depth--
      i++
    }

    if (depth === 0) {
      const classEnd = i
      const classBody = source.slice(bodyStart, classEnd)

      // Emit standalone JS - no runtime dependency
      result += source.slice(lastIndex, classStart)
      result += `let ${className} = class ${className}${extendsClause} ${classBody}; `

      if (polyCtorClasses.has(className)) {
        // Polymorphic constructor: use dispatcher function for apply trap
        result += `${className} = new Proxy(${className}, { apply(t, _, a) { return ${className}$dispatch(t, a) }, construct(t, a) { return ${className}$dispatch(t, a) } }); Object.defineProperty(${className}, '__tjs_callable', { value: ${className} });`
      } else {
        result += `${className} = new Proxy(${className}, { apply(t, _, a) { return Reflect.construct(t, a) } }); Object.defineProperty(${className}, '__tjs_callable', { value: ${className} });`
      }
      lastIndex = classEnd
    }
  }

  result += source.slice(lastIndex)
  return result
}

/**
 * Validate that Date is not used (TjsDate mode)
 * Throws an error if Date constructor or static methods are found
 */
/**
 * Report EVERY occurrence of a banned construct, with a location, in one pass.
 *
 * The three unconditional rejections (`var`, `new Date`, `eval`) each threw on the first
 * hit with no file, line or column — so fixing a file with three violations took three
 * separate `tjs check` runs, each printing an identical positionless message, and the
 * order they appeared in was the order the VALIDATORS ran rather than the order they
 * appear in the source.
 *
 * `locAt` was sitting in this same file, and ~7 sibling diagnostics already threw
 * `SyntaxError` with a location the CLI renders with source context.
 *
 * Reports at the FIRST occurrence (so the caret lands somewhere useful) and lists the
 * remaining ones beneath it — a caret can only point at one place, but a fix list should
 * be complete.
 */
/**
 * Report a rejection at offsets the caller has ALREADY decided on.
 *
 * `rejectAll` re-matches with a second pattern, and when that pattern disagrees with the
 * caller's own filter the rejection silently no-ops. That is not hypothetical: the
 * `new X` checker filtered on `source[after] === '.'` and re-matched with `(?!\s*\.)`,
 * so one `new Shape\n  .Circle()` in a file made `rejectAll` match nothing and the rule
 * went quiet for every genuine violation after it.
 *
 * Deciding once and reporting the same offsets removes the disagreement by construction.
 */
/** How many extra locations to name before falling back to a count. */
const MAX_REPORTED_LOCATIONS = 20

function rejectAt(source: string, offsets: number[], message: string): void {
  if (!offsets.length) return

  // ONE forward pass over the source, not `locAt` per hit.
  //
  // `locAt` scans from offset 0, so reporting N violations was O(N × filesize): 3200 hits
  // in a 343KB file took 540ms to build a 44,498-character message it was about to throw.
  // The offsets arrive ascending, so a running cursor gets every line/column in one pass.
  const sorted = [...offsets].sort((a, b) => a - b)
  const locs: Array<{ line: number; column: number }> = []
  let line = 1
  let lineStart = 0
  let cursor = 0
  for (const at of sorted) {
    while (cursor < at && cursor < source.length) {
      if (source[cursor] === '\n') {
        line++
        lineStart = cursor + 1
      }
      cursor++
    }
    locs.push({ line, column: at - lineStart })
  }

  // CAPPED. Naming 3199 further locations is not a diagnostic, it is a denial of service
  // against the reader; the count is the part that carries information past the first few.
  const rest = locs.slice(1)
  const shown = rest.slice(0, MAX_REPORTED_LOCATIONS)
  const extra = rest.length
    ? `\n\nAlso at: ${shown
        .map((l) => `line ${l.line}:${l.column}`)
        .join(', ')}` +
      (rest.length > shown.length
        ? `, and ${rest.length - shown.length} more`
        : '') +
      ` (${locs.length} occurrences in total)`
    : ''
  throw new SyntaxError(message + extra, locs[0])
}

/**
 * Reject every match of `pattern` on the MASKED view, reporting all of their locations.
 *
 * The masked view is what makes it safe: a construct mentioned inside a string or comment
 * is data, not code, and rejecting on the raw text is this repo's dominant defect class.
 *
 * Reporting is delegated to `rejectAt`, which is also what callers that have already
 * decided their own offsets should use — deciding once and reporting the same offsets is
 * what stops a filter and a reporter from disagreeing (see `validateNoNew`).
 */
function rejectAll(
  source: string,
  masked: string,
  pattern: RegExp,
  message: string
): void {
  const re = new RegExp(
    pattern.source,
    pattern.flags.includes('g') ? pattern.flags : pattern.flags + 'g'
  )
  const hits: number[] = []
  let m: RegExpExecArray | null
  while ((m = re.exec(masked)) !== null) {
    hits.push(m.index)
    if (m.index === re.lastIndex) re.lastIndex++ // zero-width guard
  }
  if (!hits.length) return
  // ONE reporter. This block was a verbatim copy of `rejectAt`'s.
  rejectAt(source, hits, message)
}

/**
 * Warn on every remaining use of the `unsafe` marker.
 *
 * `unsafe` is being retired. It is the only escape in this language that does not say WHICH
 * rule it suspends — every other one is a named, greppable callable (`LegacyDate`,
 * `DangerousLegacyEquals`, `LegacyDefault`) — and it is what drags `/* @tjs-unsafe *\/` along
 * as a comment channel for smuggling the marker through files `tsc` must accept.
 *
 * Of the three things it gated, `new Date()` now has `LegacyDate(x)`; `var` and `eval` are
 * refused outright, which is the precedent strict mode and ESM set. They removed legacy
 * behaviour without a trapdoor — there is no `unstrict` block and no way back to `with` — and
 * a language that takes something away and then hands you a way back has not taken it away.
 *
 * A WARNING rather than an error, for now: this ships before the removal so existing uses
 * surface in the release that still accepts them, rather than breaking on the one that does
 * not. Publish, then deprecate, then remove.
 */
export function warnOnUnsafeMarker(
  source: string,
  warnings?: string[]
): string {
  if (!warnings) return source
  const spans = findUnsafeSpans(source)
  if (spans.length) {
    warnings.push(
      `\`unsafe\` is deprecated (${spans.length} use${
        spans.length === 1 ? '' : 's'
      }) and will be removed. ` +
        'For `new Date(x)` use `LegacyDate(x)`; for `new X(…)` on a class declared in this ' +
        'file, drop `new` (calling the class constructs it). `var` and `eval` are refused, ' +
        '`unsafe` or not: ' +
        'use `let`/`const`, and `Eval()` for sandboxed evaluation.'
    )
  }
  return source
}

export function validateNoDate(source: string, warnings?: string[]): string {
  // The footgun is the Date OBJECT — mutable, timezone-dependent, and the source of most
  // date bugs. The numeric statics are not: `Date.now()`, `Date.parse()` and `Date.UTC()`
  // all return a plain number and are perfectly well-behaved, so they are WARNINGS.
  //
  // They were briefly hard errors recommending `Timestamp.now()` back when that returned
  // an ISO STRING — advice which silently broke arithmetic like `Date.now() - start`. A
  // remedy that does not work is worse than no remedy. Timestamp is now epoch-ms based, so
  // the recommendation is finally true, and these stay warnings that may tighten later.
  const errors = [
    {
      pattern: /\bnew\s+Date\b/,
      message:
        // Names the IMPORT, because `Timestamp` is not a global. Without it the remedy
        // produced a second error — `Timestamp is not defined` — which is the
        // errors-as-curriculum principle failing in the one place it matters most: the
        // diagnostic IS the teaching moment, and following it verbatim did not work.
        '`new Date()` is not allowed in TJS — the Date object is mutable and ' +
        'timezone-dependent.\n\n' +
        "  import { Timestamp } from 'tjs-lang'\n" +
        '  const t = Timestamp.now()          // wall-clock, epoch ms\n' +
        '  const t = Timestamp.from(iso)      // parse\n\n' +
        'Or `performance.now()` for a monotonic counter — no import needed. ' +
        'To keep Date deliberately: `LegacyDate(x)` — named and greppable, ' +
        'like every other escape in this language.',
    },
  ]
  const warns = [
    {
      pattern: /\bDate\.now\b/,
      message:
        'Date.now() is safe (it returns a number). Timestamp.now() is a drop-in with the same type and meaning; performance.now() is better for elapsed time, being monotonic and unaffected by clock changes.',
    },
    {
      pattern: /\bDate\.parse\b/,
      message:
        'Date.parse() returns a number and is safe, but its parsing of non-ISO input is implementation-defined. Prefer Timestamp.parse() when you want a timestamp value.',
    },
    {
      pattern: /\bDate\.UTC\b/,
      message:
        'Date.UTC() returns a number and is safe. Timestamp.from() is preferred when you want a timestamp value rather than milliseconds.',
    },
  ]

  const scan = maskLiterals(source)
  for (const { pattern, message } of errors) {
    rejectAll(source, scan, pattern, message)
  }
  for (const { pattern, message } of warns) {
    if (pattern.test(scan)) warnings?.push(message)
  }

  return source
}

/**
 * Validate that eval and Function constructor are not used (TjsNoeval mode)
 * Note: Eval and SafeFunction from TJS runtime are allowed
 */
/**
 * Transform const! declarations to const and validate immutability
 *
 * const! declares compile-time immutable bindings. The object itself
 * is not frozen at runtime (performance trap), but the transpiler
 * rejects any code that attempts to mutate the binding's properties.
 *
 *   const! config = { debug: false, port: 8080 }
 *   config.debug = true  // ERROR: Cannot mutate immutable binding 'config'
 *   console.log(config.port)  // OK: reads are fine
 *
 * Emits as plain `const` — semantics are enforced at transpile time.
 * When runtimes support records/tuples, const! can emit those instead.
 */
export function transformConstBang(source: string): string {
  // Find all const! declarations and collect binding names
  const immutableNames = new Set<string>()

  // Match: const! name = ... or const! { a, b } = ... or const! [a, b] = ...
  const masked = maskLiterals(source)
  const constBangRe = /\bconst!\s+(\w+)\b/g
  let m
  while ((m = constBangRe.exec(masked)) !== null) {
    immutableNames.add(m[1])

    // `const! x = 1` is REDUNDANT, and worse than redundant: `!` says this binding needs
    // protection the plain form does not give, which implies the value is mutable. A
    // primitive is not. `const x = 1` already provides everything `const!` could.
    //
    // What `const!` actually buys is DEEP immutability — `const c = { a: 1 }` happily
    // allows `c.a = 2`, and `const!` rejects it at compile time. On a primitive there is
    // nothing to deepen, so the marker states a distinction the language does not make.
    //
    // Rejected rather than warned, on the same ground as `new` on a local class: a form
    // whose two spellings are provably identical is decoration with the look of
    // significance, and this project keeps choosing to delete those. (One line to soften
    // to a warning if that proves too strict in practice.)
    //
    // Scanned on the MASKED view and sliced from the original, so `'const! x = 1'` inside
    // a string is not a declaration.
    const after = source.slice(m.index + m[0].length)
    const init = after.match(
      /^\s*=\s*(-?\+?\d[\d_]*(?:\.\d+)?|'[^']*'|"[^"]*"|true|false|null|undefined)\s*(?:[;\n]|$)/
    )
    if (init) {
      throw new SyntaxError(
        `\`const! ${m[1]} = ${init[1]}\` is redundant — \`${init[1]}\` is a primitive, ` +
          `and a primitive cannot be mutated.\n\n` +
          `  const ${m[1]} = ${init[1]}\n\n` +
          `\`const!\` adds DEEP immutability, which only differs for objects and arrays: ` +
          `\`const c = { a: 1 }\` allows \`c.a = 2\`, and \`const! c = { a: 1 }\` rejects it.`,
        locAt(source, m.index)
      )
    }
  }

  if (immutableNames.size === 0) return source

  // Rewrite `const!` POSITIONALLY, at offsets found on the masked view.
  //
  // This was `source.replace(/\bconst!\s+/g, 'const ')` — a raw regex over unmasked
  // source, so it edited the CONTENTS of string literals. A file with any real `const!`
  // in it turned `const doc = 'write const! x = {} to declare one'` into
  // `'write const x = {} to declare one'`, silently rewriting the user's data. It only
  // hid because of the early return above: with no real declaration the function bails
  // before reaching the replace, so the single-string test case looks clean.
  //
  // Scanned separately from the name loop, because that one requires `(\w+)` and so does
  // not see `const! { a, b } = …`, which still has to be rewritten to be legal JS.
  //
  // Only the `!` is dropped; the whitespace after it is left exactly as written. The old
  // form matched `const!\s+` and emitted `const `, which COLLAPSED that whitespace — so
  // `const!\n  x` lost a newline and every line number after it shifted. Splicing back to
  // front keeps the earlier offsets valid as we go.
  const bangRe = /\bconst!(?=\s)/g
  const at: number[] = []
  let b
  while ((b = bangRe.exec(masked)) !== null) at.push(b.index)
  for (let i = at.length - 1; i >= 0; i--) {
    source = source.slice(0, at[i]) + 'const' + source.slice(at[i] + 6)
  }

  // Strip comments before checking mutations (avoid false positives
  // from code examples in TDoc comments)
  // Shared scanner, not a raw regex: a `//` inside a template literal is not a comment,
  // and a mutation-shaped string is not a mutation. Both are handled by masking.
  const stripped = maskLiterals(source)

  // Check for mutations to immutable bindings
  for (const name of immutableNames) {
    // Property assignment: name.prop = ..., name[key] = ...
    const assignRe = new RegExp(
      `\\b${name}\\s*(?:\\.[\\w]+|\\[[^\\]]+\\])\\s*(?:=(?!=)|\\+\\+|--|\\+=|-=|\\*=|\\/=|%=|&&=|\\|\\|=|\\?\\?=|<<=|>>=|>>>=|\\^=|&=|\\|=)`,
      'g'
    )
    if (assignRe.test(stripped)) {
      throw new Error(
        `Cannot mutate immutable binding '${name}'. ` +
          `const! bindings are read-only at compile time.`
      )
    }

    // Prefix increment/decrement: ++name.prop, --name.prop
    const prefixRe = new RegExp(
      `(?:\\+\\+|--)\\s*${name}\\s*(?:\\.[\\w]+|\\[[^\\]]+\\])`,
      'g'
    )
    if (prefixRe.test(stripped)) {
      throw new Error(
        `Cannot mutate immutable binding '${name}'. ` +
          `const! bindings are read-only at compile time.`
      )
    }

    // delete name.prop
    const deleteRe = new RegExp(
      `\\bdelete\\s+${name}\\s*(?:\\.[\\w]+|\\[[^\\]]+\\])`,
      'g'
    )
    if (deleteRe.test(stripped)) {
      throw new Error(
        `Cannot mutate immutable binding '${name}'. ` +
          `const! bindings are read-only at compile time.`
      )
    }

    // push/pop/splice/shift/unshift/sort/reverse/fill on the binding
    const mutatingMethods =
      'push|pop|splice|shift|unshift|sort|reverse|fill|copyWithin|set'
    const methodRe = new RegExp(
      `\\b${name}\\s*\\.\\s*(?:${mutatingMethods})\\s*\\(`,
      'g'
    )
    if (methodRe.test(stripped)) {
      throw new Error(
        `Cannot call mutating method on immutable binding '${name}'. ` +
          `const! bindings are read-only at compile time.`
      )
    }
  }

  return source
}

export function validateNoVar(source: string): string {
  // Match var declarations at statement level. The "not inside strings/comments" part
  // used to be a claim in this comment rather than a property of the code: `var` inside a
  // template — as in `src/css/*`, which GENERATES TJS source — was rejected as a real
  // declaration. The mask makes the claim true.
  // Catches: var x, var x = ..., var {x} = ..., var [x] = ...
  const varPattern = /(?<![a-zA-Z_$])\bvar\s+/
  rejectAll(
    source,
    maskLiterals(source),
    varPattern,
    '`var` is not allowed in TJS — use `const` or `let`.\n\n' +
      'There is no escape for this one, deliberately. `let` and `const` cover every\n' +
      'legitimate use; what they do not cover is function-scoped HOISTING, which is the\n' +
      'footgun `var` is banned for. Strict mode and ESM set the precedent: where they\n' +
      'removed a legacy behaviour they removed it outright — there is no `unstrict` block\n' +
      'and no way back to `with`. A language that takes something away and then hands you\n' +
      'a trapdoor has not taken it away.'
  )
  return source
}

/**
 * `new X()` on a class declared in this file is an error in TJS: call it instead. (The
 * deprecated `unsafe new X()` still passes, with the deprecation warning.)
 *
 * It used to be a LINT for user classes while `new Date()` was a hard error — a rule for
 * built-ins and a suggestion for your own code, which is not a rule. And on a user class
 * it was a no-op with the look of significance: `P(1)` and `new P(2)` produce identical
 * objects, same constructor, because a TJS class is CALLED. So `new` was ceremony that
 * carried no information, compiled fine, and would eventually land in a codebase and
 * harden into something we could not remove.
 *
 * Abolishing it costs nothing (the call form already works everywhere), retires the
 * `no-explicit-new` lint in favour of the compiler.
 *
 * SCOPED TO CLASSES DECLARED IN THIS FILE, and that limit was found by building the
 * general rule and watching the corpus reject it. `new` is decoration only where TJS
 * made the class callable; for a built-in it is MANDATORY — `new Float32Array(1024)`
 * throws without it, as do Map, Set, Promise, RegExp and every typed array. Banning
 * `new` outright broke eight shipped examples within a minute of landing.
 *
 * So the rule matches the case it was argued for and no other: a name declared `class X`
 * in this source, where `X(1)` and `new X(1)` provably return the same object.
 *
 * `new Date()` keeps its own message: the remedy there is Timestamp, not "drop the
 * keyword", and a generic diagnostic would send people to the wrong fix.
 */
export function validateNoNew(source: string): string {
  const masked = maskLiterals(source)
  // ONE definition of "declared class", shared with the converter's rewriter. They were
  // two regexes that disagreed about the paren-less form, so `tjs convert` emitted
  // `const p = new Point;` and `tjs check` then rejected it — see `declared-classes.ts`.
  const declared = declaredClassNames(source, masked)
  if (declared.length === 0) return source

  // ONE pattern, and the SAME member-access exemption the rewriter applies.
  //
  // This built its own `new RegExp` per class and checked each separately, which is where
  // it diverged from `dropRedundantNew` in two ways that compounded:
  //
  //   - **It disagreed with the converter.** `\b` after the name is satisfied by a `.`, so
  //     `new Shape.Circle(2)` matched as `new Shape`. `tjs convert` emits that line
  //     verbatim — correctly, since the rewriter exempts member access — and `tjs check`
  //     then rejected it, naming the WRONG class and offering a remedy that is
  //     runtime-fatal if followed (`Shape.Circle(…)` is fine; `Shape(…)` is not what the
  //     author wrote). Converter-and-checker disagreement is exactly what
  //     `declared-classes.ts` was created to end; this shared its name list but not its
  //     match.
  //   - **It was quadratic.** One regex per class over the whole source: 747ms against 6ms
  //     for 160 classes, ~125×, on a path the browser playground runs.
  const re = newExpressionPattern(declared)
  const hits: Array<{ at: number; name: string }> = []
  for (const m of masked.matchAll(re)) {
    const after = m.index! + m[0].length
    // ONE predicate, shared with the rewriter — see `constructsDeclaredClass`. Deciding
    // here and reporting these same offsets is what stops the two from disagreeing.
    if (!constructsDeclaredClass(masked, after, !!m[2])) continue
    hits.push({ at: m.index!, name: m[1] })
  }
  if (!hits.length) return source

  // EVERY hit, not just the first. Reporting `hits[0]` alone meant a file's second and
  // later violations were invisible until the first was fixed — and combined with the
  // re-match disagreement above, a single exempt `new X\n  .Y()` silenced the rule
  // outright for the whole file.
  const name = hits[0].name
  rejectAt(
    source,
    hits.map((h) => h.at),
    // Quote the CALL form as the fix, not as the source: the diagnostic used to say
    // "`new Point()` is not allowed" for source that reads `new Point`, which sends the
    // reader looking for a call site that is not there.
    `\`new ${name}\` is not allowed in TJS — a class is CALLED, so \`${name}(…)\` does exactly what \`new ${name}(…)\` does and returns the same object. Drop the keyword.`
  )
  return source
}

export function validateNoEval(source: string, warnings?: string[]): string {
  // Match eval() calls - but not Eval() (capital E)
  // Use negative lookbehind to avoid matching inside words
  const evalPattern = /(?<![A-Za-z_$])\beval\s*\(/
  const scan = maskLiterals(source)
  rejectAll(
    source,
    scan,
    evalPattern,
    '`eval()` is not allowed in TJS. Use `Eval()` from the TJS runtime for sandboxed\n' +
      'evaluation — fuel-metered, capability-injected, and unable to reach this scope.\n\n' +
      'There is no escape for this one, deliberately, and it could not be given one:\n' +
      "direct `eval` is a syntactic form that sees the CALLER's scope, so any named\n" +
      "wrapper would silently be a different operation. Reaching the caller's scope is\n" +
      'precisely the part that is dangerous.'
  )
  // Match new Function() - but not SafeFunction or other *Function names
  // `new Function` is NOT abolished — it is simply unsafe, and there is no meaning-
  // preserving rewrite (SafeFunction is sandboxed; swapping it changes what the code can
  // do). So it is flagged, not replaced: comment the risk and let the author decide.
  // `eval()` above stays an error — see the TODO note on whether the two should agree.
  const functionPattern = /\bnew\s+Function\s*\(/
  if (functionPattern.test(scan)) {
    warnings?.push(
      'new Function() is unsafe: it evaluates arbitrary source with full ambient authority. ' +
        'SafeFunction() from the TJS runtime is sandboxed, but it is NOT a drop-in — it ' +
        'cannot reach globals — so this is flagged rather than rewritten.'
    )
  }

  return source
}

/**
 * Transform bang access (!.) to __tjs.bang() calls.
 *
 * x!.foo       → __tjs.bang(x,'foo')
 * x.y!.foo     → __tjs.bang(x.y,'foo')
 * fn()!.foo    → __tjs.bang(fn(),'foo')
 * arr[0]!.foo  → __tjs.bang(arr[0],'foo')
 * x!.foo!.bar  → __tjs.bang(__tjs.bang(x,'foo'),'bar')
 *
 * If the source is null/undefined, returns MonadicError.
 * If the source is a MonadicError, propagates it.
 * Otherwise, performs a bare property access (throws as usual).
 */
export function transformBangAccess(source: string): string {
  // Quick bail — no !. in source at all
  if (!source.includes('!.')) return source

  let result = ''
  let i = 0

  // State tracking for strings/comments
  type State =
    | 'normal'
    | 'string-single'
    | 'string-double'
    | 'string-template'
    | 'line-comment'
    | 'block-comment'
  let state: State = 'normal'
  /**
   * One frame per template literal we are currently inside, innermost last, each holding
   * the brace depth reached within its ACTIVE `${ … }` substitution.
   *
   * A bare depth counter is not enough, and the bug it produced is worth recording. Entering
   * `${` switched the scanner to 'normal' and **nothing ever switched it back**, so from the
   * first substitution onward the rest of that template was scanned as code — and its
   * CLOSING backtick was then read as an OPENING one. Every literal after that point in the
   * file carried inverted parity. In `eval-no-transpile-execution.test.ts` that turned
   * `'function f(o) { return o!.a }'` — a bang quoted as test DATA — into
   * `'function f(o) { return __tjs.bang(o,'a') }'`, injecting bare quotes into a
   * single-quoted string and failing the parse ~75 lines from the desync that caused it.
   */
  const templates: Array<{ braces: number }> = []

  while (i < source.length) {
    const ch = source[i]
    const next = source[i + 1]

    // State transitions
    if (state === 'normal') {
      if (ch === '/' && next === '/') {
        state = 'line-comment'
        result += ch
        i++
        continue
      }
      if (ch === '/' && next === '*') {
        state = 'block-comment'
        result += ch
        i++
        continue
      }
      if (ch === "'") {
        state = 'string-single'
        result += ch
        i++
        continue
      }
      if (ch === '"') {
        state = 'string-double'
        result += ch
        i++
        continue
      }
      if (ch === '`') {
        state = 'string-template'
        templates.push({ braces: 0 })
        result += ch
        i++
        continue
      }

      // A regex literal is not code either, and `/['"`]/` would otherwise open a phantom
      // string that swallows the rest of the file. Checked AFTER `//` and `/*` above,
      // because those are always comments in JavaScript.
      if (ch === '/' && isRegexStart(result)) {
        const close = findRegexEnd(source, i)
        if (close !== -1) {
          result += source.slice(i, close + 1)
          i = close + 1
          continue
        }
      }

      // Inside a `${ … }` substitution the matching `}` ends it and returns us to the
      // template body. Depth is tracked per template, so a nested template's substitutions
      // resolve against their own frame rather than a shared counter.
      if (templates.length > 0) {
        const top = templates[templates.length - 1]!
        if (ch === '{') top.braces++
        else if (ch === '}') {
          if (top.braces === 0) {
            state = 'string-template'
            result += ch
            i++
            continue
          }
          top.braces--
        }
      }

      // Detect bang access: ! followed by . followed by a word char (not digit)
      if (
        ch === '!' &&
        next === '.' &&
        i + 2 < source.length &&
        /[a-zA-Z_$]/.test(source[i + 2])
      ) {
        // Scan backward in `result` to find the expression start
        const exprEnd = result.length
        const exprStart = findExprStartBackward(result)

        if (exprStart < exprEnd) {
          const expr = result.slice(exprStart)
          result = result.slice(0, exprStart)

          // Scan forward to capture the property name after !.
          let j = i + 2
          while (j < source.length && /[\w$]/.test(source[j])) j++
          const prop = source.slice(i + 2, j)

          result += `__tjs.bang(${expr},'${prop}')`
          i = j
          continue
        }
      }

      result += ch
      i++
    } else if (state === 'line-comment') {
      result += ch
      if (ch === '\n') state = 'normal'
      i++
    } else if (state === 'block-comment') {
      result += ch
      if (ch === '*' && next === '/') {
        result += next
        state = 'normal'
        i += 2
      } else {
        i++
      }
    } else if (state === 'string-single') {
      result += ch
      if (ch === '\\') {
        result += next || ''
        i += 2
      } else if (ch === "'") {
        state = 'normal'
        i++
      } else {
        i++
      }
    } else if (state === 'string-double') {
      result += ch
      if (ch === '\\') {
        result += next || ''
        i += 2
      } else if (ch === '"') {
        state = 'normal'
        i++
      } else {
        i++
      }
    } else if (state === 'string-template') {
      result += ch
      if (ch === '\\') {
        result += next || ''
        i += 2
      } else if (ch === '`') {
        templates.pop()
        // Closing a template always lands in code — either at the top level, or back inside
        // the `${ … }` of an enclosing template, which is code too.
        state = 'normal'
        i++
      } else if (ch === '$' && next === '{') {
        result += next
        i += 2
        if (templates.length > 0) templates[templates.length - 1]!.braces = 0
        state = 'normal'
      } else {
        i++
      }
    } else {
      result += ch
      i++
    }
  }

  return result
}

/**
 * Scan backward through `text` to find the start of the expression
 * that ends at the last character of `text`.
 *
 * Handles: identifiers, member chains (. and ?.), function calls (),
 * computed access [], and nested combinations.
 */
function findExprStartBackward(text: string): number {
  let pos = text.length - 1

  // Skip trailing whitespace
  while (pos >= 0 && /\s/.test(text[pos])) pos--
  if (pos < 0) return text.length

  // Walk backward consuming expression parts
  while (pos >= 0) {
    const ch = text[pos]

    if (/[\w$]/.test(ch)) {
      // Identifier — consume word chars
      while (pos >= 0 && /[\w$]/.test(text[pos])) pos--
      // Check if preceded by . or ?. (member chain continues)
      if (pos >= 0 && text[pos] === '.') {
        if (pos >= 1 && text[pos - 1] === '?') {
          pos -= 2
        } else {
          pos--
        }
        continue
      }
      return pos + 1
    } else if (ch === ')') {
      pos = findMatchingOpen(text, pos, '(', ')')
      if (pos < 0) return 0
      pos--
      if (pos >= 0 && /[\w$]/.test(text[pos])) continue
      if (pos >= 0 && text[pos] === '.') {
        if (pos >= 1 && text[pos - 1] === '?') pos -= 2
        else pos--
        continue
      }
      return pos + 1
    } else if (ch === ']') {
      pos = findMatchingOpen(text, pos, '[', ']')
      if (pos < 0) return 0
      pos--
      if (pos >= 0 && /[\w$]/.test(text[pos])) continue
      if (pos >= 0 && text[pos] === '.') {
        if (pos >= 1 && text[pos - 1] === '?') pos -= 2
        else pos--
        continue
      }
      return pos + 1
    } else {
      return pos + 1
    }
  }

  return 0
}

/** Find the matching opening bracket/paren scanning backward from `pos`. */
function findMatchingOpen(
  text: string,
  pos: number,
  open: string,
  close: string
): number {
  let depth = 1
  pos--
  while (pos >= 0 && depth > 0) {
    if (text[pos] === close) depth++
    else if (text[pos] === open) depth--
    if (depth > 0) pos--
  }
  return pos
}

/**
 * Transform `let x: <example>` and `let x: <example> = value` declarations.
 *
 * Strips the `: <example>` annotation so Acorn can parse, and records the
 * variable name + example text so the linter and (later) type inference can
 * use the annotation. Acorn rejects the colon since it is not valid JS.
 *
 *   let x: ''           → let x         (annotation: x → '')
 *   let x: 0 = 5        → let x = 5     (annotation: x → 0)
 *   let result: { ok: false } = ...     (annotation: result → { ok: false })
 *
 * Only `let` is processed. `const` always has an initializer, so the type
 * is always inferable. `var` is rejected by TjsNoVar mode.
 */
export function transformLetTypeAnnotations(source: string): {
  source: string
  annotations: Map<string, string>
} {
  const annotations = new Map<string, string>()
  if (!source.includes('let ')) return { source, annotations }

  type Replacement = { start: number; end: number; replacement: string }
  const replacements: Replacement[] = []

  let i = 0
  let state: TokenizerState = 'normal'
  const templateStack: number[] = []

  while (i < source.length) {
    const char = source[i]
    const nextChar = source[i + 1]

    switch (state) {
      case 'single-string':
        if (char === '\\' && i + 1 < source.length) {
          i += 2
          continue
        }
        if (char === "'") state = 'normal'
        i++
        continue
      case 'double-string':
        if (char === '\\' && i + 1 < source.length) {
          i += 2
          continue
        }
        if (char === '"') state = 'normal'
        i++
        continue
      case 'template-string':
        if (char === '\\' && i + 1 < source.length) {
          i += 2
          continue
        }
        if (char === '$' && nextChar === '{') {
          i += 2
          templateStack.push(1)
          state = 'normal'
          continue
        }
        if (char === '`') state = 'normal'
        i++
        continue
      case 'line-comment':
        if (char === '\n') state = 'normal'
        i++
        continue
      case 'block-comment':
        if (char === '*' && nextChar === '/') {
          i += 2
          state = 'normal'
          continue
        }
        i++
        continue
      case 'regex':
        if (char === '\\' && i + 1 < source.length) {
          i += 2
          continue
        }
        if (char === '[') {
          i++
          while (i < source.length && source[i] !== ']') {
            if (source[i] === '\\' && i + 1 < source.length) i += 2
            else i++
          }
          if (i < source.length) i++
          continue
        }
        if (char === '/') {
          i++
          while (i < source.length && /[gimsuy]/.test(source[i])) i++
          state = 'normal'
          continue
        }
        i++
        continue
      case 'normal':
        if (templateStack.length > 0) {
          if (char === '{') {
            templateStack[templateStack.length - 1]++
          } else if (char === '}') {
            templateStack[templateStack.length - 1]--
            if (templateStack[templateStack.length - 1] === 0) {
              templateStack.pop()
              i++
              state = 'template-string'
              continue
            }
          }
        }
        if (char === "'") {
          i++
          state = 'single-string'
          continue
        }
        if (char === '"') {
          i++
          state = 'double-string'
          continue
        }
        if (char === '`') {
          i++
          state = 'template-string'
          continue
        }
        if (char === '/' && nextChar === '/') {
          i += 2
          state = 'line-comment'
          continue
        }
        if (char === '/' && nextChar === '*') {
          i += 2
          state = 'block-comment'
          continue
        }
        if (char === '/') {
          let j = i - 1
          while (j >= 0 && /\s/.test(source[j])) j--
          const beforeChar = j >= 0 ? source[j] : ''
          const isRegexContext =
            !beforeChar ||
            /[=(!,;:{[&|?+\-*%<>~^]/.test(beforeChar) ||
            (j >= 5 &&
              /\b(return|case|throw|in|of|typeof|instanceof|new|delete|void)$/.test(
                source.slice(Math.max(0, j - 10), j + 1)
              ))
          if (isRegexContext) {
            i++
            state = 'regex'
            continue
          }
        }

        // Detect `let <ident> :` at top-level normal state
        if (
          char === 'l' &&
          source.slice(i, i + 4) === 'let ' &&
          (i === 0 || !/[\w$]/.test(source[i - 1]))
        ) {
          // Skip past `let` and whitespace
          let j = i + 4
          while (j < source.length && /\s/.test(source[j])) j++
          // Match identifier
          if (j < source.length && /[a-zA-Z_$]/.test(source[j])) {
            const nameStart = j
            while (j < source.length && /[\w$]/.test(source[j])) j++
            const nameEnd = j
            const varName = source.slice(nameStart, nameEnd)
            // Skip whitespace; require a `:` (not `::` or part of `?:`)
            let k = j
            while (k < source.length && /\s/.test(source[k])) k++
            if (
              k < source.length &&
              source[k] === ':' &&
              source[k + 1] !== ':'
            ) {
              const colonPos = k
              // Skip whitespace after colon
              let exStart = colonPos + 1
              while (exStart < source.length && /[ \t]/.test(source[exStart])) {
                exStart++
              }
              // Scan example expression until `=`, `,`, `;`, or newline at depth 0
              const exEnd = scanExampleEnd(source, exStart)
              if (exEnd > exStart) {
                const example = source.slice(exStart, exEnd).trim()
                annotations.set(varName, example)
                replacements.push({
                  start: nameEnd,
                  end: exEnd,
                  replacement: '',
                })
                i = exEnd
                continue
              }
            }
          }
        }
        break
    }
    i++
  }

  if (replacements.length === 0) return { source, annotations }

  // Apply right-to-left to preserve positions
  let result = source
  for (let k = replacements.length - 1; k >= 0; k--) {
    const r = replacements[k]
    result = result.slice(0, r.start) + r.replacement + result.slice(r.end)
  }
  return { source: result, annotations }
}

/**
 * Scan forward from `start` and return the position where the example
 * expression ends. Stops at `=`, `,`, `;`, or a newline when paren/brace/
 * bracket depth is 0. Skips through nested brackets, strings, and templates.
 */
function scanExampleEnd(source: string, start: number): number {
  let i = start
  let parens = 0
  let braces = 0
  let brackets = 0
  let state: 'normal' | 'sq' | 'dq' | 'tpl' = 'normal'
  const templateStack: number[] = []
  while (i < source.length) {
    const c = source[i]
    if (state === 'sq') {
      if (c === '\\') {
        i += 2
        continue
      }
      if (c === "'") state = 'normal'
      i++
      continue
    }
    if (state === 'dq') {
      if (c === '\\') {
        i += 2
        continue
      }
      if (c === '"') state = 'normal'
      i++
      continue
    }
    if (state === 'tpl') {
      if (c === '\\') {
        i += 2
        continue
      }
      if (c === '$' && source[i + 1] === '{') {
        templateStack.push(1)
        state = 'normal'
        i += 2
        continue
      }
      if (c === '`') state = 'normal'
      i++
      continue
    }
    // normal
    if (templateStack.length > 0) {
      if (c === '{') templateStack[templateStack.length - 1]++
      else if (c === '}') {
        templateStack[templateStack.length - 1]--
        if (templateStack[templateStack.length - 1] === 0) {
          templateStack.pop()
          state = 'tpl'
          i++
          continue
        }
      }
    }
    if (c === "'") {
      state = 'sq'
      i++
      continue
    }
    if (c === '"') {
      state = 'dq'
      i++
      continue
    }
    if (c === '`') {
      state = 'tpl'
      i++
      continue
    }
    if (c === '(') parens++
    else if (c === ')') parens--
    else if (c === '{') braces++
    else if (c === '}') braces--
    else if (c === '[') brackets++
    else if (c === ']') brackets--
    if (parens === 0 && braces === 0 && brackets === 0) {
      if (c === '=' || c === ',' || c === ';' || c === '\n') return i
    }
    i++
  }
  return i
}
